import { EzLogger } from '/public/javascript/common/ezclocker-logger.js';

import {
    EzBadParamException,
    EzBadStateException,
    EzException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzArray,
    EzFunction,
    EzJson
} from '/ezlibrary/helpers/EzHelpers.js';

/*import {
    EzRegistrationState,
    EzContextEventName,
    //EzLocale,
    EzEmployerOption,
    EzTimeZone
} from '/ezlibrary/enums/EzEnumerations.js';*/
import { EzTimeZone } from '/ezlibrary/date-time/EzTimeZone.js';
import { EzEmployerOption } from '/ezlibrary/EzEmployerOption.js';
import { EzRegistrationState } from '/ezlibrary/enums/EzRegistrationState.js';
import { EzContextEventName } from '/ezlibrary/EzClockerContext/EzContextEventName.js';
import { EzLocale } from '/ezlibrary/locales/EzLocale.js';
import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';
import { EzClass } from '/ezlibrary/EzClass.js';
import { EzUx } from '/ezlibrary/ux/EzUx.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Represents a DateTime for ezclocker
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzDateTime, ezDateTime } from '/public/javascript/common/ez-date-time.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     globalThis.ezApi.ezclocker?.[EzDateTime.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     EzClass.ezWaitReady(
 *         EzDateTime.ezEventNames.onReady,
 *         {listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzDateTime extends EzClass {
    /**
     * @static
     * @public @readonly @property
     * Gets the default active timezone value of: 'AUTOMATIC'
     */
    static get DEFAULT_ACTIVE_TIMEZONE() {
        return 'AUTOMATIC';
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Date Formatting Constants
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @static
     * @public @readonly @property
     * Gets the default moment.js ISO date time format string (does not include seconds or milliseconds): 'YYYY-MM-DDTHH:mmZ'
     */
    static get DEFAULT_ISO_DATE_TIME_FORMAT() {
        return 'YYYY-MM-DDTHH:mmZ';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets date format string: 'ddd MM/DD/YYYY'
     */
    static get DTFORMAT_ddd_MM_DD_YYYY() {
        return 'ddd MM/DD/YYYY';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets time format string: 'hh:mm a'
     */
    static get DTFORMAT_hh_mm_a() {
        return 'hh:mm a';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default moment.js ISO date time format string that includes seconds as: 'YYYY-MM-DDTHH:mm:ssZ'
     */
    static get DEFAULT_ISO_DATE_TIME_WITH_SECONDS_FORMAT() {
        return 'YYYY-MM-DDTHH:mm:ssZ';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default moment.js ISO date time format string that includes milliseconds as: 'YYYY-MM-DDTHH:mm:ss.999Z'
     */
    static get DEFAULT_ISO_DATE_TIME_WITH_MILLISECONDS_FORMAT() {
        return 'YYYY-MM-DDTHH:mm:ss.999Z';
    }

    /**
     * TODO: Fix time zone id validation
     * @static
     * @public @method
     * Determines if the provided timeZoneId is considered a valid date time zone id.
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @returns {boolean}
     */
    static isValidTimeZone(timeZoneId = null) {
        return EzString.hasLength(timeZoneId);

        /* Disabled due to weird stack trace overflow error in moment-timezone parsing.
        if (!EzString.hasLength(timeZoneId)) {
            return false;
        }

        try {
            const parsedTimeZone = moment.tz.zone(timeZoneId);

            return timeZoneId?.toUpperCase() === parsedTimeZone?.name?.toUpperCase();
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Provided timeZoneId of ${timeZoneId}
                    is not considered a valid time zone id.`);

            return false;
        }
        */
    }


    /**
     * @static
     * @public @method
     * Determines if the provided timeZoneId is considered a valid date time zone id.
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidTimeZone(timeZoneId)
     */
    static ezIsValidTimeZone = EzDateTime.isValidTimeZone;

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: 8:30 PM
     * @returns {string}
     */
    static get SHORT_TIME_FORMAT() {
        return 'LT';
    }

    /**
     * @static\ 04ublic @readonly @property65
     * Returns format string for: 8:30:25 PM
     * @returns {string}
     */
    static get SHORT_TIME_WITH_SECONDS_FORMAT() {
        return 'LTS';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: 09/04/1986
     * @returns {string}
     */
    static get TWO_DIGIT_SHORT_DATE() {
        return 'L';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: 9/4/1986
     * @returns {string}
     */
    static get SHORT_DATE() {
        return 'l';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: September 4, 1986
     * @returns {string}
     */
    static get LONG_MONTH_DATE_FORMAT() {
        return 'LL';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: Sep 4, 1986
     * @returns {string}
     */
    static get SHORT_MONTH_DATE_FORMAT() {
        return 'll';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: September 4, 1986 8:30 PM
     * @returns {string}
     */
    static get LONG_MONTH_DATE_SHORT_TIME_FORMAT() {
        return 'LLL';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: Sep 4, 1986 8:30 PM
     * @returns {string}
     */
    static get SHORT_MONTH_DATE_SHORT_TIME_FORMAT() {
        return 'lll';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: Thursday, September 4, 1986 8:30 PM
     * @returns {string}
     */
    static LONG_MONTH_DAY_DATE_SHORT_TIME_FORMAT() {
        return 'LLLL';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns format string for: Thu, Sep 4, 1986 8:30 PM
     * @returns {string}
     */
    static SHORT_MONTH_DAY_DATE_SHORT_TIME_FORMAT() {
        return 'llll';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the default display date and time format string for moment.js
     * Format string: 'L LT'
     * Returns in localized format: 09/04/1986 8:30 PM
     * @returns {string}
     */
    static get DEFAULT_DISPLAY_DATE_TIME() {
        return 'L LT';
    }

    /**
     * @public @readonly @property
     * Returns the default display date and time with seconds format string for moment.js
     * Format string: 'L LTS'
     * Returns in localized format: 09/04/1986 8:30:25 PM
     */
    static get DEFAULT_DISPLAY_DATE_TIME_WITH_SECONDS() {
        return 'L LTS';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the default display date format string for moment.js
     * Format string: 'L'
     * Returns in localized format: 09/04/1986
     * @returns {string}
     */
    static get DEFAULT_MOMENT_DATE_FORMAT() {
        return 'L';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default momentjs time-stanp display format string.
     * @returns {string}
     */
    static get DEFAULT_MOMENT_TIMESTAMP_DISPLAY_FORMAT() {
        return 'MM/DD/YYYY hh:mm:ss';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the format string for displaying the date as: MM/YYYY
     * @returns {string}
     */
    static get DEFAULT_MONTH_YEAR_DISPLAY_FORMAT() {
        return 'MM/YYYY';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the default display day and date format string for moment.js Format string: 'dddd L'
     * Returns in localized format: Thursday 09/04/1986
     */
    static get DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT() {
        return 'ddd L';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the default time display without seconds format string for moment.js Format string: 'LT'
     * Returns in localized format: 8:30 PM
     * @returns {string}
     */
    static get DEFAULT_MOMENT_HH_MM_TIME_FORMAT() {
        return 'LT';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default time display format string for moment.js
     * Format string: 'LTS'
     * Result is in localized format: 8:30:25 PM
     * @returns {string}
     */
    static get DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT() {
        return 'LTS';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the REGEX value for validation ISO dates.
     * @returns {regex}
     */
    static get ISO_VALIDATION_REGEX() {
        // Possible Alternative RegEx: /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/
        // eslint-disable-next-line no-useless-escape
        return /^([+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24\:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets date format string: 'MM/DD/YYYY'
     * @returns {string}
     */
    static get DEFAULT_MM_DD_YYYY_FORMAT() {
        return 'MM/DD/YYYY';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default date picker display format string.
     * @returns {string}
     */
    static get DEFAULT_DATE_PICKER_FORMAT() {
        return 'mm/dd/yy';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default date and time display format string for moment.js.
     * @returns {regex}
     */
    static get DEFAULT_MOMENT_DATE_TIME_FORMAT() {
        return `${EzDateTime.DEFAULT_MOMENT_DATE_FORMAT}[ at ]${EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT}`.trim();
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default date and time display format (with seconds) string for moment.js.
     * @returns {regex}
     */
    static get DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT() {
        return `${EzDateTime.DEFAULT_MOMENT_DATE_FORMAT}[ at ]${EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT}`.trim();
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default date picker display format string.
     * @returns {string}
     */
    static get DEFAULT_MONTH_DAY_YEAR_FORMAT() {
        return EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an array of ezClocker supported time zone abbreviations.
     * From timezones.json, object:
     *      {
     *          "value": <string>,
     *          "abbr": <string>, (value stored in this array)
     *          "offset": <number>,
     *          "isdst": <boolean>,
     *          "text": <string>,
     *          "utc": [
     *              <string>,
     *              ...
     *              <string>
     *          ]
     *     }
     * @returns {array}
     */
    static get EZCLOCKER_SUPPORTED_TIMEZONES_ABBR() {
        return [
            // Hawaiian Standard Time
            'HST',
            // Alaskan Standard Time
            'AKDT',
            // Pacific Daylight Time
            'PDT',
            // Pacific Standard Time
            'PST',
            // US Mountain Standard Time
            'UMST',
            // Mountain Standard Time
            'MDT',
            // Central Standard Time
            'CDT',
            // Canada Central Standard Time
            'CCST',
            // Eastern Daylight Time
            'EDT',
            // US Eastern Standard Time
            'UEDT',
            // West Pacific Standard Time
            'WPST'
        ];
    }

    /**
     * @static
     * @public @method
     * Patches an isoDateTime that was URL decoded, replacing the + with a space. Returns the plus to the ISO date.
     * If the provided isoDateTime is undefined or empty, an EzString.EMPTY is returned.
     * Does not validate if the provided isoDateTime is a valid iso date!
     * @param {string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {string}
     */
    static patchBadDecodedIso(isoDateTime = EzString.EMPTY) {
        if (!EzString.hasLength(isoDateTime)) {
            return EzString.EMPTY;
        }

        return !isoDateTime.includes('/') || !isoDateTime.includes('\\')
            ? isoDateTime.replaceAll(' ', '+')
            : isoDateTime;
    }

    /**@static
     * @private @field
     * Stores the preferred display date time foramt
     * Default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME
     * @type {string}
     */
    static #ezPreferredDisplayDateTimeFormat = EzDateTime.DEFAULT_DISPLAY_DATE_TIME;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred display date time foramt
     * @returns {string}
     */
    static get ezPreferredDisplayDateTimeFormat() {
        return this.#ezPreferredDisplayDateTimeFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred display date time foramt
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME
     */
    static set ezPreferredDisplayDateTimeFormat(preferredDisplayDateTimeFormat = EzDateTime.DEFAULT_DISPLAY_DATE_TIME) {
        this.#ezPreferredDisplayDateTimeFormat = EzString.stringWithLengthOrDefault(
            preferredDisplayDateTimeFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred display date time foramt
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDisplayDateTimeFormat
     */
    get ezPreferredDisplayDateTimeFormat() {
        return EzDateTime.ezPreferredDisplayDateTimeFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred display date time foramt
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_DISPLAY_DATE_TIME
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDisplayDateTimeFormat
     */
    set ezPreferredDisplayDateTimeFormat(preferredDisplayDateTimeFormat = EzDateTime.DEFAULT_DISPLAY_DATE_TIME) {
        EzDateTime.ezPreferredDisplayDateTimeFormat = preferredDisplayDateTimeFormat;
    }

    /**
     * @static
     * @private @field
     * Stores
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     * @type {string}
     */
    static #ezPreferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred date time format string
     * @returns {string}
     */
    static get ezPreferredDateFormat() {
        return this.#ezPreferredDateFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date time format string
     * @param {string} preferredDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     */
    static set ezPreferredDateFormat(preferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        this.#ezPreferredDateFormat = EzString.stringWithLengthOrDefault(
            preferredDateFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred date time format string
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    get ezPreferredDateFormat() {
        return EzDateTime.ezPreferredDateFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred date time format string
     * @param {string} preferredDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    set ezPreferredDateFormat(preferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        EzDateTime.ezPreferredDateFormat = preferredDateFormat;
    }
    /**
     * @public @method
     * Sets the preferred display date format for moment objects
     * @param {string} ezPreferredDateFormat
     * Dfeault: EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    ezSetPreferredDisplayDateFormat(ezPreferredDateFormat = EzDateTime.DEFAULT_MOMENT_DATE_FORMAT) {
        EzDateTime.ezPreferredDateFormat = ezPreferredDateFormat;
    }
    /**
     * @public @method
     * Gets the preferred display date format for moment objects
     * Default: 'MM/DD/YYYY'
     * Example: '06/07/2021'
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateFormat
     */
    ezGetPreferredDisplayDateFormat() {
        return EzDateTime.ezPreferredDateFormat;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred date time format string
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     * @type {string}
     */
    static #ezPreferredDateTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred date time format string
     * @returns {string}
     */
    static get ezPreferredDateTimeFormat() {
        return this.#ezPreferredDateTimeFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date time format string
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     */
    static set ezPreferredDateTimeFormat(preferredDateTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT) {
        this.#ezPreferredDateTimeFormat = EzString.stringWithLengthOrDefault(
            preferredDateTimeFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred date time format string
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateTimeFormat
     */
    get ezPreferredDateTimeFormat() {
        return EzDateTime.ezPreferredDateTimeFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred date time format string
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateTimeFormat
     */
    set ezPreferredDateTimeFormat(preferredDateTimeFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT) {
        EzDateTime.ezPreferredDateTimeFormat = preferredDateTimeFormat;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred date time with seconds format
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT
     * @type {string}
     */
    static #ezPreferredDateTimeWithSecondsFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred date time with seconds format
     * @returns {string}
     */
    static get ezPreferredDateTimeWithSecondsFormat() {
        return this.#ezPreferredDateTimeWithSecondsFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date time with seconds format
     * @param {string} ezPreferredDateTimeWithSecondsFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT
     */
    static set ezPreferredDateTimeWithSecondsFormat(preferredDateTimeWithSecondsFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT) {
        this.#ezPreferredDateTimeWithSecondsFormat = EzString.stringWithLengthOrDefault(
            preferredDateTimeWithSecondsFormat,
            EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred date time with seconds format
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     */
    get ezPreferredDateTimeWithSecondsFormat() {
        return EzDateTime.#ezPreferredDateTimeWithSecondsFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred date time with seconds format
     * @param {string} ezPreferredDateTimeWithSecondsFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT
     */
    set ezPreferredDateTimeWithSecondsFormat(preferredDateTimeWithSecondsFormat = EzDateTime.DEFAULT_MOMENT_DATE_TIME_WITH_SECONDS_FORMAT) {
        EzDateTime.ezPreferredDateTimeWithSecondsFormat = preferredDateTimeWithSecondsFormat;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred date picker format
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     * @type {string}
     */
    static #ezPreferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred date picker format
     * @returns {string}
     */
    static get ezPreferredDatePickerFormat() {
        return this.#ezPreferredDatePickerFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date picker format
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     */
    static set ezPreferredDatePickerFormat(preferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT) {
        this.#ezPreferredDatePickerFormat = EzString.stringWithLengthOrDefault(
            preferredDatePickerFormat,
            EzDateTime.DEFAULT_DATE_PICKER_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred date picker format
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    get ezPreferredDatePickerFormat() {
        return EzDateTime.ezPreferredDatePickerFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred date picker format
     * @param {string} preferredDateTimeFormat
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    set ezPreferredDatePickerFormat(preferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT) {
        EzDateTime.ezPreferredDatePickerFormat = preferredDatePickerFormat;
    }
    /**
    * @public @method
    * Gets the preferred date picker display format string.
    * Default: 'mm/dd/yy'
    * Example: '06/07/2021'
    *
    * @returns {string}
    *
    * @deprecated
    * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
    */
    ezGetPreferredDatePickerFormat() {
        return EzDateTime.ezPreferredDatePickerFormat;
    }

    /**
     * @public @method
     * Sets the preferred date picker display format
     * @param {string} preferredDatePickerFormat
     * Default: EzDateTime.DEFAULT_DATE_PICKER_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredDatePickerFormat
     */
    ezSetPreferredDatePickerFormat(preferredDatePickerFormat = EzDateTime.DEFAULT_DATE_PICKER_FORMAT) {
        EzDateTime.ezPreferredDatePickerFormat = EzString.hasLength(preferredDatePickerFormat)
            ? preferredDatePickerFormat
            : EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred day name and date format
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     * @type {string}
     */
    static #ezPreferredMomentDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred day name and date for
     * @returns {string}
     */
    static get ezPreferredMomentDayNameAndDateFormat() {
        return this.#ezPreferredMomentDayNameAndDateFormat;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred day name and date for
     * @param {string} preferredDayNameAndDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     */
    static set ezPreferredMomentDayNameAndDateFormat(preferredDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT) {
        this.#ezPreferredMomentDayNameAndDateFormat = EzString.stringWithLengthOrDefault(
            preferredDayNameAndDateFormat,
            EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT);
    }
    /**
     * @public @readonly @property
     * Gets the preferred day name and date for
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    get ezPreferredMomentDayNameAndDateFormat() {
        return EzDateTime.ezPreferredMomentDayNameAndDateFormat;
    }
    /**
     * @public @property @setter
     * Sets the preferred day name and date for
     * @param {string} preferredDayNameAndDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    set ezPreferredMomentDayNameAndDateFormat(preferredDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT) {
        EzDateTime.ezPreferredMomentDayNameAndDateFormat = preferredDayNameAndDateFormat;
    }
    /**
     * @public @method
     * Returns the preferred day name plus date format string for moment objects.
     * Default format: 'dddd MM/DD/YYYY'
     * Example: Monday '06/07/2021'
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    ezGetPreferredMomentDayNameAndDateFormat() {
        return EzDateTime.ezPreferredMomentDayNameAndDateFormat;
    }

    /**
     * @public @method
     * Sets the preferred moment Day Name and Date format string.
     * @param {string} preferredDayNameAndDateFormat
     * Default: EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredMomentDayNameAndDateFormat
     */
    ezSetPreferredMomentDayNameAndDateFormat(preferredDayNameAndDateFormat = EzDateTime.DEFAULT_MOMENT_DAY_NAME_DATE_FORMAT) {
        EzDateTime.ezPreferredMomentDayNameAndDateFormat = preferredDayNameAndDateFormat;
    }

    /**
     * @static
     * @private @field
     * Stores the preferred locale to use
     * Dfeault: EzLocale.DEFAULT_LOCAL
     * @type {string}
     * A valid enum property value from EzLocale
     */
    static #ezPreferredLocale = EzLocale.DEFAULT_LOCALE;
    /**
     * @static
     * @public @property @getter
     * Gets the preferred locale to use
     * @returns {string}
     * A valid enum property value from EzLocale
     */
    static get ezPreferredLocale() {
        return this.#ezPreferredLocale;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the preferred locale to use
     * @param {string} ezLocale
     * A valid enum property value from EzLocale
     * Default: EzLocale.DEFAULT_LOCALE
     */
    static set ezPreferredLocale(ezLocale = EzLocale.DEFAULT_LOCAL) {
        this.#ezPreferredLocale = EzString.stringWithLengthOrDefault(
            ezLocale,
            EzDateTime.DEFAULT_LOCAL);

        /* TODO: Finish adding locale support
        // Inject the locale into the browser document
        this.#ezInjectMomentLocaleIntoDocument();

        // Set the moment.js locale to the preferred
        moment.locale(this.#ezPreferredLocale);
        */
    }
    /**
     * @static
     * @private @method
     * Injects the moment.js locale file into the document
     */
    /* TODO: Finish adding locale support
    static #ezInjectMomentLocaleIntoDocument() {
        let script = document.createElement('script');

        script.src = `/node_modules/moment/locale/${EzDateTime.ezInstance.ezPreferredLocale.toLowerCase()}.js`;

        script.charSet = "UTF-8";

        document.head.appendChild(script);
    }
    */
    /**
     * @public @property @getter
     * Gets the preferred locale to us
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredLocale
     */
    get ezPreferredLocale() {
        return EzDateTime.ezPreferredLocale;
    }
    /**
     * @public @property @setter
     * Sets the preferred locale to us
     * @param {string} ezLocale
     * A valid enum property value from EzLocale
     * Default: EzLocale.DEFAULT_LOCAL
     * @deprecated
     * Migrate to static property: EzDateTime.ezPreferredLocale
     */
    set ezPreferredLocale(ezLocale = EzLocale.DEFAULT_LOCAL) {
        EzDateTime.ezPreferredLocale = ezLocale;
    }

    /**
     * @static
     * @private @field
     * Stores if the perferred time zone was loaded from options or not
     * Default: false
     * @type {boolean}
     */
    static #ezLoadedPreferredTimeZoneOption = false;
    /**
     * @static
     * @public @property @getter
     * Gets if the perferred time zone was loaded from options or not
     * @returns {boolean}
     */
    static get ezLoadedPreferredTimeZoneOption() {
        return this.#ezLoadedPreferredTimeZoneOption;
    }
    /**
     * @static
     * @public @property @setter
     * Sets if the perferred time zone was loaded from options or not
     * @param {boolean} loadedPreferredTimeZoneOption
     * Default: false
     */
    static set ezLoadedPreferredTimeZoneOption(loadedPreferredTimeZoneOption = false) {
        this.#ezLoadedPreferredTimeZoneOption = EzBoolean.booleanOrFalse(loadedPreferredTimeZoneOption);
    }
    /**
     * @public @property @getter
     * Gets if the perferred time zone was loaded from options or not
     * @returns {boolean}
     * @deprecated
     * Migrate to static property: EzDateTime.ezLoadedPreferredTimeZoneOption
     */
    get ezLoadedPreferredTimeZoneOption() {
        return EzDateTime.ezLoadedPreferredTimeZoneOption;
    }
    /**
     * @public @property @setter
     * Sets if the perferred time zone was loaded from options or not
     * @param {boolean} loadedPreferredTimeZoneOption
     * Default: false
     * @deprecated
     * Migrate to static property: EzDateTime.ezLoadedPreferredTimeZoneOption
     */
    set ezLoadedPreferredTimeZoneOption(loadedPreferredTimeZoneOption = false) {
        EzDateTime.ezLoadedPreferredTimeZoneOption = EzBoolean.booleanOrFalse(loadedPreferredTimeZoneOption);
    }
    /**
     * @static
     * @public @method
     * Sets the active time zone to the active employer's preffered timezone option value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Selected Employer is only available when user is an Employer,
     * a Payroll Manager signing in as a employer, or a manager signing in as a
     * employer.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
    static ezLoadPreferredTimezoneOption() {
        if (!globalThis?.ezApi?.ezclocker) {
            EzLogger.warn(
                EzString.em`
                    Unable to load the preferred time zone at this time:
                    Requried dependency globalThis.ezApi.ezclocker is not available yet.`);

            return;
        }

        if (!globalThis.ezApi.ezclocker?.ezClockerContext) {
            EzLogger.warn(
                EzString.em`
                    Unable to load the preferred time zone at this time:
                    Required dependency globalThis.ezApi.ezclocker.ezClockerContext is not available yet.`);

            return;
        }

        if (!EzObject.isValid(globalThis.ezApi.ezclocker.ezClockerContext.ezSelectedEmployerAccount)) {
            EzLogger.warn(
                EzString.em`
                    Unable to load the preferred time zone at this time:
                    Requires a valid instance for globalThis.ezApi.ezclocker.ezClockerContext.ezSelectedEmployerAccount`);

            return;
        }

        if (EzBoolean.isTrue(EzDateTime.ezLoadedPreferredTimeZoneOption)) {
            EzLogger.info(
                EzString.msg`
                    Skipped loading the preferred time zone option: Option is already loaded.`);

            return;
        }

        const loadedActiveTimeZone = globalThis.ezApi.ezclocker.ezClockerContext.ezReadSelectedEmployerAccountOption(
            EzEmployerOption.EZOPTION_PREFERRED_TIMEZONE,
            EzDateTime.DEFAULT_ACTIVE_TIMEZONE);

        globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
            EzDateTime.ezEventNames.onSupportedTimeZonesReady,
            globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                EzDateTime.ezApiName,
                'Supported time zones read.',
                EzDateTime.ezSupportedTimeZones));

        EzDateTime.ezActiveTimeZone = EzDateTime.isValidTimeZone(loadedActiveTimeZone)
            ? loadedActiveTimeZone
            : EzDateTime.DEFAULT_ACTIVE_TIMEZONE;

        EzDateTime.ezLoadedPreferredTimeZoneOption = true;

        globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
            EzDateTime.ezEventNames.onActiveTimeZoneLoaded,
            globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                EzDateTime.ezApiName,
                'Active time zone loaded from options.',
                EzDateTime.ezActiveTimeZone));
    }
    /**
     * @public @method
     * Sets the active time zone to the active employer's preffered timezone option value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Selected Employer is only available when user is an Employer,
     * a Payroll Manager signing in as a employer, or a manager signing in as a
     * employer.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @deprecated
     * Migrate to static method: EzDateTime.ezLoadPreferredTimezoneOption
     */
    ezLoadPreferredTimezoneOption() {
        EzDateTime.ezLoadPreferredTimezoneOption();
    }

    /**
     * @static
     * @private @field
     * Stores if the preferred locale option was loaded yet
     * Default: false
     * @type {boolean}
     */
    static #ezLoadedPreferredLocaleOption = false;
    /**
     * @static
     * @public @property @getter
     * Gets if the preferred locale option was loaded yet
     * @returns {boolean}
     */
    static get ezLoadedPreferredLocaleOption() {
        return this.#ezLoadedPreferredLocaleOption;
    }
    /**
     * @static
     * @public @property @setter
     * Sets if the preferred locale option was loaded yet
     * @param {boolean} loadedPreferredLocaleOption
     * Default: false
     */
    static set ezLoadedPreferredLocaleOption(loadedPreferredLocaleOption = false) {
        this.#ezLoadedPreferredLocaleOption = EzBoolean.booleanOrFalse(loadedPreferredLocaleOption);
    }
    /**
     * @public @property @getter
     * Gets if the preferred locale option was loaded yet
     * @returns {boolean}
     * @deprecated
     * Migrate to static property: EzDateTimeZone.ezLoadedPreferredLocaleOption
     */
    get ezLoadedPreferredLocaleOption() {
        return EzDateTime.ezLoadedPreferredLocaleOption;
    }
    /**
     * @public @property @setter
     * Sets if the preferred locale option was loaded yet
     * @param {boolean} loadedPreferredLocaleOption
     * Default: false
     * @deprecated
     * Migrate to static property: EzDateTimeZone.ezLoadedPreferredLocaleOption
     */
    set ezLoadedPreferredLocaleOption(loadedPreferredLocaleOption = false) {
        EzDateTime.ezLoadedPreferredLocaleOption = loadedPreferredLocaleOption;
    }

    /**
     * @static
     * @public @method
     * Guesses the local time zone
     * @returns {string}
     */
    static ezGuessLocalTimeZone() {
        return moment.tz.guess(true);
    }
    /**
     * @public @method
     * Guesses the local time zone
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTimeZone.ezGuessLocalTimeZone
     */
    ezGuessLocalTimeZone() {
        return EzDateTime.ezGuessLocalTimeZone();
    }

    /**
     * @static
     * @private @method
     * Sets the default time zone id for the moment.js library.
     * @param {string} momentJsDefaultTimeZone
     * Default: EzDateTime.ezGuessLocalTimeZone()
     * @returns {boolean}
     * True if the moment.js default time zone id was set. False otherwise.
     */
    static #setMomentJsDefaultTimeZone(momentJsDefaultTimeZone = EzDateTime.ezGuessLocalTimeZone()) {
        const activeTimeZoneIdToUse = !EzString.hasLength(momentJsDefaultTimeZone) ||
            '0' === momentJsDefaultTimeZone ||
            ' ' === momentJsDefaultTimeZone ||
            EzDateTime.DEFAULT_ACTIVE_TIMEZONE === momentJsDefaultTimeZone
            ? EzDateTime.ezGuessLocalTimeZone()
            : EzDateTime.timeZoneIdOrDefault(
                momentJsDefaultTimeZone,
                EzDateTime.ezGuessLocalTimeZone());

        const FAILED_LOG_MSG = `Failed to set the default time zone id of ${activeTimeZoneIdToUse} for momentJs.`;

        try {
            moment.tz.setDefault(activeTimeZoneIdToUse);
        } catch (err) {
            try {
                EzLogger.warn(
                    EzString.em`
                        ${FAILED_LOG_MSG}
                        Error reported: ${err.message}.
                        Falling back to guessed local time zone from browser.`);

                moment.tz.setDefault(EzDateTime.ezGuessLocalTimeZone());

                EzLogger.info(`Set moment.js library's active time zone to: ${activeTimeZoneIdToUse}`);
                return true;
            } catch (err2) {
                EzLogger.error(
                    EzString.em`
                        ${FAILED_LOG_MSG}
                        Error reported: ${err2.message}.`,
                    err2);
            }
        }

        return false;
    }

    /**
     * @private @field
     * Stores the current active time zone id
     * @returns {string}
     */
    static #ezActiveTimeZoneId = null;
    /**
     * @public @property
     * Gets the current active time zone id
     * @returns {string}
     */
    static get ezActiveTimeZoneId() {
        if (null == this.#ezActiveTimeZoneId) {
            this.#ezActiveTimeZoneId = EzDateTime.ezGuessLocalTimeZone();
        }

        return this.#ezActiveTimeZoneId;
    }
    /**
     * @public @property
     * Sets the current active time zone id
     * @param {string} activeTimeZoneId
     * Default: EzDateTime.ezGuessLocalTimeZone()
     */
    static set ezActiveTimeZoneId(activeTimeZoneId = EzDateTime.ezGuessLocalTimeZone()) {
        const activeTimeZoneIdToUse = !EzString.hasLength(activeTimeZoneId) ||
            '0' === activeTimeZoneId ||
            ' ' === activeTimeZoneId ||
            EzDateTime.DEFAULT_ACTIVE_TIMEZONE === activeTimeZoneId
            ? EzDateTime.ezGuessLocalTimeZone()
            : EzDateTime.timeZoneIdOrDefault(
                activeTimeZoneId,
                EzDateTime.ezGuessLocalTimeZone());

        if (this.#ezActiveTimeZoneId !== activeTimeZoneIdToUse) {
            this.#ezActiveTimeZoneId = activeTimeZoneIdToUse;

            EzLogger.info(`Set EzDateTime's active time zone id to: ${this.#ezActiveTimeZoneId}`);

            if (this.#setMomentJsDefaultTimeZone(this.#ezActiveTimeZoneId) && globalThis?.ezApi?.ezclocker?.ezEventEngine) {
                globalThis.ezApi.ezclocker.ezEventEngine.ezTriggerEvent(
                    EzDateTime.ezEventNames.onActiveTimeZoneChanged,
                    globalThis.ezApi.ezclocker.ezEventEngine.ezBuildEventData(
                        EzDateTime.ezApiName,
                        `Set EzDateTime's active time zone id and library moment.js's active time zone to: ${this.#ezActiveTimeZoneId}`,
                        this.#ezActiveTimeZoneId));
            }
        }
    }
    /**
     * @public @property
     * Gets the current active time zone id
     * @returns {string}
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    get activeTimeZone() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public @property
     * Sets the current active time zone id
     * @param {string} activeTimeZoneId
     * Default: EzDateTime.ezGuessLocalTimeZone()
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    set activeTimeZone(activeTimeZoneId = EzDateTime.ezGuessLocalTimeZone()) {
        EzDateTime.ezActiveTimeZoneId = activeTimeZoneId;
    }
    /**
     * @public @method
     * Sets the active timezone id
     * @param {string} activeTimeZoneId
     * Default: EzDateTime.ezGuessLocalTimeZone()
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezSetActiveTimezone(activeTimeZoneId = EzDateTime.ezGuessLocalTimeZone()) {
        EzDateTime.ezActiveTimeZoneId = activeTimeZoneId;
    }
    /**
     * @public @method
     * Gets the active timezone for the context
     * @returns {string}
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezGetActiveTimeZone() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public @method
     * Gets the active timezone for the context
     * @returns {string}
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezGetActiveTimeZoneId() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public @method
     * Gets the active timezone for the context
     * @returns {string}
     * @deprecated
     * Migrate to using the property:: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     */
    ezGetActiveTimezone() {
        return EzDateTime.ezActiveTimeZoneId;
    }
    /**
     * @public @method
     * Preferred method to use to obtain the active time zone id.
     * Gets the active timezone for the context
     * @returns {string}
     * @deprecated
     * Migrate to static property: EzDateTime.ezActiveTimeZoneId
     */
    ezGetActiveTimezoneId() {
        return EzDateTime.ezActiveTimeZoneId;
    }

    /**
     * @public @readonly @property
     * Returns the event names triggered by EzDateTime
     * @returns {object}
     * @deprecated
     * Migrate to static property: EzDateTime.ezEventNames property
     */
    get ezEventNames() {
        return EzDateTime.ezEventNames;
    }

    /**
     * @public @readonly @property
     * Returns the browser's default language (locale)
     * @returns {string}
     */
    get ezBrowserLanguage() {
        const lang = navigator.language;

        return EzString.hasLength(lang)
            ? lang
            : EzLocale.DEFAULT_LOCALE;
    }

    /**
     * @public @readonly @property
     * Returns EzLocale based upon the browser default language
     * @returns {string}
     * A valid enum property value from EzLocale
     */
    get ezBrowserLocale() {
        const browserLang = EzDateTime.ezInstance.ezBrowserLanguage;

        return EzLocale.ezLocaleForValue(
            2 <= browserLang.length
                ? browserLang
                    .slice(0, 2)
                    .toUpperCase()
                : browserLang.toUpperCase());
    }

    /**
     * @private @field
     * Stores the all the known time zones
     * Default: EzArray.EMPTY
     * @type {array}
     */
    static #ezAllKnownTimeZones = EzArray.EMPTY;
    /**
     * @static
     * @public @property @getter
     * Gets the all the known time zones
     * @returns {array}
     */
    static get ezAllKnownTimeZones() {
        return this.#ezAllKnownTimeZones;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the all the known time zones
     * @param {array} allKnownTimeZones
     * Default: EzArray.EMPTY
     */
    static set ezAllKnownTimeZones(allKnownTimeZones = EzArray.EMPTY) {
        this.#ezAllKnownTimeZones = EzArray.arrayOrEmpty(allKnownTimeZones);
    }
    /**
     * @public @property @getter
     * Gets the all the known time zones
     * @returns {array}
     * @deprecated
     * Migrate to static property: EzDeateTime.ezAllKnownTimeZones
     */
    get ezAllKnownTimeZones() {
        return EzDateTime.ezAllKnownTimeZones;
    }
    /**
     * @public @property @setter
     * Sets the all the known time zones
     * @param {array} allKnownTimeZones
     * Default: EzArray.EMPTY
     * @deprecated
     * Migrate to static property: EzDeateTime.ezAllKnownTimeZones
     */
    set ezAllKnownTimeZones(allKnownTimeZones = EzArray.EMPTY) {
        EzDateTime.ezAllKnownTimeZones = EzArray.arrayOrEmpty(allKnownTimeZones);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default supported time zones array constant
     * @returns {array}
     */
    static get DEFAULT_SUPPORTED_TIME_ZONES() {
        let guessedLocalZoneInfo = {
            name: EzTimeZone.UTC,
            abbrs: [
                EzTimeZone.UTC
            ],
            untils: EzArray.EMPTY,
            offsets: [
                0
            ],
            population: 0
        };

        try {
            guessedLocalZoneInfo = moment.tz.zone(
                EzDateTime.ezGuessLocalTimeZone());
        } catch (err) {
            EzLogger.error(
                EzString.em`
                    Failed to guess the local time zone due to the following error: ${err.message}
                    Automatic time zone will default to UTC instead.`);
        }

        return [
            {
                value: `Automatic (${guessedLocalZoneInfo.name})`,
                abbr: guessedLocalZoneInfo.abbrs[0],
                offset: guessedLocalZoneInfo.offsets[0],
                isdst: false,
                text: guessedLocalZoneInfo.name,
                utc: [
                    EzDateTime.DEFAULT_ACTIVE_TIMEZONE,
                    guessedLocalZoneInfo.name
                ]
            },
            {
                value: 'US Hawaiian Time (Honolulu)',
                abbr: 'HST',
                offset: -10,
                isdst: false,
                text: '(UTC-10:00) Hawaii',
                utc: [
                    'Pacific/Honolulu'
                ]
            },
            {
                value: 'US Alaskan Time (Anchorage)',
                abbr: 'AKDT',
                offset: -8,
                isdst: true,
                text: '(UTC-09:00) Alaska',
                utc: [
                    'America/Anchorage'
                ]
            },
            {
                value: 'US Pacific Time (Los Angeles)',
                abbr: 'PDT',
                offset: -7,
                isdst: true,
                text: '(UTC-07:00) Pacific Time (US & Canada)',
                utc: [
                    'America/Los_Angeles'
                ]
            },
            {
                value: 'US Mountain Time (Denver)',
                abbr: 'MDT',
                offset: -6,
                isdst: true,
                text: '(UTC-07:00) Mountain Time (US & Canada)',
                utc: [
                    'America/Denver'
                ]
            },
            {
                value: 'US Mountain Standard Time (Phoenix)',
                abbr: 'MDT',
                offset: -6,
                isdst: true,
                text: '(UTC-07:00) Mountain Standard Time (US & Canada)',
                utc: [
                    'America/Phoenix'
                ]
            },
            {
                value: 'US Central Time (Chicago)',
                abbr: 'CDT',
                offset: -5,
                isdst: true,
                text: '(UTC-06:00) Central Time (US & Canada)',
                utc: [
                    'America/Chicago'
                ]
            },
            {
                value: 'US Eastern Time (New York)',
                abbr: 'EDT',
                offset: -4,
                isdst: true,
                text: '(UTC-05:00) Eastern Time (US & Canada)',
                utc: [
                    'America/New_York'
                ]
            },
            {
                value: 'Chamorro Standard Time (Guam)',
                abbr: 'WPST',
                offset: 10,
                isdst: false,
                text: '(UTC+10:00) Guam, Port Moresby',
                utc: [
                    'Pacific/Guam'
                ]
            }
        ];
    }

    /**
     * @private @field
     * Stores the supported time zones
     * Default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES
     * @type {array}
     */
    static #ezSupportedTimeZones = EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES;
    /**
     * @static
     * @public @property @getter
     * Gets the supported time zones
     * @returns {array}
     */
    static get ezSupportedTimeZones() {
        return this.#ezSupportedTimeZones;
    }
    /**
     * @static
     * @public @property @getter
     * Sets the supported time zones
     * @param {array} supportedTimeZones
     * Default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES
     */
    static set ezSupportedTimeZones(supportedTimeZones = EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES) {
        this.#ezSupportedTimeZones = EzArray.arrayOrDefault(
            supportedTimeZones,
            EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES);
    }
    /**
     * @public @property @getter
     * Gets the supported time zones
     * @returns {array}
     * @deprecated
     * Migrate to static property: EzDateTime.ezSupportedTimeZones
     */
    get ezSupportedTimeZones() {
        return EzDateTime.ezSupportedTimeZones;
    }
    /**
     * @public @property @getter
     * Sets the supported time zones
     * @param {array} supportedTimeZones
     * Default: EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES
     * @deprecated
     * Migrate to static property: EzDateTime.ezSupportedTimeZones
     */
    set ezSupportedTimeZones(supportedTimeZones = EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES) {
        EzDateTime.ezSupportedTimeZones = EzArray.arrayOrDefault(
            supportedTimeZones,
            EzDateTime.DEFAULT_SUPPORTED_TIME_ZONES);
    }

    /**
     * @static
     * @public @readonly @property
     * Gets if the now date time is within a leap year or not.
     * @returns {boolean}
     */
    static get nowIsLeapYear() {
        return EzDateTime.now(EzDateTime.ezActiveTimeZoneId).isLeapYear();
    }

    /**
     * @static
     * @public @readonly @property
     * Gets if the provided aMoment is within a leap year or not.
     * @param {moment} aMoment
     * @returns {boolean}
     */
    static isLeapYear(aMoment = null) {
        if (!EzDateTime.isValidMoment(aMoment)) {
            throw EzBadParamException(
                'aMoment',
                this,
                `get ${this.isLeapYear.toString()}`);
        }

        return aMoment.isLeapYear();
    }

    /**
     * @public @method
     * Initializes EzDateTime
     * @returns {EzDateTime}
     */
    ezInit() {
        EzDateTime.ezInstance.ezRegisterEvents();

        EzDateTime.ezInstance.ezRegisterWantEvents();

        return EzDateTime.ezInstance;
    }

    /**
     * @public @method
     * Registers events triggered by this class
     */
    ezRegisterEvents() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzDateTime.ezApiName,
            EzDateTime.ezEventNames.onActiveTimeZoneChanged);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzDateTime.ezApiName,
            EzDateTime.ezEventNames.onActiveTimeZoneLoaded);

        globalThis.ezApi.ezclocker.ezEventEngine.ezRegisterEvent(
            EzDateTime.ezApiName,
            EzDateTime.ezEventNames.onSupportedTimeZonesReady);
    }

    /**
     * @public @method
     * Registers want events for initialization
     */
    ezRegisterWantEvents() {
        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzDateTime.ezEventNames.onActiveTimeZoneChanged,
            EzDateTime.ezApiName,
            EzDateTime.ezInstance.ezDisplayActiveTimezone);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzContextEventName.onSelectedEmployerAccountReady,
            EzDateTime.ezApiName,
            EzDateTime.ezInstance.ezHandleOnSelectedEmployerReady,
            true);

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzContextEventName.onUserContextReady,
            EzDateTime.ezApiName,
            EzDateTime.ezInstance.ezHandleOnUserContextReady,
            true);
    }

    /**
     * @public @method
     * Loads the authenticated users's preferred locale option
     */
    ezLoadAuthenticatedUserPreferredLocaleOption() {
        if (EzBoolean.isTrue(EzDateTime.ezLoadedPreferredLocaleOption)) {
            // Already loaded
            return;
        }

        // Sets the locale from the browser's navigator.language property
        EzDateTime.ezPreferredLocale = EzDateTime.ezInstance.ezBrowserLocale;

        if (EzBoolean.isTrue(globalThis.ezApi.ezclocker['ezClockerContext']?.ready) &&
            EzBoolean.isTrue(globalThis.ezApi.ezclocker.ezClockerContext.ezGetUserContext().ready)) {

            // TODO: Finishe locale support: Add user level options and ready the preferred locale value

            EzDateTime.ezLoadedPreferredLocaleOption = true;
        }
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default option object for the JQuery Date Picker Input
     * @returns {Object}
     */
    static get defaultDatePickerOptions() {
        return {
            dateFormat: globalThis.ezApi.ezclocker.ezDateTime.ezGetPreferredDatePickerFormat(),
            defaultDate: globalThis.ezApi.ezclocker.ezDateTime.ezToJsDate(EzDateTime.nowWithStartOfDay()),
            constrainInput: false,
            changeMonth: true,
            changeYear: true
        };
    }

    /**
     * @public @method
     * Returns the default option object for the JQuery Date Picker Input
     * @returns {Object}
     * @deprecated
     * Migrate to static property: EzDateTime.defaultDatePickerOptions
     */
    ezGetDefaultDatePickerOptions() {
        return EzDateTime.defaultDatePickerOptions;
    }

    /**
     * @public @method
     * Applies the jQuery DatePicker initialization to the provided elementId.
     * Component details: https://jqueryui.com/datepicker/
     * @param {string} dateTimePickerId
     * @param {Object|null} eventHandlers
     * @param {Object|null} propertyOverrides
     * @param {moment|null} aInitialMoment
     * @returns {Object}
     * Returns the jQuery reference to the datepicker.
     * @deprecated
     * Migrate to static method: EzDateTime.initDatePicker(dateTimePickerId, eventHandlers, propertyOverrides, aInitialMoment)
     */
    ezInitDateTimePicker(dateTimePickerId, eventHandlers, propertyOverrides, aInitialMoment) {
        return EzDateTime.initDatePicker(
            dateTimePickerId,
            eventHandlers,
            propertyOverrides,
            aInitialMoment);
    }

    /**
     * @static`
     * @public @method
     * Applies the jQuery DatePicker initialization to the provided elementId.
     * Component details: https://jqueryui.com/datepicker/
     * @param {string} datePickerId
     * @param {undefined|null|object} eventHandlers
     * Default: null
     * @param {undefined|null|object} propertyOverrides
     * Default: null
     * @param {undefined|null|moment} aInitialMoment
     * Default: null
     * @param {undefined|null|boolean} isEndPeriod
     * Default: false
     * @returns {object}
     * Returns the jQuery reference to the datepicker.
     */
    static initDatePicker(datePickerId, eventHandlers = null, propertyOverrides = null, aInitialMoment = null, isEndPeriod = false) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                this,
                this.initDatePicker);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            throw new EzBadStateException(
                `Expected HTML element id="${datePickerId}" to reference a valid date picker HTML input element.`,
                `Date picker HTML input element with id="${datePickerId}" does not exist.`,
                this,
                this.initDatePicker,
                `Failed to initialize a date picker HTML input element with id="${datePickerId}".`);
        }

        const datePickerOptions = EzDateTime.defaultDatePickerOptions;

        if (EzObject.isValid(eventHandlers)) {
            for (const eHandlers in eventHandlers) {
                if (EzFunction.isFunction(eventHandlers[eHandlers])) {
                    datePickerOptions[eHandlers] = eventHandlers[eHandlers];
                }
            }
        }

        if (EzObject.isValid(propertyOverrides)) {
            for (const pOverrides in propertyOverrides) {
                datePickerOptions[pOverrides] = propertyOverrides[pOverrides];
            }
        }

        // Initialize the datepicker with the options.
        const updateJQueryDatePicker = datePickerHTMLElement.datepicker(datePickerOptions);

        // Assign the initial value (if any)
        aInitialMoment = EzDateTime.isValidMoment(aInitialMoment)
            ? aInitialMoment
            : null;

        if (EzBoolean.isTrue(isEndPeriod)) {
            EzDateTime.setDatePickerValueEndOfDay(
                datePickerId,
                aInitialMoment);
        } else {
            EzDateTime.setDatePickerValueStartOfDay(
                datePickerId,
                aInitialMoment);
        }

        return updateJQueryDatePicker;
    }

    /**
     * @static`
     * @public @method
     * Applies the jQuery DatePicker initialization to the provided elementId.
     * Component details: https://jqueryui.com/datepicker/
     * @param {string} datePickerId
     * @param {undefined|null|object} eventHandlers
     * Default: null
     * @param {undefined|null|object} propertyOverrides
     * Default: null
     * @param {undefined|null|moment} aInitialMoment
     * Default: null
     * @param {undefined|null|boolean} isEndPeriod
     * Default: false
     * @returns {object}
     * Returns the jQuery reference to the datepicker.
     * @deprecated
     * Migrate to static method: EzDateTime.initDatePicker(datePickerId, eventHandlers, propertyOverrides, aInitialMoment, isEndPeriod)
     */
    ezInitDatePicker = EzDateTime.initDatePicker;

    /**
     * @public @method
     * Creates a moment instance from the provided iso8601DateTime value
     * @param {undefined|null|string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.fromIso(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIso(isoDateTime)
     */
    ezFromIso8601(isoDateTime = EzString.EMPTY) {
        return EzDateTime.fromIso(isoDateTime);
    }

    /**
     * @public @method
     * Creates a new moment with the provided ISO string.
     * If the iso string is empty, null is returned. If the iso string is not valid, ezNowWithStartOfDay is returned.
     * @param {string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.fromIso(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIso(isoDateTime)
     */
    ezCreateFromIso(isoDateTime = EzString.EMPTY) {
        return EzDateTime.fromIso(isoDateTime);
    }

    /**
     * @public @method
     * Creates a new moment with the provided ISO string.
     * If the provided isoString is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * Default: EzString.EMPTY
     * @returns {moment|string}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.ezInstance.ezFromIsoOrEmpty(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIsoOrEmpty(isoDateTime)
     */
    ezCreateFromIsoOrEmpty(isoDateTime = EzString.EMPTY) {
        return EzDateTime.ezInstance.ezFromIsoOrEmpty(isoDateTime);
    }

    /**
     * @public @method
     * Creates a new moment with the provided ISO string.
     * If the provided isoString is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * @returns {moment|string}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.ezInstance.ezFromIsoOrNull(isoDateTime)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIsoOrNull(isoDateTime)
     */
    ezCreateFromIsoOrNull(isoDateTime) {
        return EzDateTime.ezInstance.ezFromIsoOrEmpty(isoDateTime);
    }

    /**
     * @public @method
     * Creates a moment instance from the provided iso string OR returns the provided defaultMoment if the iso string is not valid.
     * @param {string} isoDateTime
     * @param {moment} defaultMoment
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     *  1) Within EzDateTime class: EzDateTime.ezInstance.ezFromIsoOrDefault(isoDateTime, defaultMoment)
     *  2) Outside EzDateTime class: globalThis.ezApi.ezclocker.ezDateTime.ezFromIsoOrDefault(isoDateTime, defaultMoment)
     */
    ezCreateFromIsoOrDefaultMoment(isoDateTime, defaultMoment) {
        return EzDateTime.ezInstance.ezFromIsoOrDefault(
            isoDateTime,
            defaultMoment);
    }

    /**
     * TODO: Finish locale work
     * @static
     * @public @method
     * Creates a moment instance from the provided iso8601DateTime value.
     * Sets the timezone to the preferred time zone.
     * @param {undefined|null|string} isoDateTime
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Default: EzDateTime.ezPreferredLocale
     * @returns {moment}
     */
    static fromIso(isoDateTime = null, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezPreferredLocale*/) {
        const momentFromIso = EzDateTime.ezInstance.ezFromIsoOrNull(
            isoDateTime,
            timeZoneId
            /*, ezLocale*/);

        if (null == momentFromIso) {
            throw new EzBadParamException(
                'isoString',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromIso);
        }

        return momentFromIso;
    }

    ezFromIso = EzDateTime.fromIso;

    /**
     * TODO: Finish locale work
     * @public @method
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {string|moment}
     */
    ezFromIsoOrEmpty(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        const momentFromIso = EzDateTime.ezInstance.ezFromIsoOrNull(
            isoDateTime,
            timeZoneId
            /*, ezLocale*/);

        if (null == momentFromIso) {
            return EzString.EMPTY;
        }

        return momentFromIso;
    }

    /**
     * TODO: Finish locale work
     * @public @method
     * Creates a moment instance from the provided isoDateTime value
     * @param {string} isoDateTime
     * @param {any} defaultMoment
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     */
    ezFromIsoOrDefault(isoDateTime, defaultMoment, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        const momentFromIso = EzDateTime.ezInstance.ezFromIsoOrNull(
            isoDateTime,
            timeZoneId
            /*, ezLocale */);

        return null != momentFromIso
            ? momentFromIso
            : defaultMoment;
    }

    /**
     * TODO: Finish locale work
     * @public @method
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @returns {null|moment}
     */
    ezTimeStampFromIsoOrNull(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        if (!EzString.hasText(isoDateTime)) {
            return null;
        }

        isoDateTime = EzDateTime.patchBadDecodedIso(isoDateTime);

        if (!EzDateTime.validateIso(isoDateTime)) {
            return null;
        }

        /*
        if (!EzString.hasLength(ezLocale)) {
            ezLocale = EzDateTime.ezInstance.ezPreferredLocale;
        }

        let momentInstance = moment(isoString).locale(ezLocale);
        */

        try {
            const momentInstance = moment(isoDateTime)
                .tz(EzDateTime.safeTimeZoneId(timeZoneId));

            return EzDateTime.isValidMoment(momentInstance)
                ? momentInstance
                : null;
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided isoDateTime of '${isoDateTime}' to a moment instance
                    due to the following error: ${err.message}`);

            return null;
        }
    }

    /**
     * TODO: Finish locale work
     * @public @method
     * Creates a moment instance from the provided isoDateTime value
     * @param {string} isoDateTime
     * @param {any} defaultMoment
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     */
    ezTimeStampFromIsoOrDefault(isoDateTime, defaultMoment, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        const momentFromIso = EzDateTime.ezInstance.ezTimeStampFromIsoOrNull(
            isoDateTime,
            timeZoneId
            /*, ezLocale */);

        return null != momentFromIso
            ? momentFromIso
            : defaultMoment;
    }

    /**
     * TODO: Finish locale work
     * @public @method
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the input TZ and if absent, preferred time zone.
     * @param {string} isoDateTime
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     */
    ezFromIsoAndTimeZone(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId/*, ezLocale = EzDateTime.ezInstance.ezPreferredLocale*/) {
        const momentFromIso = EzDateTime.ezInstance.ezFromIsoOrNull(
            isoDateTime,
            timeZoneId
            /*, ezLocale */);

        if (null === momentFromIso) {
            throw new EzBadParamException(
                'isoDateTime',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromIsoAndTimeZone);
        }

        return momentFromIso;
    }

    /**
     * TODO: Finish locale work
     * @static
     * @public @method
     * Creates a moment instance from the provided isoDateTime value.
     * Sets the timezone to the preferred time zone.
     * @param {undefined|null|string} isoDateTime
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Default is: EzDateTime.ezPreferredLocale
     * If the provided isoDateTime is not a valid ISO 8601 value, then empty string is returned.
     * @returns {null|moment}
     */
    static fromIsoOrNull(isoDateTime, timeZoneId = EzDateTime.ezActiveTimeZoneId /*, ezLocale = EzDateTime.ezPreferredLocale*/) {
        if (!EzString.hasLength(isoDateTime)) {
            return null;
        }

        isoDateTime = EzDateTime.patchBadDecodedIso(isoDateTime);

        if (!EzDateTime.validateIso(isoDateTime)) {
            return null;
        }

        /*
        if (!EzString.hasLength(ezLocale)) {
            ezLocale = EzDateTime.ezInstance.ezPreferredLocale;
        }

        let momentInstance = moment(isoString).locale(ezLocale);
        */

        try {
            return moment(isoDateTime)
                .tz(EzDateTime.safeTimeZoneId(timeZoneId));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided isoDateTime of '${isoDateTime}' to a moment instance
                    due to the following error: ${err.message}`);

            return null;
        }
    }

    ezFromIsoOrNull = EzDateTime.fromIsoOrNull;

    /**
     * @public @method
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     * @deprecated
     * Migrate to static method: EzDateTime.validateIso(proposedIsoDateTimeValue)
     */
    ezValidateIsIso8601(proposedValue = EzString.EMPTY) {
        return EzDateTime.validateIso(proposedValue);
    }

    /**
     * @public @method
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     * @deprecated
     * Migrate to static method: EzDateTime.validateIso(proposedIsoDateTimeValue)
     */
    ezValidateIsoString(proposedIsoDateTimeValue = EzString.EMPTY) {
        return EzDateTime.validateIso(proposedIsoDateTimeValue);
    }

    /**
     * @static
     * @public @method
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     */
    static validateIso(proposedIsoDateTimeValue = EzString.EMPTY) {
        /**
         * @method
         * Builds the validation failed result.
         * @returns {object}
         */
        const ezBuildValidationFailedResult = () => {
            // Proposed value considered a valid ISO 8601 value
            return {
                proposedIsoDateTimeValue,
                proposedValue: proposedIsoDateTimeValue,
                valid: false,
                iso8601Value: null,
                message: `The proposed value of ${proposedIsoDateTimeValue} is not a valid ISO 8601 value.`
            };
        };

        if (!EzString.hasLength(proposedIsoDateTimeValue)) {
            return ezBuildValidationFailedResult();
        }

        if (!EzDateTime.ISO_VALIDATION_REGEX.test(proposedIsoDateTimeValue)) {
            // If the regex fails, fall back to creating a Javascript Date from the proposed value.
            try {
                // See if Javascript's Date method will parse the value:
                const jsDate = new Date(proposedIsoDateTimeValue);

                if (!EzObject.isValid(jsDate) || 'Invalid Date' === jsDate.toString()) {
                    // All validation checks failed
                    return ezBuildValidationFailedResult();
                }
            } catch (err) {
                EzLogger.debug(
                    EzString.em`
                        Failed to create a new Javascript Date instance from the proposedValue ${proposedIsoDateTimeValue}.
                        Error reported: ${err.message}`,
                    err);

                // All validation checks failed
                return ezBuildValidationFailedResult();
            }
        }

        // Proposed value considered a valid ISO 8601 value
        return {
            proposedIsoDateTimeValue,
            proposedValue: proposedIsoDateTimeValue,
            valid: true,
            iso8601Value: proposedIsoDateTimeValue,
            message: `The proposed value of ${proposedIsoDateTimeValue} is a valid ISO 8601 value.`
        };
    }

    /**
     * @public @method
     * Evaluates the provided proposedISO8601 value to determine if the value is a valid ISO 8601 value.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: EzString.EMPTY
     * @returns {object}
     * Example returned object:
     *  {
     *      valid: {boolean},
     *      proposedValue: {undefined|null|string},
     *      iso8601Value: {null|string},
     *      message: {string}
     *  }
     * @deprecated
     * Migrate to static method: EzDateTime.validateIso(proposedIsoDateTimeValue)
     */
    ezValidateIso = EzDateTime.validateIso;

    /**
     * @static
     * @public @method
     * Determines if the provided isoDateTime is a valid ISO date time string.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: null
     * @returns {boolean}
     */
    static isValidIso(proposedIsoDateTimeValue = null) {
        return EzBoolean.isTrue(EzDateTime.validateIso(proposedIsoDateTimeValue).valid);
    }

    /**
     * @static
     * @public @method
     * Determines if the provided isoDateTime is a valid ISO date time string.
     * @param {undefined|null|string} proposedIsoDateTimeValue
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidIso
     */
    ezIsValidIso = EzDateTime.isValidIso;

    /**
     * @static
     * @public @method
     * Appends the time from the time picker to the provided moment instance.
     * @param {string} timePickerId
     * @param {moment} momentInstance
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static appendTimeFromTimePicker(timePickerId, momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezAppendTimeFromTimePicker);
        }
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezAppendTimeFromTimePicker);
        }

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        const timePickerElement = EzUx.$id(timePickerId);

        const defaultResponse = EzDateTime.withZone(
            momentInstance,
            timeZoneId);

        if (!EzObject.isValid(timePickerElement) || EzNumber.ZERO === timePickerElement?.length) {
            EzLogger.error(
                EzString.msg`
                    Failed to locate a time picker input HTML element with id="${timePickerId}".
                    Falling back to returning the default response (${EzDateTime.toIso(momentInstance)}) in time zone ${timeZoneId}.`);

            return defaultResponse;
        }

        const hour = timePickerElement?.timepicker('getHour');

        const min = timePickerElement?.timepicker('getMinute');

        if (!EzObject.isValid(hour) || !EzObject.isValid(min)) {
            EzLogger.error(
                EzString.msg`
                    Failed to obtain the time picker input HTML element with id="${timePickerId}"'s hour and/or minute values.
                    Falling back to returning the default response (${EzDateTime.toIso(momentInstance)}) in time zone ${timeZoneId}.`);

            return defaultResponse;
        }

        return EzDateTime.withZone(
            momentInstance,
            timeZoneId)
            .hours(hour)
            .minutes(min)
            .seconds(0)
            .milliseconds(0);
    }

    ezAppendTimeFromTimePicker = EzDateTime.appendTimeFromTimePicker;

    /**
     * @public @method
     * Returns the defaul options for time picker initialization.
     * @returns {Object}
     */
    ezGetDefaultTimePickerOptions() {
        return {
            timeFormat: 'hh:mm tt',
            showPeriod: true,
            showLeadingZero: true
        };
    }

    /**
     * @public @method
     * Initializes a UX time picker with defaults. Applies any additional options or overrides to the default
     * options provided in timePickerOptionsOverrides.
     * @param {string} timePickerId
     * @param {undefiend|null|moment} initialMoment
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNow()
     * @param {undefined|null|object} timePickerOptionsOverrides
     * Default: null
     * @returns {object}
     * Jquery referece to the time picker
     */
    ezInitTimePicker(timePickerId, initialMoment = globalThis.ezApi.ezclocker.ezDateTime.ezNow(), timePickerOptionsOverrides = null) {
        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezInitTimePicker);
        }

        if (!EzDateTime.isValidMoment(initialMoment)) {
            initialMoment = globalThis.ezApi.ezclocker.ezDateTime.ezNow();
        }

        const timePickerOptions = EzDateTime.ezInstance.ezGetDefaultTimePickerOptions();

        if (EzObject.isValid(timePickerOptionsOverrides)) {
            for (const prop in timePickerOptionsOverrides) {
                timePickerOptions[prop] = timePickerOptionsOverrides[prop];
            }
        }

        const timePickerRef = globalThis.ezApi.ezclocker.ezUi.ezId(timePickerId).timepicker(timePickerOptions);

        EzDateTime.ezInstance.ezSetTimePickerValue(timePickerId, initialMoment);

        return timePickerRef;
    }

    /**
     * @public @method
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * EzDateTime.ezInstance.ezMomentFromDatePickerStartOfDay(
     *     datePickerId,
     *     returnNullInsteadOfTodayIfNull)
     * EzDateTime.fromDatePickerEndOfDay(
     *     datePickerId,
     *     returnNullInsteadOfTodayIfNull)
     */
    ezDateFromDatePicker(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.ezInstance.ezMomentFromDatePickerStartOfDay(datePickerId, false, timeZoneId);
    }

    /**
     * @public @method
     * Obtains a moment instance, in the selected timezone for the date in a date-picker.
     * The time is set to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * @returns {moment}
     * @deprecated
     * Migrate to one of the following:
     * EzDateTime.ezInstance.ezDateTimeFromDatePickerWithTimeAtStartOfDay(
     *     datePickerId,
     *     returnNullInsteadOfTodayIfNull)
     * EzDateTime.ezInstance.ezDateTimeFromDatePickerWithTimeAtEndOfDay(
     *     datePickerId,
     *     returnNullInsteadOfTodayIfNull)
     */
    ezMomentFromDatePicker(datePickerId, returnNullInsteadOfTodayIfNull, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.ezInstance.ezMomentFromDatePickerStartOfDay(
            datePickerId,
            returnNullInsteadOfTodayIfNull,
            timeZoneId);
    }

    /**
     * @static
     * @public @method
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * Default: false
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static fromDatePickerStartOfDay(datePickerId, returnNullInsteadOfTodayIfNull = false, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                this.ezInstance,
                this.fromDatePickerStartOfDay);
        }

        const defaultMoment = EzBoolean.booleanOrFalse(returnNullInsteadOfTodayIfNull)
            ? EzDateTime.nowWithStartOfDay(timeZoneId)
            : null;

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        const datePickerElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerElement) || EzNumber.ZERO === datePickerElement?.length) {
            try {
                throw new EzBadStateException(
                    `Expected date picker HTML element to exist for id="${datePickerId}"`,
                    `Lookup of HTML element for id' id="${datePickerId}" returned undefined or null.`,
                    this,
                    this.fromDatePickerEndOfDay,
                    `Could not find a valid date picker HTML input element for id="${datePickerId}"`);
            } catch (err) {
                const defaultResponseInfo = null == defaultMoment
                    ? 'Will return the default null response instead.'
                    : EzString.em`
                        Will return the default date time response of EzDateTime.nowWithStartOfDay(timeZoneId)
                        (${EzDateTime.toIso(defaultMoment)} instead.`;

                EzLogger.warn(
                    EzString.em`
                        Failed to obtain the date and time value from the HTML date picker input element with id="${datePickerId}"
                        due to the following error: ${err.message}.
                        ${defaultResponseInfo}`,
                    err);
            }

            return defaultMoment;
        }

        const jsDate = datePickerElement.datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            try {
                throw new EzBadStateException(
                    EzString.em`
                        Expected call to datePickerElement.datepicker('getDate') to return a valid Javascript Date instance
                        for HTML date picker input with id="${datePickerId}"`,
                    EzString.em`
                        Call to datePickerElement.datepicker('getDate') return an undefined or null value instead
                        for HTML date picker input with id="${datePickerId}"`,
                    this,
                    this.fromDatePickerEndOfDay,
                    `Could not find a valid date picker HTML input element for id="${datePickerId}"`);
            } catch (err) {
                const defaultResponseInfo = null == defaultMoment
                    ? 'Will return the default null response instead.'
                    : EzString.em`
                        Will return the default date time response of EzDateTime.nowWithStartOfDay(timeZoneId)
                        (${EzDateTime.toIso(defaultMoment)} instead.`;

                EzLogger.warn(
                    EzString.em`
                        Failed to obtain the Javascript date instance for the HTML date picker input element with id="${datePickerId}"
                        due to the following error: ${err.message}.
                        ${defaultResponseInfo}`,
                    err);
            }

            return defaultMoment;
        }

        const jsDateAsIsoDateTime = EzDateTime.jsDateToIsoDateTime(
            jsDate,
            // Hours
            0,
            // Minutes
            0,
            // Seconds
            0,
            // Milliseconds
            0,
            timeZoneId);

        return EzDateTime.withTimeAtStartOfDay(
            moment(jsDateAsIsoDateTime).tz(timeZoneId),
            timeZoneId);
    }

    ezMomentFromDatePickerStartOfDay = EzDateTime.fromDatePickerStartOfDay;

    /**
     * @static
     * @public @method
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the end of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @returns {moment}
     */
    static fromDatePickerEndOfDay(datePickerId, returnNullInsteadOfTodayIfNull, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                this,
                this.fromDatePickerEndOfDay);
        }

        const defaultMoment = EzBoolean.booleanOrFalse(returnNullInsteadOfTodayIfNull)
            ? EzDateTime.nowWithEndOfDay(timeZoneId)
            : null;

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        const datePickerElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerElement) || EzNumber.ZERO === datePickerElement?.length) {
            try {
                throw new EzBadStateException(
                    `Expected date picker HTML element to exist for id="${datePickerId}"`,
                    `Lookup of HTML element for id' id="${datePickerId}" returned undefined or null.`,
                    this,
                    this.fromDatePickerEndOfDay,
                    `Could not find a valid date picker HTML input element for id="${datePickerId}"`);
            } catch (err) {
                const defaultResponseInfo = null == defaultMoment
                    ? 'Will return the default null response instead.'
                    : EzString.em`
                        Will return the default date time response of EzDateTime.nowWithEndOfDay(timeZoneId)
                        (${EzDateTime.toIso(defaultMoment)} instead.`;

                EzLogger.warn(
                    EzString.em`
                        Failed to obtain the date and time value from the HTML date picker input element with id="${datePickerId}"
                        due to the following error: ${err.message}.
                        ${defaultResponseInfo}`,
                    err);
            }

            return defaultMoment;
        }

        const jsDate = datePickerElement.datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            try {
                throw new EzBadStateException(
                    EzString.em`
                        Expected call to datePickerElement.datepicker('getDate') to return a valid Javascript Date instance
                        for HTML date picker input with id="${datePickerId}"`,
                    EzString.em`
                        Call to datePickerElement.datepicker('getDate') return an undefined or null value instead
                        for HTML date picker input with id="${datePickerId}"`,
                    this,
                    this.fromDatePickerEndOfDay,
                    `Could not find a valid date picker HTML input element for id="${datePickerId}"`);
            } catch (err) {
                const defaultResponseInfo = null == defaultMoment
                    ? 'Will return the default null response instead.'
                    : EzString.em`
                        Will return the default date time response of EzDateTime.nowWithEndOfDay(timeZoneId)
                        (${EzDateTime.toIso(defaultMoment)} instead.`;

                EzLogger.warn(
                    EzString.em`
                        Failed to obtain the Javascript date instance for the HTML date picker input element with id="${datePickerId}"
                        due to the following error: ${err.message}.
                        ${defaultResponseInfo}`,
                    err);
            }

            return defaultMoment;
        }

        const jsDateAsIsoDateTime = EzDateTime.jsDateToIsoDateTime(
            jsDate,
            // Hours
            EzNumber.TWENTY_THREE,
            // Minutes
            EzNumber.FIFTY_NINE,
            // Seconds
            EzNumber.FIFTY_NINE,
            // Milliseconds
            EzNumber.NINE_HUNDRED_NINETY_NINE,
            timeZoneId);

        return EzDateTime.withTimeAtEndOfDay(
            moment(jsDateAsIsoDateTime).tz(timeZoneId),
            timeZoneId);
    }

    /**
     * @static
     * @public @method
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the end of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.fromDatePickerEndOfDay(datePickerId, returnNullInsteadOfTodayIfNull, timeZoneId)
     */
    ezMomentFromDatePickerEndOfDay = EzDateTime.fromDatePickerEndOfDay;

    /**
     * @static
     * @public @method
     * Transforms the provided javascript date (jsDate) to an ISO date without time or timezone information.
     * @param {Date} jsDate
     * A valid Javascript Date instance
     * Default: null
     * @returns {string}
     */
    static jsDateToIsoDate(jsDate = null) {
        if (!EzObject.isValid(jsDate) || !EzFunction.isFunction(jsDate?.getFullYear)) {
            throw new EzBadParamException(
                'jsDate',
                EzDateTime,
                EzDateTime.ezJsDateToIsoDate);
        }

        const yearStr = EzNumber.asStringOrDefault(
            jsDate.getFullYear(),
            moment().year())
            .padStart(4, '0');

        const monthStr = EzNumber.asStringOrDefault(
            jsDate.getMonth() + 1,
            '01')
            .padStart(2, '0');

        const dayStr = EzNumber.asStringOrDefault(
            jsDate.getDate(),
            '01')
            .padStart(2, '0');

        return `${yearStr}-${monthStr}-${dayStr}`;
    }

    /**
     *@public @method
     * Transforms the provided javascript date (jsDate) to an ISO date without time or timezone information.
     * @param {Date} jsDate
     * Default: null
     * A valid Javascript Date instance
     * @returns {string}
     * @deprecated:
     * Migrate to static method: EzDateTime.jsDateToIsoDate(jsDate)
     */
    ezJsDateToIsoDate = EzDateTime.jsDateToIsoDate;

    /**
     * @static
     * @public @method
     * Transforms the provided javascript date (jsDate) to an ISO date without time or timezone information.
     * @param {Date} jsDate
     * A valid Javascript Date instance
     * Default: null
     * @param {undefined|null|number} hours
     * Default: 0
     * @param {undefined|null|number} minutes
     * Default: 0
     * @param {undefined|null|number} seconds
     * Default: 0
     * @param {undefined|null|number} milliseconds
     * Default: 0
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     */
    static jsDateToIsoDateTime(jsDate = null, hours = 0, minutes = 0, seconds = 0, milliseconds = 0, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzObject.isValid(jsDate) || !EzFunction.isFunction(jsDate?.getFullYear)) {
            throw new EzBadParamException(
                'jsDate',
                EzDateTime,
                EzDateTime.jsDateToIsoDateTime);
        }

        timeZoneId = EzDateTime.safeTimeZoneId(timeZoneId);

        return moment.tz(jsDate, timeZoneId).hours(hours).minutes(minutes).seconds(seconds).milliseconds(milliseconds).format();
    }

    /**
     * @static
     * Transforms a JS Date to an ISO string with local time zone.
     * @param {Date} jsDate
     * @param {Object} options
     * options = {
     *      year: true,
     *      month: true,
     *      date: true,
     *      hours: true,
     *      minutes: true,
     *      seconds: true,
     *      milliseconds: false,
     *      zone: true
     *  }
     * @returns {string}
     */
    static jsDateToIso(jsDate, options = {year:true, month:true, date:true, hours:true, minutes:true, seconds:true, milliseconds:false, zone:true}) {
        if (!EzObject.isValid(jsDate)) {
            throw new EzBadParamException(
                'jsDate',
                this,
                this.jsDateToIso);
        }

        let jsDateAsIso = EzString.EMPTY;

        let dateDelimiter = EzString.EMPTY;

        if (EzBoolean.booleanOrFalse(options?.year)) {
            jsDateAsIso = `${jsDate.getFullYear()}`;

            dateDelimiter = '-';
        }

        if (EzBoolean.booleanOrFalse(options?.month)) {
            jsDateAsIso = `${jsDateAsIso}${dateDelimiter}${EzNumber.toIsoMonthString(jsDate.getMonth() + 1)}`;

            dateDelimiter = '-';
        }

        if (EzBoolean.booleanOrFalse(options?.date)) {
            jsDateAsIso = `${jsDateAsIso}${dateDelimiter}${EzNumber.toIsoDateString(jsDate.getDate())}`;
        }

        let jsTimeAsIso = EzString.EMPTY;

        let timeDelimiter = 'T';

        if (EzBoolean.booleanOrFalse(options?.hours)) {
            jsTimeAsIso = `${timeDelimiter}${EzNumber.toIsoHourString(jsDate.getHours())}`;

            timeDelimiter = ':';
        }

        if (EzBoolean.booleanOrFalse(options?.minutes)) {
            jsTimeAsIso = `${jsTimeAsIso}${timeDelimiter}${EzNumber.toIsoMinuteString(jsDate.getMinutes())}`;

            timeDelimiter = ':';
        }

        if (EzBoolean.booleanOrFalse(options?.seconds)) {
            jsTimeAsIso = `${jsTimeAsIso}${timeDelimiter}${EzNumber.toIsoSecondString(jsDate.getSeconds())}`;

            timeDelimiter = '.';
        }

        if (EzBoolean.booleanOrFalse(options?.milliseconds)) {
            jsTimeAsIso = `${jsTimeAsIso}${timeDelimiter}${EzNumber.toIsoMillisecondString(jsDate.getMilliseconds())}`;
        }

        let jsTimeZoneAsIso = EzString.EMPTY;

        if (EzBoolean.booleanOrFalse(options?.zone)) {
            const timeZoneOffset = -jsDate.getTimezoneOffset();

            const hourOffset = Math.floor(Math.abs(timeZoneOffset) / 60);

            const minuteOffset = Math.abs(timeZoneOffset) % 60;

            jsTimeZoneAsIso = `${EzNumber.toIsoHoursString(hourOffset)}:${EzNumber.toIsoMinuteString(minuteOffset)}`;

            const timeZoneOffsetPrefix = 0 <= timeZoneOffset
                ? '+'
                : '-';

            jsTimeZoneAsIso = '00:00' === jsTimeZoneAsIso
                ? 'Z'
                : `${timeZoneOffsetPrefix}${jsTimeZoneAsIso}`;
        }

        return `${jsDateAsIso}${jsTimeAsIso}${jsTimeZoneAsIso}`;
    }

    /**
     * Transforms a JS Date to an ISO string with local time zone.
     * @param {Date} jsDate
     * @param {Object} options
     * options = {
     *      year: true,
     *      month: true,
     *      date: true,
     *      hours: true,
     *      minutes: true,
     *      seconds: true,
     *      milliseconds: false,
     *      zone: true
     *  }
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.jsDateToIso(jsDate, options)
     */
    ezJsDateToIso = EzDateTime.jsDateToIso;

    /**
     * @public @method
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the start of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {boolean} returnNullInsteadOfTodayIfNull
     * @param {string} timeZoneId
     * Optionally in the specified timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    ezDateTimeFromDatePickerWithTimeAtStartOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId) || !globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromDatePickerStartOfDay);
        }

        const jsDate = globalThis.ezApi.ezclocker.ezUi.ezId(datePickerId).datepicker('getDate');

        if (null == jsDate) {
            return null;
        }

        // Step 1: Create a moment with time at beginning of day in the users's selected time zone
        return EzDateTime.nowWithStartOfDay(
            EzDateTime.safeTimeZoneId(timeZoneId))
            // Step 2: Set only the year, month, and date from the JSDate (ignoring its timezone)
            .year(jsDate.getFullYear())
            .month(jsDate.getMonth())
            .date(jsDate.getDate());
    }

    /**
     * @public @method
     * Creates a moment with the active time zone from the date value in the date picker with the
     * provided datePickerId.
     * The moment's time value is forced to the end of day.
     * Set returnNullInsteadOfTodayIfNull to true to have the call return null instead of today if the date pickers
     * date is null.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date in the local time zone.
     * EzClocker sets date pickers with the display date only (ignoring the time and zone)
     * When the date is read from the date picker, the time and zone must get ignored to
     * nake sure the date doesn't translate when converting it to the moment in the user's
     * selected time zone.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {string} timeZoneId
     * Optionally return the date in the provided timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    ezDateTimeFromDatePickerWithTimeAtEndOfDay(datePickerId, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId) || !globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDateTimeFromDatePickerWithTimeAtEndOfDay);
        }

        const jsDate = globalThis.ezApi.ezclocker.ezUi.ezId(datePickerId).datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            throw new EzException(
                EzString.em`
                        The date picker (id=${datePickerId}') did not return a valid Javascript date instance from the call to
                        globalThis.ezApi.ezclocker.ezUi.ezId(${datePickerId}).datepicker('getDate')`);
        }

        // Step 1: Create a moment with time at the end of day in the users's selected time zone
        return EzDateTime.nowWithEndOfDay(timeZoneId)
            // Step 2: Set only the year, month, and date from the JSDate (ignoring its timezone)
            .year(jsDate.getFullYear())
            .month(jsDate.getMonth())
            .date(jsDate.getDate());
    }

    /**
     * @public @method
     * Gets the date picker element's date as a string with a language-sensitive representation of the date portion
        of the specified date in the user agent's timezone. In otherwords, returns a locale specific display date (which
        may not be mm/dd/yyyy).
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        ENGINEERING NOTES
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        JQuery UI date pickers store the value as a Javascript Date in the local time zone.
        EzClocker sets date pickers with the display date only (ignoring the time and zone)
        When the date is read from the date picker, the time and zone must get ignored to
        nake sure the date doesn't translate when converting it to the moment in the user's
        selected time zone.
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @returns {string}
     */
    ezGetDatePickerLocaleDisplayDate(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetDatePickerLocaleDisplayDate);
        }

        if (!globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        const jsDate = globalThis.ezApi.ezclocker.ezUi.ezId(datePickerId).datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        return jsDate.toLocaleDateString();
    }

    /**
     * @public @method
     * Gets the date picker element's date as a string in ezClocker's default display date format.
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        ENGINEERING NOTES
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
        JQuery UI date pickers store the value as a Javascript Date in the local time zone.
        EzClocker sets date pickers with the display date only (ignoring the time and zone)
        When the date is read from the date picker, the time and zone must get ignored to
        nake sure the date doesn't translate when converting it to the moment in the user's
        selected time zone.
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @returns {string}
     */
    ezGetDatePickerDisplayDate(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetDatePickerDisplayDate);
        }

        if (!globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        const jsDate = globalThis.ezApi.ezclocker.ezUi.ezId(datePickerId).datepicker('getDate');

        if (!EzObject.isValid(jsDate)) {
            return EzDateTime.toDisplayDate(EzDateTime.nowWithEndOfDay());
        }

        // Step 1: Create a moment with time at the end of day in the users's selected time zone
        const momentInstance = EzDateTime.nowWithEndOfDay();

        // Step 2: Set only the year, month, and date from the JSDate (ignoring its timezone)
        return EzDateTime.toDisplayDate(
            momentInstance
                .year(jsDate.getFullYear())
                .month(jsDate.getMonth())
                .date(jsDate.getDate()));
    }

    /**
     * @static
     * @public @method
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery Date Picker Formatting Values
     *     d - day of month (no leading zero)
     *     dd - day of month (two digit)
     *     o - day of the year (no leading zeros)
     *     oo - day of the year (three digit)
     *     D - day name short
     *     DD - day name long
     *     m - month of year (no leading zero)
     *     mm - month of year (two digit)
     *     M - month name short
     *     MM - month name long
     *     y - year (two digit)
     *     yy - year (four digit)
     *     @ - Unix timestamp (ms since 01/01/1970)
     *     ! - Windows ticks (100ns since 01/01/0001)
     *     '...' - literal text
     *     EzString.EMPTY - single quote
     * MomentJS Date Formatting Values
     *             TOKEN   EXAMPLE
     *     Month
     *             M       1 2 ... 11 12
     *             Mo      1st 2nd ... 11th 12th
     *             MM  01 02 ... 11 12
     *             MMM Jan Feb ... Nov Dec
     *             MMMM    January February ... November December
     *     Quarter
     *             Q       1 2 3 4
     *             Qo      1st 2nd 3rd 4th
     *     Day of Month
     *             D       1 2 ... 30 31
     *             Do      1st 2nd ... 30th 31st
     *             DD      01 02 ... 30 31
     *     Day of Year
     *             DDD     1 2 ... 364 365
     *             DDDo    1st 2nd ... 364th 365th
     *             DDDD    001 002 ... 364 365
     *     Day of Week
     *             d       0 1 ... 5 6
     *             do      0th 1st ... 5th 6th
     *             dd      Su Mo ... Fr Sa
     *             ddd     Sun Mon ... Fri Sat
     *             dddd    Sunday Monday ... Friday Saturday
     *     Day of Week (Locale)
     *             e       0 1 ... 5 6
     *     Day of Week (ISO)
     *             E       1 2 ... 6 7
     *     Week of Year
     *             w       1 2 ... 52 53
     *             wo      1st 2nd ... 52nd 53rd
     *             ww      01 02 ... 52 53
     *     Week of Year (ISO)
     *              W      1 2 ... 52 53
     *             Wo      1st 2nd ... 52nd 53rd
     *             WW      01 02 ... 52 53
     *     Year
     *             YY      70 71 ... 29 30
     *             YYYY    1970 1971 ... 2029 2030
     *             YYYYYY  -001970 -001971 ... +001907 +001971
     *                     Note: Expanded Years (Covering the full time value range of approximately 273,790 years forward or backward from 01 January, 1970)
     *             Y       1970 1971 ... 9999 +10000 +10001
     *                     Note: This complies with the ISO 8601 standard for dates past the year 9999
     *     Era Year
     *             y       1 2 ... 2020 ...
     *     Era
     *             N       BC AD
     *             NN      BC AD
     *             NNN     BC AD
     *                     Note: Abbr era name
     *             NNNN    Before Christ, Anno Domini
     *                     Note: Full era name
     *             NNNNN   BC AD
     *                     Note: Narrow era name
     *     Week Year
     *             gg      70 71 ... 29 30
     *             gggg    1970 1971 ... 2029 2030
     *     Week Year (ISO)
     *             GG      70 71 ... 29 30
     *             GGGG    1970 1971 ... 2029 2030
     *     AM/PM
     *             A       AM PM
     *             a       am pm
     *     Hour
     *             H       0 1 ... 22 23
     *             HH      00 01 ... 22 23
     *             h       1 2 ... 11 12
     *             hh      01 02 ... 11 12
     *             k       1 2 ... 23 24
     *             kk      01 02 ... 23 24
     *     Minute
     *             m       0 1 ... 58 59
     *             mm      00 01 ... 58 59
     *     Second
     *             s       0 1 ... 58 59
     *             ss      00 01 ... 58 59
     *     Fractional Second
     *             S       0 1 ... 8 9
     *             SS      00 01 ... 98 99
     *             SSS     000 001 ... 998 999
     *             SSSS... 000[0..] 001[0..] ... 998[0..] 999[0..]
     *     Time Zone
     *             z       EST CST ... MST PST
     *             zz      EST CST ... MST PST
     *                     Note: as of 1.6.0, the z/zz format tokens have been deprecated from plain moment objects.
     *                     However, they *do* work if you are using a specific time zone with the moment-timezone addon.
     *             Z       -07:00 -06:00 ... +06:00 +07:00
     *             ZZ      -0700 -0600 ... +0600 +0700
     *     Unix Timestamp
     *             X       1360013296
     *     Unix Millisecond Timestamp
     *             x       1360013296123
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
    static datePickerDateFormatToMomentDateFormat(datePickerDateFormat) {
        if (!EzObject.isValid(datePickerDateFormat)) {
            throw new EzBadParamException(
                'datePickerDateFormat',
                EzDateTime,
                EzDateTime.datePickerDateFormatToMomentDateFormat);
        }

        return datePickerDateFormat
            .replace(/DD/, 'dddd')
            .replace(/D/, 'ddd')
            .replace(/dd/, 'DD')
            .replace(/d/, 'D')
            .replace(/MM/, 'MMMM')
            .replace(/M/, 'MMM')
            .replace(/mm/, 'MM')
            .replace(/m/, 'M')
            .replace(/yy/, 'YYYY')
            .replace(/y/, 'YY')
            .replace(/o/, 'DDD')
            .replace(/oo/, 'DDDD')
            .replace(/@/, 'X')
            .replace(/!/, 'x');
    }

    /**
     * @public @method
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery Date Picker Formatting Values
     *     d - day of month (no leading zero)
     *     dd - day of month (two digit)
     *     o - day of the year (no leading zeros)
     *     oo - day of the year (three digit)
     *     D - day name short
     *     DD - day name long
     *     m - month of year (no leading zero)
     *     mm - month of year (two digit)
     *     M - month name short
     *     MM - month name long
     *     y - year (two digit)
     *     yy - year (four digit)
     *     @ - Unix timestamp (ms since 01/01/1970)
     *     ! - Windows ticks (100ns since 01/01/0001)
     *     '...' - literal text
     *     EzString.EMPTY - single quote
     * MomentJS Date Formatting Values
     *             TOKEN   EXAMPLE
     *     Month
     *             M       1 2 ... 11 12
     *             Mo      1st 2nd ... 11th 12th
     *             MM  01 02 ... 11 12
     *             MMM Jan Feb ... Nov Dec
     *             MMMM    January February ... November December
     *     Quarter
     *             Q       1 2 3 4
     *             Qo      1st 2nd 3rd 4th
     *     Day of Month
     *             D       1 2 ... 30 31
     *             Do      1st 2nd ... 30th 31st
     *             DD      01 02 ... 30 31
     *     Day of Year
     *             DDD     1 2 ... 364 365
     *             DDDo    1st 2nd ... 364th 365th
     *             DDDD    001 002 ... 364 365
     *     Day of Week
     *             d       0 1 ... 5 6
     *             do      0th 1st ... 5th 6th
     *             dd      Su Mo ... Fr Sa
     *             ddd     Sun Mon ... Fri Sat
     *             dddd    Sunday Monday ... Friday Saturday
     *     Day of Week (Locale)
     *             e       0 1 ... 5 6
     *     Day of Week (ISO)
     *             E       1 2 ... 6 7
     *     Week of Year
     *             w       1 2 ... 52 53
     *             wo      1st 2nd ... 52nd 53rd
     *             ww      01 02 ... 52 53
     *     Week of Year (ISO)
     *              W      1 2 ... 52 53
     *             Wo      1st 2nd ... 52nd 53rd
     *             WW      01 02 ... 52 53
     *     Year
     *             YY      70 71 ... 29 30
     *             YYYY    1970 1971 ... 2029 2030
     *             YYYYYY  -001970 -001971 ... +001907 +001971
     *                     Note: Expanded Years (Covering the full time value range of approximately 273,790 years forward or backward from 01 January, 1970)
     *             Y       1970 1971 ... 9999 +10000 +10001
     *                     Note: This complies with the ISO 8601 standard for dates past the year 9999
     *     Era Year
     *             y       1 2 ... 2020 ...
     *     Era
     *             N       BC AD
     *             NN      BC AD
     *             NNN     BC AD
     *                     Note: Abbr era name
     *             NNNN    Before Christ, Anno Domini
     *                     Note: Full era name
     *             NNNNN   BC AD
     *                     Note: Narrow era name
     *     Week Year
     *             gg      70 71 ... 29 30
     *             gggg    1970 1971 ... 2029 2030
     *     Week Year (ISO)
     *             GG      70 71 ... 29 30
     *             GGGG    1970 1971 ... 2029 2030
     *     AM/PM
     *             A       AM PM
     *             a       am pm
     *     Hour
     *             H       0 1 ... 22 23
     *             HH      00 01 ... 22 23
     *             h       1 2 ... 11 12
     *             hh      01 02 ... 11 12
     *             k       1 2 ... 23 24
     *             kk      01 02 ... 23 24
     *     Minute
     *             m       0 1 ... 58 59
     *             mm      00 01 ... 58 59
     *     Second
     *             s       0 1 ... 58 59
     *             ss      00 01 ... 58 59
     *     Fractional Second
     *             S       0 1 ... 8 9
     *             SS      00 01 ... 98 99
     *             SSS     000 001 ... 998 999
     *             SSSS... 000[0..] 001[0..] ... 998[0..] 999[0..]
     *     Time Zone
     *             z       EST CST ... MST PST
     *             zz      EST CST ... MST PST
     *                     Note: as of 1.6.0, the z/zz format tokens have been deprecated from plain moment objects.
     *                     However, they *do* work if you are using a specific time zone with the moment-timezone addon.
     *             Z       -07:00 -06:00 ... +06:00 +07:00
     *             ZZ      -0700 -0600 ... +0600 +0700
     *     Unix Timestamp
     *             X       1360013296
     *     Unix Millisecond Timestamp
     *             x       1360013296123
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @deprecated
     * Migrate to static method: EzDateTime.datePickerDateFormatToMomentDateFormat(datePickerDateFormat)
     */
    ezDatePickerDateFormatToMomentDateFormat = EzDateTime.datePickerDateFormatToMomentDateFormat;

    /**
     * @static
     * @public @method
     * Gets a JQuery UI date picker's date format for the provided datePickerId
     * @param {string} datePickerId
     * @returns {string}
     */
    static getDatePickerDateFormat(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.getDatePickerDateFormat);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            EzLogger.error(
                EzString.em`
                    Failed to obtain a date picker input HTML element's date format string for id="${datePickerId}"
                    due to the folowing error: Date picker HTML element with id="${datePickerId}" does not exist.
                    Returning the default date format string of ${EzDateTime.DEFAULT_MM_DD_YYYY} instead.`);
        }

        const datePickerDateFormatStr = datePickerHTMLElement.datepicker(
            'option',
            'dateFormat');

        return EzString.hasText(datePickerDateFormatStr)
            ? datePickerDateFormatStr
            : EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @public @method
     * Gets a JQuery UI date picker's date format for the provided datePickerId
     * @param {string} datePickerId
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.getDatePickerDateFormat(datePickerId)
     */
    ezGetDatePickerDateFormat = EzDateTime.getDatePickerDateFormat;

    /**
     * @static
     * @public @method
     * Gets a Moment date format string for the JQuery UI date picker's date format.
     * If the date picker does not have a date format value, then returns the default moment date format of EzDateTime.ezInstance.DEFAULT_MM_DD_YYYY
     * @param {string} datePickerId
     * @returns {string}
     */
    static getMomentJsDateFormatForDatePicker(datePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.getMomentJsDateFormatForDatePicker);
        }

        const datePickerDateFormat = EzDateTime.getDatePickerDateFormat(datePickerId);

        return EzString.hasLength(datePickerDateFormat)
            ? EzDateTime.datePickerDateFormatToMomentDateFormat(datePickerDateFormat)
            : EzDateTime.DEFAULT_DATE_PICKER_FORMAT;
    }

    /**
     * @static
     * @public @method
     * Gets a Moment date format string for the JQuery UI date picker's date format.
     * If the date picker does not have a date format value, then returns the default moment date format of EzDateTime.ezInstance.DEFAULT_MM_DD_YYYY
     * @param {string} datePickerId
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.getDateFormatForDatePicker(datePickerId)
     */
    ezGetMomentDateFormatForDatePickerDateFormat = EzDateTime.getMomentJsDateFormatForDatePicker;

    /**
     * @public @method
     * Sets a datepicker value from the provided moment.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Unless the provided momentInstance's time is needed, use one of the following instead of this method:
     *  1) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueStartOfDay(...)
     *  2) globalThis.ezApi.ezclocker.ezDateTime.ezSetDatePickerValueEndOfDay(...)
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    ezSetDatePickerValue(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerValue);
        }
        if (!globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerValue,
                `Date picker HTML element with id=${datePickerId} does not currently exist.`);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                EzDateTime.withTimeAtStartOfDay(momentInstance));
        }
    }

    /**
     * @public @method
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    ezSetDatePickerDateWithTimeAtStartOfDay(datePickerId, momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId) || !globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerDateWithTimeAtStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            // Set date picker value to null (clear out date)
            EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            const newDateTimeForDatePicker = EzDateTime.withTimeAtStartOfDay(
                momentInstance,
                timeZoneId);

            const currentPickerMoment = EzDateTime.ezInstance.ezDateTimeFromDatePickerWithTimeAtStartOfDay(
                datePickerId,
                timeZoneId);

            if (EzDateTime.equals(currentPickerMoment, newDateTimeForDatePicker)) {
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            } else {
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            }
        }
    }

    /**
     * @public @method
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    ezSetDatePickerDateWithTimeAtEndOfDay(datePickerId, momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId) || !globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerDateWithTimeAtStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            // Set date picker value to null (clear out date)
            EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            const newDateTimeForDatePicker = EzDateTime.withTimeAtEndOfDay(
                momentInstance,
                timeZoneId);

            const currentPickerMoment = EzDateTime.ezInstance.ezDateTimeFromDatePickerWithTimeAtEndOfDay(
                datePickerId,
                timeZoneId);

            if (EzDateTime.equals(currentPickerMoment, newDateTimeForDatePicker)) {
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            } else {
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                    datePickerId,
                    newDateTimeForDatePicker);
            }
        }
    }

    /**
     * @static
     * @public @method
     * Sets the date picker's input value (but not the date picker's internal date value) equal to the provided
     * momentInstance formatted with the date picker control's dateFormat value. Nothing is set if the date picker's
     * input value is already equal to the formatted display date
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    static setDatePickerInputValueWithMomentDisplayDate(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate);
        }

        let momentAtStartOfDayWithPTZDisplayDate = EzString.EMPTY;

        if (EzDateTime.isValidMoment(momentInstance)) {
            momentAtStartOfDayWithPTZDisplayDate = EzDateTime.withTimeAtStartOfDay(momentInstance)
                .format(
                    EzDateTime.getMomentJsDateFormatForDatePicker(datePickerId));
        }

        // Set the date pickers input text box equal to the same value if it not already the same value
        if (momentAtStartOfDayWithPTZDisplayDate !== EzString.stringOrEmpty(EzUx.getInputValue(datePickerId))) {
            // Setting the input value will trigger the inputs onChange event.
            EzUx.setInputValue(
                datePickerId,
                momentAtStartOfDayWithPTZDisplayDate);
        }
    }

    /**
     * @static
     * @public @method
     * Sets the date picker's input value (but not the date picker's internal date value) equal to the provided
     * momentInstance formatted with the date picker control's dateFormat value. Nothing is set if the date picker's
     * input value is already equal to the formatted display date
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerInputValueWithMomentDisplayDate(datePickerId, momentInstance)
     */
    ezSetDatePickerInputValueWithMomentDisplayDate = EzDateTime.setDatePickerInputValueWithMomentDisplayDate;

    /**
     * @static
     * @public @method
     * Sets the date picker controls date and input value equal to the provided momentInstance formatted
     * with the date picker control's dateFormat value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {undefined|null|moment} momentInstance
     * When undefined or null, the date picker's selected date is cleared
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     */
    static setDatePickerInputAndDateValueWithMomentDisplayDate(datePickerId, momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                this,
                this.setDatePickerInputAndDateValueWithMomentDisplayDate.toString());
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            throw new EzBadStateException(
                `Expected date picker id "${datePickerId}" to reference a valid date picker HTML element`,
                `Date picker HTML element with id="${datePickerId}" does not exist.`,
                this,
                this.setDatePickerInputAndDateValueWithMomentDisplayDate,
                `Failed to set the date picker HTML element for id="${datePickerId}"'s input and date value.`);
        }

        // Set to null instead of empty string so the date date picker's selected date is cleard when the provided momentInstance is null or undefined
        const datePickerDisplayDate = EzDateTime.isValidMoment(momentInstance)
            ? EzDateTime.withZone(momentInstance, timeZoneId)
                .format(
                    EzDateTime.getMomentJsDateFormatForDatePicker(datePickerId))
            : EzString.EMPTY;

        // Set the actual date of the date picker using the datePickerDisplayDate value
        // Calling datepicker('setDate', datePickerDisplayDate) should not trigger a onChange event for the input
        datePickerHTMLElement.datepicker(
            'setDate',
            datePickerDisplayDate);

        // Set the date pickers input text value to the datePickerDisplayDate value (if not already equal that)
        if (EzString.stringOrEmpty(EzUx.getInputValue(datePickerId)) !== datePickerDisplayDate) {
            // Setting the input value will trigger the inputs onChange event.
            EzUx.setInputValue(
                datePickerId,
                datePickerDisplayDate);
        }
    }

    /**
     * @static
     * @public @method
     * Sets the date picker controls date and input value equal to the provided momentInstance formatted
     * with the date picker control's dateFormat value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {undefined|null|moment} momentInstance
     * When undefined or null, the date picker's selected date is cleared
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Optionally set the date to the provided timeZoneId before setting the date picker's date.
     * Default: EzDateTime.ezActiveTimeZoneId
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(datePickerId, momentInstance, timeZoneId)
     */
    ezSetDatePickerInputAndDateValueWithMomentDisplayDate = EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate;

    /**
     * @static
     * @public @method
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * Default: null
     */
    static setDatePickerValueStartOfDay(datePickerId, momentInstance = null) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValueStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                EzDateTime.withTimeAtStartOfDay(momentInstance));
        }
    }

    /**
     * @public @method
     * Sets a datepicker value from the provided moment. Time is set to the beginning of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerValueStartOfDay(datePickerId, momentInstance)
     */
    ezSetDatePickerValueStartOfDay = EzDateTime.setDatePickerValueStartOfDay;

    /**
     * @static
     * @public @method
     * Sets a datepicker value from the provided moment. Time is set to the end of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * Default: null
     */
    static setDatePickerValueEndOfDay(datePickerId, momentInstance = null) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime,
                EzDateTime.setDatePickerValueEndOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                datePickerId,
                null);
        } else {
            const currentPickerMoment = EzDateTime.fromDatePickerEndOfDay(datePickerId);

            if (EzDateTime.equals(momentInstance, currentPickerMoment)) {
                EzDateTime.setDatePickerInputValueWithMomentDisplayDate(
                    datePickerId,
                    EzDateTime.withTimeAtEndOfDay(momentInstance));
            } else {
                EzDateTime.setDatePickerInputAndDateValueWithMomentDisplayDate(
                    datePickerId,
                    EzDateTime.withTimeAtEndOfDay(momentInstance));
            }
        }
    }

    /**
     * @public @method
     * Sets a datepicker value from the provided moment. Time is set to the end of the day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     * Default: null
     * @deprecated
     * Migrate to static method: EzDateTime.setDatePickerValueEndOfDay(datePickerId, momentInstance)
     */
    ezSetDatePickerValueEndOfDay = EzDateTime.setDatePickerValueEndOfDay;

    /**
     * @public @method
     * Sets the date picker's minimum date with time at start of day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    ezSetDatePickerMinDateStartOfDay(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateStartOfDay);
        }

        if (!globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateStartOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateStartOfDay);
        }

        globalThis.ezApi.ezclocker.ezUi.ezId(datePickerId).datepicker(
            'option',
            'minDate',
            EzDateTime.toJsDate(
                EzDateTime.withTimeAtStartOfDay(momentInstance)));
    }

    /**
     * @public @method
     * Sets the date picker's minimum date with time at endof day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    ezSetDatePickerMinDateEndOfDay(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateEndOfDay);
        }
        if (!globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateEndOfDay);
        }

        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetDatePickerMinDateEndOfDay);
        }

        globalThis.ezApi.ezclocker.ezUi.ezId(datePickerId).datepicker(
            'option',
            'minDate',
            EzDateTime.toDisplayDate(
                EzDateTime.withTimeAtEndOfDay(momentInstance)));
    }

    /**
     * @static
     * @public @method
     * Sets the date picker's maximum date with time at end of day.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {moment} momentInstance
     */
    static setDatePickerMaxDateEndOfDay(datePickerId, momentInstance) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                this,
                this.setDatePickerMaxDateEndOfDay);
        }
        if (!globalThis.ezApi.ezclocker.ezUi.ezElementExists(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                this,
                this.setDatePickerMaxDateEndOfDay);
        }
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                this,
                this.setDatePickerMaxDateEndOfDay);
        }

        const datePickerHTMLElement = EzUx.$id(datePickerId);

        if (!EzObject.isValid(datePickerHTMLElement) || EzNumber.ZERO === datePickerHTMLElement?.length) {
            throw new EzBadStateException(
                `Expected HTML element id "${datePickerId}" to reference a valid date picker HTML input element.`,
                `HTML element id "${datePickerId}" doe snot reference a valid HTML element.`,
                this,
                this.setDatePickerMaxDateEndOfDay,
                `Failed to set the date picker HTML input element with id=${datePickerId}'s maxDate property equal to ${EzDateTime.toIso(momentInstance)}.`);
        }

        datePickerHTMLElement.datepicker(
            'option',
            'maxDate',
            EzDateTime.toDisplayDate(
                EzDateTime.withTimeAtEndOfDay(momentInstance)));
    }

    ezSetDatePickerMaxDateEndOfDay = EzDateTime.setDatePickerMaxDateEndOfDay;

    /**
     * @static
     * @public @method
     * Converts the provided momentInstance to a JavaScript Date
     * @param {moment} momentInstance
     * Default: null
     * @returns {Date}
     */
    static toJsDate(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                this,
                this.toJsDate);
        }

        return EzDateTime.momentWithPTZ(momentInstance).toDate();
    }

    /**
     * @public @method
     * Converts the provided momentInstance to a JavaScript Date
     * @param {moment} momentInstance
     * Default: null
     * @returns {Date}
     * @deprecated
     * Migrate to static method: EzDateTime.toJsDate(momentInstance)
     */
    ezToJsDate = EzDateTime.toJsDate;

    /**
     * @public @method
     * Creates a moment instance from a date picker's value and a time picker's value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {string} timePickerId
     * @returns {moment}
     */
    ezDateTimeFromPickers(datePickerId, timePickerId, timeZone) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDateTimeFromPickers);
        }

        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDateTimeFromPickers);
        }

        const datePickerMoment = EzDateTime.ezInstance.ezMomentFromDatePickerStartOfDay(datePickerId, false, timeZone);

        let hour = globalThis.ezApi.ezclocker.ezUi.ezId(timePickerId).timepicker('getHour');

        if (!EzNumber.isNumber(hour)) {
            hour = 0;
        }

        let min = globalThis.ezApi.ezclocker.ezUi.ezId(timePickerId).timepicker('getMinute');

        if (!EzNumber.isNumber(min)) {
            min = 0;
        }

        return datePickerMoment
            .hour(hour)
            .minute(min)
            .second(0)
            .millisecond(0);
    }

    /**
     * @public @method
     * Creates a moment instance from a date picker's value and a time picker's value.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * JQuery UI date pickers store the value as a Javascript Date, in the local time zone.
     * In order to not have the momentInstance converted to local timezone when setting the date picker
     * the 'display date' is used from the momentInstance time.
     * When the display date is set in the date picker, the timezone switches to local time zone.
     * However, the date is the same as it was in momentInstance - which means when reading from the date
     * picker the timezone MUST get ignored and only the month, day, and year value is used.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} datePickerId
     * @param {string} timePickerId
     * @returns {moment}
     */
    ezMomentFromPickers(datePickerId, timePickerId) {
        if (!EzString.hasLength(datePickerId)) {
            throw new EzBadParamException(
                'datePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromPickers);
        }
        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromPickers);
        }

        const datePickerMoment = EzDateTime.ezInstance.ezMomentFromDatePickerStartOfDay(datePickerId);

        let hour = globalThis.ezApi.ezclocker.ezUi.ezId('edit_clockInTime').timepicker('getHour');

        if (!EzNumber.isNumber(hour)) {
            hour = 0;
        }

        let min = globalThis.ezApi.ezclocker.ezUi.ezId('edit_clockInTime').timepicker('getMinute');

        if (!EzNumber.isNumber(min)) {
            min = 0;
        }

        return datePickerMoment
            .hour(hour)
            .minute(min)
            .second(0)
            .millisecond(0);
    }

    /**
     * @public @method
     * Returns the string formatted MM/DD/YYYY for the provided moment.js instance. If none provided,
     * returns the current date instead.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated Migrate to  EzDateTime.toDisplayDate()
     */
    ezDateString(momentInstance) {
        return EzDateTime.toDisplayDate(momentInstance);
    }

    static get NEVER_SET_MOMENT_UTC() {
        return moment()
            .tz(EzTimeZone.UTC)
            .year(0)
            // Janurary (zero based month)
            .month(0)
            .date(1)
            .hour(0)
            .minute(0)
            .second(0)
            .millisecond(0);
    }

    static get NEVER_SET_DATE_ISO() {
        return EzDateTime.ezInstance.ezToIsoDate(EzDateTime.NEVER_SET_MOMENT_UTC);
    }

    static get NEVER_SET_DATE_TIME_ISO() {
        return EzDateTime.toIso(EzDateTime.NEVER_SET_MOMENT_UTC);
    }

    static getNeverSetDateTimeIso(noZone, noMilliseconds, noMillisecondsOrSeconds, noTime) {
        if (EzBoolean.isTrue(noTime)) {
            return EzDateTime.NEVER_SET_DATE_ISO;
        }

        if (EzBoolean.isTrue(noMillisecondsOrSeconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_SET_DATE_ISO}T00:00`
                : `${EzDateTime.NEVER_SET_DATE_ISO}T00:00Z`;
        }

        if (EzBoolean.isTrue(noMilliseconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_SET_DATE_ISO}T00:00:00`
                : `${EzDateTime.NEVER_SET_DATE_ISO}T00:00:00Z`;
        }

        return EzDateTime.NEVER_SET_DATE_TIME_ISO;
    }

    static get NEVER_SET_DISPLAY_DATE() {
        return '-';
    }

    static get NEVER_EXPIRE_MOMENT_UTC() {
        return moment()
            .tz(EzTimeZone.UTC)
            .year(9999)
            // Janurary (zero based month)
            .month(0)
            .date(1)
            .hour(23)
            .minute(59)
            .second(59)
            .millisecond(999);
    }

    static get NEVER_EXPIRE_DATE_ISO() {
        return EzDateTime.ezInstance.ezToIsoDate(EzDateTime.NEVER_EXPIRE_MOMENT_UTC);
    }

    static get NEVER_EXPIRE_DATE_TIME_ISO() {
        return EzDateTime.toIso(EzDateTime.NEVER_EXPIRE_MOMENT_UTC);
    }

    static getNeverExpireDateTimeIso(noZone, noTime, noMillisecondsOrSeconds, noMilliseconds) {
        if (EzBoolean.isTrue(noTime)) {
            return EzDateTime.NEVER_EXPIRE_DATE_ISO;
        }

        if (EzBoolean.isTrue(noMillisecondsOrSeconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00`
                : `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00Z`;
        }

        if (EzBoolean.isTrue(noMilliseconds)) {
            return EzBoolean.isTrue(noZone)
                ? `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00:00`
                : `${EzDateTime.NEVER_EXPIRE_DATE_ISO}T00:00:00Z`;
        }

        return EzDateTime.NEVER_SET_DATE_TIME_ISO;
    }

    static get NEVER_EXPIRE_DISPLAY_DATE() {
        return 'NEVER';
    }

    /**
     * @static
     * @public @method
     * Determines if the provided momentInstance (or possibly string) represents a never expire date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals '-'
     *      starts with '9999/01/01'
     *      starts with '9999.01.01'
     *      starts with '01/01/9999'
     *      starts with '01-01-9999'
     *      starts with '01.01.9999'
     *      equals '9999-01-01T23:59:59:999Z'
     *      equals '9999-01-01T23:59:59:999'
     *      equals '9999-01-01T23:59:59Z'
     *      equals '9999-01-01T23:59:59'
     *      equals '9999-01-01T23:59Z'
     *      equals '9999-01-01T23:59'
     *      equals '9999-01-01'
     * @returns {boolean}
     */
    static isNeverExpire(momentInstanceOrString) {
        if (EzString.isString(momentInstanceOrString)) {
            momentInstanceOrString = momentInstanceOrString.toUpperCase();

            return !EzString.hasLength(momentInstanceOrString) ||
                EzDateTime.NEVER_EXPIRE_DISPLAY_DATE === momentInstanceOrString ||
                EzDateTime.NEVER_EXPIRE_DATE_ISO === momentInstanceOrString ||
                EzDateTime.NEVER_EXPIRE_DATE_TIME_ISO === momentInstanceOrString ||
                '01-01-9999' === momentInstanceOrString ||
                // No zone
                EzDateTime.getNeverExpireDateTimeIso(true, false, false, false) === momentInstanceOrString ||
                // No milliseconds
                EzDateTime.getNeverExpireDateTimeIso(false, false, false, true) === momentInstanceOrString ||
                // No zone, no milliseconds
                EzDateTime.getNeverExpireDateTimeIso(true, false, false, true) === momentInstanceOrString ||
                // No milliseconds, no seconds
                EzDateTime.getNeverExpireDateTimeIso(false, false, true, true) === momentInstanceOrString ||
                // No Zone, no milliseconds, no seconds
                EzDateTime.getNeverExpireDateTimeIso(true, false, true, true) === momentInstanceOrString ||
                momentInstanceOrString.startsWith('9999/01/01') ||
                momentInstanceOrString.startsWith('9999.01.01') ||
                momentInstanceOrString.startsWith('01/01/9999') ||
                momentInstanceOrString.startsWith('01-01-9999') ||
                momentInstanceOrString.startsWith('01.01.9999');
        }

        return EzDateTime.isValidMoment(momentInstanceOrString)
            ? EzDateTime.NEVER_EXPIRE_DATE_ISO === EzDateTime.toIsoDate(
                EzDateTime.toUTC(momentInstanceOrString))
            : false;
    }

    /**
     * @static
     * @public @method
     * Determines if the provided momentInstance (or possibly string) represents a never expire date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals '-'
     *      starts with '9999/01/01'
     *      starts with '9999.01.01'
     *      starts with '01/01/9999'
     *      starts with '01-01-9999'
     *      starts with '01.01.9999'
     *      equals '9999-01-01T23:59:59:999Z'
     *      equals '9999-01-01T23:59:59:999'
     *      equals '9999-01-01T23:59:59Z'
     *      equals '9999-01-01T23:59:59'
     *      equals '9999-01-01T23:59Z'
     *      equals '9999-01-01T23:59'
     *      equals '9999-01-01'
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isNeverExpire(momentInstanceOrString)
     */
    ezIsNeverExpire = EzDateTime.isNeverExpire;

    /**
     * @static
     * @public @method
     * Determines if the provided momentInstance (or possibly string) represents a never set date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals 'NEVER'
     *      starts with '0000/01/01'
     *      starts with '0000.01.01'
     *      starts with '01/01/0000'
     *      starts with '01-01-0000'
     *      starts with '01.01.0000'
     *      equals '0000-01-01T00:00:00:000Z'
     *      equals '0000-01-01T00:00:00:000'
     *      equals '0000-01-01T00:00:00Z'
     *      equals '0000-01-01T00:00:00'
     *      equals '0000-01-01T00:00Z'
     *      equals '0000-01-01T00:00'
     *      equals '0000-01-01'
     * @returns {boolean}
     */
    static isNeverSet(momentInstanceOrString) {
        if (EzString.isString(momentInstanceOrString)) {
            momentInstanceOrString = momentInstanceOrString.toUpperCase();

            return !EzString.hasLength(momentInstanceOrString) ||
                EzDateTime.NEVER_SET_DISPLAY_DATE === momentInstanceOrString ||
                EzDateTime.NEVER_SET_DATE_ISO === momentInstanceOrString ||
                EzDateTime.NEVER_SET_DATE_TIME_ISO === momentInstanceOrString ||
                '01-01-0000' === momentInstanceOrString ||
                // No zone
                EzDateTime.getNeverSetDateTimeIso(true, false, false, false) === momentInstanceOrString ||
                // No milliseconds
                EzDateTime.getNeverSetDateTimeIso(false, false, false, true) === momentInstanceOrString ||
                // No zone, no milliseconds
                EzDateTime.getNeverSetDateTimeIso(true, false, false, true) === momentInstanceOrString ||
                // No milliseconds, no seconds
                EzDateTime.getNeverSetDateTimeIso(false, false, true, true) === momentInstanceOrString ||
                // No Zone, no milliseconds, no seconds
                EzDateTime.getNeverSetDateTimeIso(true, false, true, true) === momentInstanceOrString ||
                momentInstanceOrString.startsWith('0000/01/01') ||
                momentInstanceOrString.startsWith('0000.01.01') ||
                momentInstanceOrString.startsWith('01/01/0000') ||
                momentInstanceOrString.startsWith('01-01-0000') ||
                momentInstanceOrString.startsWith('01.01.0000');
        } else if (EzDateTime.isValidMoment(momentInstanceOrString)) {
            const isoDate = EzDateTime.toIsoDate(
                EzDateTime.toUTC(momentInstanceOrString));

            return EzDateTime.NEVER_SET_DATE_ISO === isoDate || '0000-01-01' === isoDate;
        }

        return false;
    }

    /**
     * @public @method
     * Determines if the provided momentInstance (or possibly string) represents a never set date.
     * @param {undefined|null|moment|string} momentInstanceOrString
     * Supported string values:
     *      equals 'NEVER'
     *      starts with '0000/01/01'
     *      starts with '0000.01.01'
     *      starts with '01/01/0000'
     *      starts with '01-01-0000'
     *      starts with '01.01.0000'
     *      equals '0000-01-01T00:00:00:000Z'
     *      equals '0000-01-01T00:00:00:000'
     *      equals '0000-01-01T00:00:00Z'
     *      equals '0000-01-01T00:00:00'
     *      equals '0000-01-01T00:00Z'
     *      equals '0000-01-01T00:00'
     *      equals '0000-01-01'
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isNeverSet(momentInstanceOrString)
     */
    ezIsNeverSet = EzDateTime.isNeverSet;

    /**
     * @public @method
     * Transforms the provided isoDateTime to a displayable date
     * @param {undefined|null|string} isoDateTime
     * @returns {string}
     */
    ezIsoToDisplayDate(isoDateTime) {
        if (!EzString.hasLength(isoDateTime)) {
            return EzString.EMPTY;
        }

        if (EzDateTime.isNeverExpire(isoDateTime)) {
            return EzDateTime.NEVER_EXPIRE_DISPLAY_DATE;
        }

        if (EzDateTime.isNeverSet(isoDateTime)) {
            return EzDateTime.NEVER_SET_DISPLAY_DATE;
        }

        try {
            const momentInstance = EzDateTime.fromIso(isoDateTime);

            return EzDateTime.toDisplayDate(momentInstance);
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Unable to transform iso date time '${isoDateTime} to a display date
                    due to the following error: ${err.message}`);

            return isoDateTime;
        }
    }

    /**
     * @public @method
     * Transforms the provided isoDateTime to a displayable string in format: MM/YYYY
     * @param {undefined|null|string} isoDateTime
     * @returns {string}
     */
    ezIsoToDisplayMonthYear(isoDateTime) {
        if (!EzString.hasLength(isoDateTime)) {
            return EzString.EMPTY;
        }

        try {
            const momentInstance = EzDateTime.fromIso(isoDateTime);

            if (EzDateTime.isNeverExpire(momentInstance)) {
                return EzDateTime.NEVER_EXPIRE_DISPLAY_DATE;
            }

            if (EzDateTime.isNeverSet(momentInstance)) {
                return EzDateTime.NEVER_SET_DISPLAY_DATE;
            }

            return EzDateTime.ezInstance.ezToDisplayMonthYear(momentInstance);
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Unable to transform iso date time '${isoDateTime} to a display month and year format
                    due to the following error: ${err.message}`);

            return isoDateTime;
        }
    }

    /**
     * @public @method
     * Transforms the provided momentInstance to a display string in formation: MM/YYYY
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezToDisplayMonthYear(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayMonthYear);
        }

        if (EzDateTime.isNeverExpire(momentInstance)) {
            return EzDateTime.NEVER_EXPIRE_DATE_TIME_ISO;
        }

        if (EzDateTime.isNeverSet(momentInstance)) {
            return EzDateTime.NEVER_SET_DISPLAY_DATE;
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.DEFAULT_MONTH_YEAR_DISPLAY_FORMAT);
    }

    /**
     * @static
     * @public @method
     * Returns the string formatted MM/DD/YYYY for the provided moment.js instance. If none provided,
     * returns the current date instead.
     * @param {moment} momentInstance
     * Default: null
     * @returns {string}
     */
    static toDisplayDate(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                this,
                this.toDisplayDate);
        }

        if (EzDateTime.isNeverExpire(momentInstance)) {
            return EzDateTime.NEVER_EXPIRE_DATE_TIME_ISO;
        }

        if (EzDateTime.isNeverSet(momentInstance)) {
            return EzDateTime.NEVER_SET_DISPLAY_DATE;
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.ezPreferredDateFormat);
    }

    /**
     * @public @method
     * Returns the string formatted MM/DD/YYYY for the provided moment.js instance. If none provided,
     * returns the current date instead.
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to static method: EzDateTime.toDisplayDate(momentInstance)
     */
    ezToDisplayDate = EzDateTime.toDisplayDate;



    /**
     * @public @method
     * Get the name of the day
     * @returns {string}
     */
    ezToDayNameDisplay(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDayNameDisplay);
        }

        momentInstance = EzDateTime.momentWithPTZ(momentInstance);

        return momentInstance.format('dddd');
    }

    /**
     * @public @method
     * Returns the day name plus date string for display using the preferred moment day name and ate format value.
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezToDisplayDayNameAndDate(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDayNameAndDate);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.ezPreferredMomentDayNameAndDateFormat);
    }

    /**
     * @public @method
     * Returns a date range in format: "{sDate} - {eDate}"
     * @param {moment} sMoment
     * @param {moment} eMoment
     * @returns {string}
     */
    ezToDisplayDateRange(sMoment, eMoment) {
        if (!EzDateTime.isValidMoment(sMoment)) {
            throw new EzBadParamException(
                'sMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateRange);
        }

        if (!EzDateTime.isValidMoment(eMoment)) {
            throw new EzBadParamException(
                'eMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateRange);
        }

        sMoment = EzDateTime.momentWithPTZ(sMoment);

        eMoment = EzDateTime.momentWithPTZ(eMoment);

        return `${EzDateTime.momentWithPTZ(sMoment).format(EzDateTime.ezPreferredDateFormat)}` +
            ' - ' +
            `${EzDateTime.momentWithPTZ(eMoment).format(EzDateTime.ezPreferredDateFormat)}`;
    }

    /**
     * @public @method
     * Returns the string hh:mm a format of the provided moment. If the moment is not valid, then the current time is used.
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezTimeString(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezTimeString);
        }

        momentInstance = EzDateTime.momentWithPTZ(momentInstance);

        return momentInstance.format('hh:mm a');
    }

    /**
     * @public @method
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string}
     */
    ezToDisplayDateTime(momentInstance = null, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateTime);
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time due to the following error:
                    ${err.message}`);

            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateTime);
        }
    }

    /**
     * @public @method
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string}
     */
    ezToDisplayTimeStampDateTime(momentInstance = null, optionalDateTimeFormat = EzDateTime.DEFAULT_MOMENT_TIMESTAMP_DISPLAY_FORMAT) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time due to the following error:
                    ${err.message}`);

            return EzString.EMPTY;
        }
    }

    /**
     * @public @method
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {any} defaultValue
     * Default: EzString.EMPTY
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeFormat
     * @returns {string|any}
     */
    ezToDisplayDateTimeOrDefault(momentInstance = null, defaultValue = EzString.EMPTY, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return defaultValue;
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time due to the following error:
                    ${err.message}`);

            return defaultValue;
        }
    }

    /**
     * @public @method
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     * @returns {string}
     */
    ezToDisplayDateTimeWithSeconds(momentInstance = null, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeWithSecondsFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateTimeWithSeconds);
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeWithSecondsFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time with seconds format due to the following error:
                    ${err.message}`);

            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDisplayDateTimeWithSeconds);
        }
    }

    /**
     * @public @method
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {any} defaultValue
     * Default: EzString.EMPTY
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     * @returns {string|any}
     */
    ezToDisplayDateTimeWithSecondsOrDefault(momentInstance = null, defaultValue = EzString.EMPTY,
        optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeWithSecondsFormat) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return defaultValue;
        }

        try {
            return EzDateTime.momentWithPTZ(momentInstance)
                .format(
                    EzString.stringWithLengthOrDefault(
                        optionalDateTimeFormat,
                        EzDateTime.ezPreferredDateTimeWithSecondsFormat));
        } catch (err) {
            globalThis.ezApi.ezclocker.ezLogger.error(
                EzString.em`
                    Failed to transform the provided momentInstance value to a display date time with seconds format due to the following error:
                    ${err.message}`);

            return defaultValue;
        }
    }

    /**
     * @public @method
     * Returns the provided moment to the configured preferred date time display format.
     * Template: "MM/DD/YYYY[ at ]hh:mm:ss a"
     * Templated provided from field: globalThis.ezApi.ezclocker.ezDateTime.ezPreferredDateTimeFormat
     * Field default built from constants:
     *  EzDateTime.DEFAULT_MOMENT_DATE_TIME_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_DATE_FORMAT
     *  EzDateTime.DEFAULT_MOMENT_HH_MM_SS_TIME_FORMAT
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|string} optionalDateTimeFormat
     * Default: EzDateTime.ezPreferredDateTimeWithSecondsFormat
     * @returns {string}
     */
    ezToDisplayDateTimeWithSecondsOrEmpty(momentInstance = null, optionalDateTimeFormat = EzDateTime.ezPreferredDateTimeWithSecondsFormat) {
        return EzDateTime.ezInstance.ezToDisplayDateTimeWithSecondsOrDefault(
            momentInstance,
            EzString.EMPTY,
            optionalDateTimeFormat);
    }

    /**
     * @public @method
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDateTime.ezToIso()
     */
    ezToIsoDateTime(momentInstance) {
        return EzDateTime.toIso(momentInstance);
    }

    /**
     * @static
     * @public @method
     * Converts the provided moment to a ISO date string in the format of YYYY-MM-DD
     * Template: "YYYY-MM-DD"
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {moment} momentInstance
     * Default: null
     * @returns {string}
     */
    static toIsoDate(momentInstance = null) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        momentInstance = EzDateTime.momentWithPTZ(momentInstance);

        return momentInstance.format('YYYY-MM-DD');
    }

    ezToIsoDate = EzDateTime.toIsoDate;

    /**
     * @static
     * @public @method
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    static toIso(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIsoOrDefault(
            momentInstance,
            timeZoneId,
            EzString.EMPTY);
    }

    /**
     * @public @method
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * @deprecated
     * Migrate to static method: EzDateTime.toIso(momentInstnace, timeZoneId)
     */
    ezToIso = EzDateTime.toIso;

    /**
     * @static
     * @public @method
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} defaultIso
     * Default: EzDateTime.nowAsIso()
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    static toIsoOrDefault(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId, defaultValue = EzDateTime.nowAsIso()) {
        try {
            return !EzDateTime.isValidMoment(momentInstance)
                ? EzDateTime.isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(
                    EzString.stringWithLengthOrDefault(
                        defaultValue,
                        EzDateTime.nowAsIso(timeZoneId)))
                : EzDateTime.isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(
                    momentInstance.tz(EzDateTime.safeTimeZoneId(timeZoneId))
                        .format(EzDateTime.DEFAULT_ISO_DATE_TIME_FORMAT));
        } catch (err) {
            EzLogger.error(
                EzString.em`
                    Unable to convert the provided momentInstance to an ISO string due to the following error:
                    ${EzString.stringOrDefault(err?.message, 'Unnamed excception without message')}`,
                err);

            return EzDateTime.isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(
                EzString.stringWithLengthOrDefault(
                    defaultValue,
                    EzDateTime.nowAsIso(timeZoneId)));
        }
    }

    /**
     * @static
     * @public @method
     * Transforms the provided isoDateTimeWithUTCTimeZone based upon the following logic:
     * 1) If the provied isoDateTimeWithUTCTimeZone has text:
     * ====> 1a) If the provided isoDateTimeWithUTCTimeZone includes text '+00:00' then transform the isoDateTimeWithUTCTimeZone's '+00:00' text to 'Z' and return
     * the updated isoDateTimeWithUTCTimeZone value.
     * ====> 1b) If the provided isoDateTimeWithUTCTimeZone does not includes text '+00:00' then return isoDateTimeWithUTCTimeZone value as provided.
     * 2) If the provied isoDateTimeWithUTCTimeZone is undefined, null, or is only whitespace then eturn isoDateTimeWithUTCTimeZone value as provided.
     */
    static isoDateTimeWithUTCOffsetToIsoDateTimeWithZ(isoDateTimeWithUTCTimeZone = null) {
        return EzString.hasText(isoDateTimeWithUTCTimeZone) && isoDateTimeWithUTCTimeZone.includes('+00:00')
            ? isoDateTimeWithUTCTimeZone.replace('+00:00', 'Z')
            : isoDateTimeWithUTCTimeZone;
    }

    /**
     * @public @method
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} defaultIso
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * @deprecated
     * Migrate to static method: EzDateTime.toIsoOrDefault(momentInstance, timeZoneId, defaultValue)
     */
    ezToIsoOrDefault = EzDateTime.toIsoOrDefault;

    /**
     * @public @method
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    ezToIsoMaintainZone(momentInstance) {
        return EzDateTime.ezInstance.ezToIsoMaintainZoneOrDefault(
            momentInstance,
            EzString.EMPTY);
    }

    /**
     * @public @method
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {undefined|null|moment} momentInstance
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} defaultIso
     * Default: globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()
     * @returns {string}
     * Returns the provided moment in ISO format: "YYYY-MM-DDTHH:mm:ss.sssZ"
     */
    ezToIsoMaintainZoneOrDefault(momentInstance, defaultValue = globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso()) {
        let result;

        if (!EzDateTime.isValidMoment(momentInstance)) {
            result = EzString.stringWithLengthOrDefault(
                defaultValue,
                globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso());
        } else {
            try {
                result = momentInstance.format(EzDateTime.DEFAULT_ISO_DATE_TIME_FORMAT);
            } catch (err) {
                globalThis.ezApi.ezclocker.ezLogger.error(
                    `Unable to convert the provided momentInstance to an ISO string due to the following error: ${err.message}`,
                    err);

                result = EzString.stringWithLengthOrDefault(
                    defaultValue,
                    globalThis.ezApi.ezclocker.ezDateTime.ezNowAsIso());
            }
        }

        if (result.includes('+00:00')) {
            result = result.replace('+00:00', 'Z');
        }

        return result;
    }

    /**
     * @public @method
     * Generates a default time iso from globalThis.ezApi.ezclocker.ezDateTime.ezNow()
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: False
     * @param {boolean} noSeconds
     * Default: True
     * @param {boolean} noMilliseconds
     * Default: True
     * @returns {string}
     */
    ezGenerateDefaultTimeIso(timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true) {
        const nowDateTime = globalThis.ezApi.ezclocker.ezDateTime.ezNow();

        let defaultTimeIso;

        if (EzBoolean.booleanOrTrue(noSeconds)) {
            defaultTimeIso = EzBoolean.booleanOrFalse(noZone)
                ? nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mm')
                : nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mmZ');
        } else if (EzBoolean.booleanOrTrue(noMilliseconds)) {
            defaultTimeIso = EzBoolean.booleanOrFalse(noZone)
                ? nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mm:ss')
                : nowDateTime.tz(EzDateTime.safeTimeZoneId(timeZoneId)).format('HH:mm:ssZ');
        }

        return defaultTimeIso;
    }

    /**
     * @public @method
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     */
    ezToTimeIso(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true) {
        return EzDateTime.ezInstance.ezToTimeIsoOrDefault(
            momentInstance,
            timeZoneId,
            noZone,
            noSeconds,
            noMilliseconds,
            EzDateTime.ezInstance.ezGenerateDefaultTimeIso(
                timeZoneId,
                noZone,
                noSeconds,
                noMilliseconds));
    }

    /**
     * @public @method
     * Returns the ISO string in the selected timezone, for the provided moment.
     * Template: "YYYY-MM-DDTHH:mm:ss.sssZ"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @param {boolean} noZone
     * Default: false
     * @param {boolean} noSeconds
     * Default: true
     * @param {boolean} noMilliseconds
     * Default: true
     * @param {undefined|null|string} defaultTimeIso
     * Default: Now time formatted as: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     * @returns {string}
     * Returns the provided moment in ISO format: "HH:mm:ss.sssZ" or "HH:mm:ssZ" or "HH:mmZ" or "HH:mm:ss.sss" or "HH:mm:ss" or "HH:mm"
     */
    ezToTimeIsoOrDefault(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId, noZone = false, noSeconds = true, noMilliseconds = true,
        defaultTimeIso = EzString.EMPTY) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.hasLength(defaultTimeIso)
                ? defaultTimeIso
                : EzDateTime.ezInstance.ezGenerateDefaultTimeIso(
                    timeZoneId,
                    noZone,
                    noSeconds,
                    noMilliseconds);
        }

        if (EzBoolean.booleanOrTrue(noSeconds)) {
            return EzBoolean.booleanOrFalse(noZone)
                ? momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mm')
                : momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mmZ');
        } else if (EzBoolean.booleanOrTrue(noMilliseconds)) {
            return EzBoolean.booleanOrFalse(noZone)
                ? momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mm:ss')
                : momentInstance.tz(
                    EzDateTime.safeTimeZoneId(timeZoneId))
                    .format('HH:mm:ssZ');
        }

        return EzBoolean.booleanOrFalse(noZone)
            ? momentInstance.tz(
                EzDateTime.safeTimeZoneId(timeZoneId))
                .format('HH:mm:ss.SSS')
            : momentInstance.tz(
                EzDateTime.safeTimeZoneId(timeZoneId))
                .format('HH:mm:ss.SSSZ');
    }

    /**
     * @public @method
     * Returns the moment as a 'UNIX' MS timestamp.
     * Template: "x"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @returns {tsring}
     */
    ezToTimestamp(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return EzDateTime.momentWithPTZ(momentInstance).format('x');
    }

    /**
     * @public @method
     * Returns the provided date as a version string in the formation: 'YYYYMMDDHHmmssSSS'
     * See https://momentjs.com/docs/#/displaying/ for formatting string details.
     * @param {moment} momentInstance
     * @returns {String}
     */
    ezToVersionStamp(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return EzDateTime.momentWithPTZ(momentInstance).format('YYYYMMDDHHmmssSSS');
    }

    /**
     * @public @readonly @property
     * Returns the current date and time as a version string in the formation: 'YYYYMMDDHHmmssSSS'
     * See https://momentjs.com/docs/#/displaying/ for formatting string details.
     * @returns {String}
     */
    get ezNowVersionStamp() {
        return EzDateTime.now().format('YYYYMMDDHHmmssSSS');
    }

    /**
     * @public @method
     * Returns the moment.now() as a 'UNIX' MS timestamp.
     * Template: "x"
     * Documentation for moment.js formatting: https://momentjs.com/docs/#/displaying/
     * @param {moment} momentInstance
     * @returns {tsring}
     */
    ezNowAsTimestamp() {
        return EzDateTime.now().format('x');

    }

    /**
     * @public @method
     * Returns the moment as a ISO String in UTC time zone
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDateTime.ezToIsoUTC(momentInstance)
     */
    ezToUTCIsoDateTime(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return EzDateTime.ezInstance.ezToIsoUTC(momentInstance);
    }

    /**
     * @public @method
     * Returns the moment as a ISO String in UTC time zone
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezToIsoUTC(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            return EzString.EMPTY;
        }

        return momentInstance.tz(EzTimeZone.UTC).format();
    }


    /**
     * @public @method
     * Returns a moment instance in the preferred timezone for the date, with time at the beginning of the date.
     * @param {number} aMonth
     * From 0 to 11
     * @param {number} aDay
     * From 1 to 31
     * @param {number} aYear
     * @returns {moment}
     */
    ezFromDate(aMonth, aDay, aYear) {
        if (!EzNumber.isNumber(aMonth)) {
            throw new EzBadParamException(
                'aMonth',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDate);
        }
        if (!EzNumber.isNumber(aDay)) {
            throw new EzBadParamException(
                'aDay',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDate);
        }
        if (!EzNumber.isNumber(aYear)) {
            throw new EzBadParamException(
                'aYear',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDate);
        }

        return EzDateTime.now()
            .set('year', aYear)
            .set('date', aDay)
            .set('month', aMonth)
            .set('hour', 0)
            .set('minute', 0)
            .set('second', 0)
            .set('millisecond', 0);
    }

    /**
     * @public @method
     * Returns the moment from the provided date string in format mm/dd/yyyy
     * @param {string} dateString
     * @returns {moment}
     */
    ezFromDateString(dateString) {
        if (!EzString.hasLength(dateString)) {
            throw new EzBadParamException(
                'dateString',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDateString);
        }

        const dateParts = dateString.split('/');

        // month is zero based
        return EzDateTime.ezInstance.ezFromDate(
            EzNumber.strToInt(dateParts[0]) - 1,
            EzNumber.strToInt(dateParts[1]),
            EzNumber.strToInt(dateParts[2]));
    }

    ezMomentFromDateStringAllFormats(dateString, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzString.hasLength(dateString)) {
            throw new EzBadParamException(
                'dateString',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezFromDateString);
        }

        dateString = EzDateTime.patchBadDecodedIso(dateString);

        return moment(dateString).tz(timeZoneId);
    }

    /**
     * @public @method
     * Returns the moment from the provided date string.
     * String is expected to be in the format of the  EzDateTime.ezInstance.ezPreferredDateFormat result
     * @param {string} preferredDateString
     * @returns {moment}
     */
    ezMomentFromPreferredDateString(preferredDateString) {
        if (!EzString.hasLength(preferredDateString)) {
            throw new EzBadParamException(
                'preferredDateString',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromPreferredDateString);
        }

        const momentInstance = moment(
            preferredDateString,
            EzDateTime.ezPreferredDateFormat,
            true);

        if (!momentInstance.isValid()) {
            throw new EzException(
                EzString.em`
                    The provided preferredDateString value of "${preferredDateString}" did not translate to a momentjs instance.
                    Assumed formatting of ${EzDateTime.ezPreferredDateFormat}`);
        }

        return EzDateTime.momentWithPTZ(momentInstance);
    }

    /**
     * @public @method
     * @param {string} aValue
     * @returns {moment}
     */
    ezCreateFromValue(aValue) {
        if (!EzString.hasLength(aValue)) {
            throw new EzBadParamException(
                'aValue',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromValue);
        }

        const momentInstance = !moment(aValue);

        return momentInstance.isValid()
            ? EzDateTime.momentWithPTZ(momentInstance)
            : EzDateTime.nowWithStartOfDay();
    }

    /**
     * @public @method
     * @param {string} dateTimeString
     * @param {string} expectedFormat
     * @returns {moment}
     */
    ezCreateFromValueInFormat(dateTimeString, expectedFormat) {
        if (!EzString.hasLength(dateTimeString) || !EzString.hasLength(expectedFormat)) {
            return null;
        }

        const momentInstance = moment(dateTimeString, expectedFormat);

        return momentInstance.isValid()
            ? EzDateTime.momentWithPTZ(momentInstance)
            : EzDateTime.nowWithStartOfDay();
    }

    /**
     * @static
     * @public @method
     * Returns the current date and time as an ISO string
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     */
    static nowAsIso(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIso(EzDateTime.now(timeZoneId));
    }

    /**
     * @public @method
     * Returns the current date and time as an ISO string
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     */
    ezNowAsIso = EzDateTime.nowAsIso;

    /**
     * @static
     * @public @method
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static now(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return moment().tz(EzDateTime.safeTimeZoneId(timeZoneId));
    }

    /**
     * @public @method
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.now(timeZoneId)
     */
    ezNow = EzDateTime.now;

    /**
     * @static
     * @public @method
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     */
    static nowWithStartOfDay(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.now(timeZoneId));
    }

    /**
     * @public @method
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithStartOfDay(timeZoneId)
     */
    ezNowWithStartOfDay = EzDateTime.nowWithStartOfDay;

    /**
     * @static
     * @public @method
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     */
    static nowWithStartOfDayIso(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIso(
            EzDateTime.nowWithStartOfDay(timeZoneId));
    }

    /**
     * @public @method
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithStartOfDayIso(timeZoneId)
     */
    ezNowWithStartOfDayIso = EzDateTime.nowWithStartOfDayIso;

    /**
     * @static
     * @public @method
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 23:59:59.999.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     */
    static nowWithEndOfDay(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.now(timeZoneId).endOf('day');
    }

    /**
     * @public @method
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 23:59:59.999.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithEndOfDay(timeZoneId)
     */
    ezNowWithEndOfDay = EzDateTime.nowWithEndOfDay;

    /**
     * @static
     * @public @method
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     */
    static nowWithEndOfDayIso(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.toIso(
            EzDateTime.nowWithEndOfDay(timeZoneId));
    }

    /**
     * @public @method
     * Returns a moment instance in the provided timeZoneId
     * (or globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone timeZoneId is not provided)
     * for the current date with the time at: 00:00:00.000.
     * @param {undefined|null|string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.nowWithStartOfDayIso(timeZoneId)
     */
    ezNowWithEndOfDayIso = EzDateTime.nowWithEndOfDayIso;

    /**
     * @public @method
     * Returns a moment with the date set to the start of the month for the
     * provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezMomentAtStartOfMonth(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                momentInstance,
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezStartOfMonthForMoment);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .startOf('month');
    }

    /**
     * @public @method
     * Returns a moment with the date set to the start of the month for the
     * provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezMomentAtEndOfMonth(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                momentInstance,
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezStartOfMonthForMoment);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .endOf('month');
    }

    /**
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new temp moment instance from the provided momentInstance in the globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone.
     *  2) Creates a new moment instance from the temp instance with:
     *      a) The year from the provided momentInstance
     *      b) The month set as 1 (Janurary)
     *      c) The date in the month as 1
     *      d) The time at the beginning of the day (00:00:00.000)
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezSetMomentFirstDayOfYear(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetMomentFirstDayOfYear);
        }

        return EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId).date(1).day(1);
    }

    /**
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new temp moment instance from the provided momentInstance in the globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone.
     *  2) Creates a new moment instance from the temp instance with:
     *      a) The year from the provided momentInstance
     *      b) The month set as 1 (Janurary)
     *      c) The date in the month as 1
     *      d) The time at the end of the day (23:59:59.999)
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezSetMomentLastDayOfYear(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetMomentLastDayOfYear);
        }

        return EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId).date(12).day(31);
    }

    /**
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeWithTimeAtStartOfDay(momentInstance, timeZoneId)
     */
    ezSetMomentStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetMomentStartOfDay);
        }

        return EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId);
    }

    /**
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Defualt: null
     * @param {undefiend|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId)
     */
    ezSetMomentEndOfDay = EzDateTime.withTimeAtEndOfDay;

    /**
     * @static
     * @public @method
     * Determines if the provided moment is a valid moment object.
     * @param {undefined|null|*|moment} momentInstance
     * Default: null
     * @returns {boolean}
     */
    static isValidMoment(momentInstance = null) {
        return EzObject.isValid(momentInstance) && momentInstance?._isAMomentObject && momentInstance?._isValid;
    }

    /**
     * @public @method
     * Determines if the provided moment is a valid moment object.
     * @param {undefined|null|*|moment} momentInstance
     * Default: null
     * @returns {boolean}
     * @deprecated
     * Migrate to the static method: EzDateTime.isValidMoment(...)
     */
    ezIsValidMoment(momentInstance = null) {
        return EzDateTime.isValidMoment(momentInstance);
    }

    /**
     * @public @method
     * Returns the provided momentInstance if it is a valid moment instance.
     * If the provided momentInstance is not a valid moment instance, then returns
     * a new moment instance with the current date and time.
     * @returns {moment}
     * @deprecated
     * Migrate to:
     *  globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeOrNow(momentInstance)
     */
    ezAssignMomentOrNow(momentInstance) {
        return EzDateTime.ezInstance.ezDateTimeOrNow(momentInstance);
    }

    /**
     * @public @method
     * Returns the provided momentInstance if it is a valid moment instance.
     * If the provided momentInstance is not a valid moment instance, then returns
     * a new moment instance with the current date and time.
     * @returns {moment}
     */
    ezDateTimeOrNow(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.isValidMoment(momentInstance)
            ? EzDateTime.withZone(
                momentInstance,
                timeZoneId)
            : globalThis.ezApi.ezclocker.ezDateTime.ezNow(timeZoneId);
    }

    /**
     * @public @method
     * If the provided momentInstance is a vallid moment then the momentInstance is return with
     * it's time set to the start of the day.
     * If the provided momentInstance is not a valid moment, then now is returned
     * with it's time set to the start of the day.
     * @param {undefined|null|moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to:
     *  globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeOrNowWithTimeAtStartOfDay(momentInstance);
     */
    ezAssignMomentOrNowThenSetTimeAtStartOfDay(momentInstance) {
        return globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeOrNowWithTimeAtStartOfDay(momentInstance);
    }

    /**
     * @public @method
     * If the provided momentInstance is a vallid moment then the momentInstance is return with
     * it's time set to the start of the day.
     * If the provided momentInstance is not a valid moment, then now is returned
     * with it's time set to the start of the day.
     * @param {undefined|null|moment} momentInstance
     * @returns {moment}
     */
    ezDateTimeOrNowWithTimeAtStartOfDay(momentInstance) {
        return EzDateTime.isValidMoment(momentInstance)
            ? globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeWithTimeAtStartOfDay(momentInstance)
            : globalThis.ezApi.ezclocker.ezDateTime.ezNowWithStartOfDay();
    }

    /**
     * @static
     * @public @method
     * Returns a time period object with:
     *  1) The start date as today's date with time at the beginning of the day in the active time zone.
     *  2) The end date as today's date plus 13 days with the time at the end of the day in the active time zone.
     * @returns {object}
     */
    static createDefaultSelectedPeriod() {
        const nowEzDateTimeWithTimeAtStartOfDayActive = EzDateTime.nowWithStartOfDay();

        return EzDateTime.createSelectedPeriod(
            nowEzDateTimeWithTimeAtStartOfDayActive,
            EzDateTime.withTimeAtEndOfDay(
                nowEzDateTimeWithTimeAtStartOfDayActive.add(
                    EzNumber.THIRTEEN,
                    'days')));
    }

    /**
     * @public @method
     * Returns a time period object with:
     *  1) The start date as today's date with time at the beginning of the day in the active time zone.
     *  2) The end date as today's date plus 13 days with the time at the end of the day in the active time zone.
     * @returns {object}
     * @deprecated
     * Migreate to static method: EzDateTime.createDefaultSelectedPeriod()
     */
    ezCreateDefaultSelectedPeriod = EzDateTime.createDefaultSelectedPeriod;

    /**
     * @static
     * @public @method
     * Creates the default selected period object, sets the values as ISO strings, and converts to JSON
     * @returns {string}
     */
    static createDefaultSelectedPeriodJson() {
        const selectedPeriod = EzDateTime.createDefaultSelectedPeriod();

        selectedPeriod.ezPeriodStartMoment = EzDateTime.toIso(selectedPeriod.ezPeriodStartMoment);

        selectedPeriod.ezPeriodEndMoment = EzDateTime.toIso(selectedPeriod.ezPeriodEndMoment);

        return globalThis.ezApi.ezToJson(selectedPeriod);
    }

    /**
     *  @public @function
     * Creates the default selected period object, sets the values as ISO strings, and converts to JSON
     * @returns {string}
     * @deprecated
     * Migreate to static method:  EzDateTime.createDefaultSelectedPeriodJso()
     */
    ezCreateDefaultSelectedPeriodJson = EzDateTime.createDefaultSelectedPeriodJson;

    /**
     * Transforms the provided momentInYear to the last date time of the year the
     * provided momentInYear is for.
     * @param {moment} momentInYear
     * @returns {moment}
     */
    ezToEndOfYear(momentInYear) {
        if (!EzDateTime.isValidMoment(momentInYear)) {
            throw new EzBadParamException(
                'momentInYear',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToEndOfYear);
        }

        return EzDateTime.momentWithPTZ(momentInYear)
            .endOf('year');
    }

    /**
     * @public @method
     * Returns the provied momentInstance as a display string formatted
     * for the date input controls.
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezToDatePickerDisplayDate(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezToDatePickerDisplayDate);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .format(EzDateTime.DEFAULT_MM_DD_YYYY_FORMAT);
    }

    /**
     * @static
     * @public @method
     * Creates the default employee options
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     * @returns {object}
     * Returns time period object:
     *  {
     *      ezStartEzDateTimeActive: {moment},
     *      ezStartEzDateTimeUTC: {moment}
     *      ezStartIsoActive: {string},
     *      ezStartIsoUTC: {string},
     *      ezEndEzDateTimeActive: {moment},
     *      ezEndEzDateTimeUTC: {moment},
     *      ezEndIsoActive: {string},
     *      ezEndIsoUTC: {string},
     *      In addition, contains  additional Legacy properties: Please migrate to using of the properties above
     *      ezPeriodStartMoment: {moment},
     *      periodStartMoment: {moment},
     *      periodStartIso: {string},
     *      ezPeriodEndMoment: {moment},
     *      periodEndMoment: {moment},
     *      periodEndIso: {string}
     *  };
     */
    static createSelectedPeriod(periodStartMoment, periodEndMoment) {
        if (!EzDateTime.isValidMoment(periodStartMoment)) {
            throw new EzBadParamException(
                'periodStartMoment',
                EzDateTime,
                EzDateTime.createSelectedPeriod);
        }
        if (!EzDateTime.isValidMoment(periodEndMoment)) {
            throw new EzBadParamException(
                'periodEndMoment',
                EzDateTime,
                EzDateTime.createSelectedPeriod);
        }

        const startMomentActive = EzDateTime.withTimeAtStartOfDay(periodStartMoment);

        const endMomentActive = EzDateTime.withTimeAtEndOfDay(periodEndMoment);

        const startMomentUTC = EzDateTime.withTimeAtStartOfDay(
            periodStartMoment,
            EzTimeZone.UTC);

        const endMomentUTC = EzDateTime.withTimeAtEndOfDay(
            periodEndMoment,
            EzTimeZone.UTC);

        const startIsoActive = EzDateTime.toIso(startMomentActive);

        const endIsoActive = EzDateTime.toIso(endMomentActive);

        const startIsoUTC = EzDateTime.toIso(startMomentUTC);

        const endIsoUTC = EzDateTime.toIso(endMomentUTC);

        // TODO: Create an EzTimePeriod JS class
        return {
            ezPeriodStartIso: startIsoActive,
            ezPeriodEndIso: endIsoActive,

            ezStartIsoActive: startIsoActive,
            ezEndIsoActive: endIsoActive,

            ezStartIsoUTC: startIsoUTC,
            ezEndIsoUTC: endIsoUTC,

            ezPeriodStartMoment: startMomentActive,
            ezPeriodEndMoment: endMomentActive,

            ezPeriodStartMomentUTC: startMomentUTC,
            ezPeriodEndMomentUTC: endMomentUTC,

            // Legacy properties - migrate to one of the above
            periodStartIso: startIsoActive,
            periodEndIso: endIsoActive,

            periodStartMoment: startMomentActive,
            periodEndMoment: endMomentActive,

            ezStartEzDateTimeActive: startMomentActive,
            ezEndEzDateTimeActive: endMomentActive,

            ezStartEzDateTimeUTC: startMomentUTC,
            ezEndEzDateTimeUTC: endMomentUTC
        };
    }

    /**
     * @public @function
     * Creates the default employee options
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     * @returns {object}
     * Returns time period object:
     *  {
     *      ezStartEzDateTimeActive: {moment},
     *      ezStartEzDateTimeUTC: {moment}
     *      ezStartIsoActive: {string},
     *      ezStartIsoUTC: {string},
     *      ezEndEzDateTimeActive: {moment},
     *      ezEndEzDateTimeUTC: {moment},
     *      ezEndIsoActive: {string},
     *      ezEndIsoUTC: {string},
     *      In addition, contains  additional Legacy properties: Please migrate to using of the properties above
     *      ezPeriodStartMoment: {moment},
     *      periodStartMoment: {moment},
     *      periodStartIso: {string},
     *      ezPeriodEndMoment: {moment},
     *      periodEndMoment: {moment},
     *      periodEndIso: {string}
     *  };
     * @deprecated
     * Migrate to static method: EzDateTime.createSelectedPeriod()
     */
    ezCreateSelectedPeriod = EzDateTime.createSelectedPeriod;

    /**
     * @public @method
     * Creates a selected period JSON Object from the provided periodStartMoment and periodEndMoment
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     * @returns {object}
     */
    ezCreateSelectedPeriodJSON(periodStartMoment, periodEndMoment) {
        if (!EzDateTime.isValidMoment(periodStartMoment)) {
            throw new EzBadParamException(
                'periodStartMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJSON);
        }
        if (!EzDateTime.isValidMoment(periodEndMoment)) {
            throw new EzBadParamException(
                'periodEndMoment',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJSON);
        }

        const startMomentPTZ = EzDateTime.momentWithPTZ(periodStartMoment);

        const startMomentUTC = globalThis.ezApi.ezclocker.ezDateTime.ezDateTimeWithTimeZone(
            periodStartMoment,
            EzTimeZone.UTC);

        const startIsoPTZ = EzDateTime.toIso(startMomentPTZ);

        const startIsoUTC = EzDateTime.toIso(startMomentUTC);

        const endMomentPTZ = EzDateTime.momentWithPTZ(periodEndMoment);

        const endMomentUTC = EzDateTime.momentWithPTZ(periodEndMoment);

        const endIsoPTC = EzDateTime.toIso(endMomentPTZ);

        const endIsoUTC = EzDateTime.toIso(endMomentUTC);

        // TODO: Create an EzTimePeriod JS class
        return {
            //ezStartEzDateTimeActive: startMomentActive,
            //ezStartEzDateTimeUTC: startMomentUTC,
            ezStartIsoActive: startIsoPTZ,
            ezStartIsoUTC: startIsoUTC,
            //ezEndEzDateTimeActive: endEzDateTimeActive,
            //ezEndEzDateTimeUTC: endEzDateTimeUTC,
            ezEndIsoActive: endIsoPTC,
            ezEndIsoUTC: endIsoUTC,
            // Legacy properties - migrate to one of the above
            //ezPeriodStartMoment: startMomentActive,
            //periodStartMoment: startMomentActive,
            //ezPeriodEndMoment: endEzDateTimeActive,
            //periodEndMoment: endEzDateTimeActive,
            periodStartIso: startIsoPTZ,
            periodEndIso: endIsoPTC
        };
    }

    /**
     * @public @method
     * Creates a selected period object from the provided moments, sets the values as ISO strings, converts to JSON
     * @param {moment} periodStartMoment
     * @param {moment} periodEndMoment
     * @returns {string}
     */
    ezCreateSelectedPeriodJson(periodStartMoment, periodEndMoment) {
        if (!EzDateTime.isValidMoment(periodStartMoment)) {
            throw new EzBadParamException(
                periodStartMoment,
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJson);
        }
        if (!EzDateTime.isValidMoment(periodEndMoment)) {
            throw new EzBadParamException(
                periodEndMoment,
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateSelectedPeriodJson);
        }

        return EzJson.toJson(
            EzDateTime.createSelectedPeriod(periodStartMoment, periodEndMoment));
    }

    /**
     * @static
     * @public @method
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * Documentation: https://momentjs.com/docs/#/manipulating/
     * Calls momentjs api method: moment.add(amountToAdd, fieldName)
     * @param {moment} momentInstance
     * @param {string} fieldName
     * Supported field names:
     *  |----------------|-----------|
     *  | Field Name     | Shorthand |
     *  |----------------|-----------|
     *  | years          | y         |
     *  | quarters       | Q         |
     *  | months         | M         |
     *  | weeks          | w         |
     *  | days           | d         |
     *  | hours          | h         |
     *  | minutes        | m         |
     *  | seconds        | s         |
     *  | milliseconds   | ms        |
     *  |----------------|-----------|
     * @param {number} amountToAdd
     * @returns {moment}
     */
    static add(momentInstance, fieldName, amountToAdd) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime,
                EzDateTime.add);
        }
        if (!EzString.hasText(fieldName)) {
            throw new EzBadParamException(
                'fieldName',
                EzDateTime,
                EzDateTime.add);
        }
        if (!EzNumber.isNumber(amountToAdd)) {
            throw new EzBadParamException(
                'amountToAdd',
                EzDateTime,
                EzDateTime.add);
        }

        momentInstance = EzDateTime.momentWithPTZ(momentInstance);

        return momentInstance.add(amountToAdd, fieldName);
    }

    /**
     * @static
     * @public @method
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.add(momentInstance, fieldName, amountToAdd)
     */
    ezPlus = EzDateTime.add;

    /**
     * @public @method
     * Sets the momentInstance instance to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     */
    ezAdd(momentInstance, aValueKey, aAmountToAdd) {
        return EzDateTime.add(momentInstance, aValueKey, aAmountToAdd);
    }


    /**
     * @public @method
     * Sets the momentInstance instance to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     */
    ezPlusUTC(momentInstance, aValueKey, aAmountToAdd) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezAddUTC);
        }

        if (!EzString.hasLength(aValueKey)) {
            throw new EzBadParamException(
                'aValueKey',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezAddUTC);
        }

        if (!EzNumber.isNumber(aAmountToAdd)) {
            throw new EzBadParamException(
                'aAmountToAdd',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezAddUTC);
        }

        return EzDateTime.ezInstance.ezToUTC(momentInstance).add(aValueKey, aAmountToAdd);
    }

    /**
     * @public @method
     * Sets the momentInstance instance to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     */
    ezAddUTC(momentInstance, aValueKey, aAmountToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(momentInstance, aValueKey, aAmountToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {yearsToAdd} number of years.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'years', yearsToAdd)
     * @param {moment} momentInstance
     * @param {number} yearsToAdd
     * @returns {moment}
     */
    ezPlusYears(momentInstance, yearsToAdd) {
        return EzDateTime.add(momentInstance, 'years', yearsToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {yearsToAdd} number of years.
     * Calls:  EzDateTime.add(momentInstance, 'years', yearsToAdd);
     * @param {moment} momentInstance
     * @param {number} yearsToAdd
     * @returns {moment}
     */
    ezAddYears(momentInstance, yearsToAdd) {
        return EzDateTime.add(momentInstance, 'years', yearsToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {monthsToAdd} number of months.
     * Calls:  EzDateTime.add(momentInstance, 'months', monthsToAdd);
     * @param {moment} momentInstance
     * @param {number} monthsToAdd
     * @returns {moment}
     */
    ezPlusMonths(momentInstance, monthsToAdd) {
        return EzDateTime.add(momentInstance, 'months', monthsToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {monthsToAdd} number of months.
     * Calls:  EzDateTime.add(momentInstance, 'months', monthsToAdd);
     * @param {moment} momentInstance
     * @param {number} monthsToAdd
     * @returns {moment}
     */
    ezAddMonths(momentInstance, monthsToAdd) {
        return EzDateTime.add(momentInstance, 'months', monthsToAdd);
    }

    /**
     * @static
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'days', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     */
    static addDays(momentInstance, daysToAdd) {
        return EzDateTime.add(momentInstance, 'days', daysToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'days', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addDays(momentInstance, daysToAdd)
     */
    ezPlusDays = EzDateTime.addDays;

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'days', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addDays(momentInstance, daysToAdd)
     */
    ezAddDays = EzDateTime.addDays;

    /**
     * @static
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'hours', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} hoursToAdd
     * @returns {moment}
     */
    static addHours(momentInstance, hoursToAdd) {
        return EzDateTime.add(momentInstance, 'hours', hoursToAdd);
    }

    /**
     * @static
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'hours', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} hoursToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addHours(momentInstance, hoursToAdd)
     */
    ezPlusHours = EzDateTime.addHours;

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'hours', daysToAdd)
     * @param {moment} momentInstance
     * @param {number} hoursToAdd
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.addHours(momentInstance, hoursToAdd)
     */
    ezAddHours = EzDateTime.addHours;

    /**
     * @static
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     */
    static addMinutes(momentInstance, minutesToAdd) {
        return EzDateTime.add(momentInstance, 'minutes', minutesToAdd);
    }

    /**
     * @static
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     * @deprecated
     * Migrate to static method: EzDateTime.addMinutes(momentInstance, minutesToAdd)
     */
    ezPlusMinutes = EzDateTime.addMinutes;

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAdd(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     * @deprecated
     * Migrate to static method: EzDateTime.addMinutes(momentInstance, minutesToAdd)
     */
    ezAddMinutes = EzDateTime.addMinutes;

    /**
     * @public @method
     * Converts the provided momentInstance to UTC time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'days', daysToAdd);
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     */
    ezPlusDaysUTC(momentInstance, daysToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(momentInstance, 'days', daysToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to UTC time zone then adds the provided {daysToAdd} number of days.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'days', daysToAdd);
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     */
    ezAddDaysUTC(momentInstance, daysToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(momentInstance, 'days', daysToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to UTC time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'hours', hoursToAdd);
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     */
    ezPlusHoursUTC(momentInstance, hoursToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(momentInstance, 'hours', hoursToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to UTC time zone then adds the provided {hoursToAdd} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'hours', hoursToAdd);
     * @param {moment} momentInstance
     * @param {number} daysToAdd
     * @returns {moment}
     */
    ezAddHoursUTC(momentInstance, hoursToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(momentInstance, 'hours', hoursToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone
     * then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     */
    ezPlusMinutesUTC(momentInstance, minutesToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(momentInstance, 'minutes', minutesToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone
     * then adds the provided {minutesToAdd} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToAdd
     */
    ezAddMinutesUTC(momentInstance, minutesToAdd) {
        return EzDateTime.ezInstance.ezPlusUTC(momentInstance, 'minutes', minutesToAdd);
    }

    /**
     * @public @method
     * Sets the momentInstance instnace to the preferred time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     *     Calling:  EzDateTime.momentWithPTZ({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToSubtract
     * @returns {moment}
     */
    ezMinus(momentInstance, aValueKey, aAmountToSubtract) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinus);
        }

        if (!EzString.hasLength(aValueKey)) {
            throw new EzBadParamException(
                'aValueKey',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinus);
        }

        if (!EzNumber.isNumber(aAmountToSubtract)) {
            throw new EzBadParamException(
                'aAmountToSubtract',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinus);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .subtract(aAmountToSubtract, aValueKey);
    }

    /**
     * @public @method
     * Sets the momentInstance instnace to the UTC time zone then executes the moment.add() function using the provided
     * aValueKey and aAmountToAdd as the params.
     * The aValueKey can be either the full name or short hand value:
     *     years|y
     *     quarters|Q
     *     months|M
     *     weeks|w
     *     days|d
     *     hours|h
     *     minutes|m
     *     seconds|s
     *     milliseconds|ms
     * Calls:  EzDateTime.ezInstance.ezToUTC({momentInstance}).add({aAmountToAdd}, {aValueKey});
     * @param {moment} momentInstance
     * @param {string} aValueKey
     * @param {number} aAmountToAdd
     * @returns {moment}
     */
    ezMinusUTC(momentInstance, aValueKey, aAmountToAdd) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusUTC);
        }

        if (!EzString.hasLength(aValueKey)) {
            throw new EzBadParamException(
                'aValueKey',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusUTC);
        }

        if (!EzNumber.isNumber(aAmountToAdd)) {
            throw new EzBadParamException(
                'aAmountToAdd',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusUTC);
        }

        return EzDateTime.ezInstance.ezToUTC(momentInstance).subtract(aValueKey, aAmountToAdd);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided yearsToSubtract years.
     * Calls:  EzDateTime.ezInstance.ezMinus(momentInstance, 'years', yearsToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} yearsToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     */
    ezMinusYears(momentInstance, yearsToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusYears);
        }
        if (!EzNumber.isNumber(yearsToSubtract)) {
            if (EzString.isString(yearsToSubtract)) {
                yearsToSubtract = EzNumber.strToIntDefault(yearsToSubtract, 0);

                if (EzNumber.isNaN(yearsToSubtract)) {
                    throw new EzBadParamException(
                        'yearsToSubtract',
                        EzDateTime.ezInstance,
                        EzDateTime.ezInstance.ezMinusYears);
                }
            } else {
                throw new EzBadParamException(
                    'yearsToSubtract',
                    EzDateTime.ezInstance,
                    EzDateTime.ezInstance.ezMinusYears);
            }
        }

        return EzDateTime.ezInstance.ezMinus(
            momentInstance,
            'years',
            yearsToSubtract);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided monthsToSubtract months.
     * Calls:  EzDateTime.ezInstance.ezMinus(momentInstance, 'months', monthsToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} monthsToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     */
    ezMinusMonths(momentInstance, monthsToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusMonths);
        }
        if (!EzNumber.isNumber(monthsToSubtract)) {
            if (EzString.isString(monthsToSubtract)) {
                monthsToSubtract = EzNumber.strToIntDefault(monthsToSubtract, 0);

                if (EzNumber.isNaN(monthsToSubtract)) {
                    throw new EzBadParamException(
                        'monthsToSubtract',
                        EzDateTime.ezInstance,
                        EzDateTime.ezInstance.ezMinusMonths);
                }
            } else {
                throw new EzBadParamException(
                    'monthsToSubtract',
                    EzDateTime.ezInstance,
                    EzDateTime.ezInstance.ezMinusMonths);
            }
        }

        return EzDateTime.ezInstance.ezMinus(
            momentInstance,
            'months',
            monthsToSubtract);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided weeksToSubtract weeks
     * Calls:  EzDateTime.ezInstance.ezMinus(momentInstance, 'weeks', weeksToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} weeksToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     */
    ezMinusWeeks(momentInstance, weeksToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusWeeks);
        }
        if (!EzNumber.isNumber(weeksToSubtract)) {
            if (EzString.isString(weeksToSubtract)) {
                weeksToSubtract = EzNumber.strToIntDefault(weeksToSubtract, 0);

                if (EzNumber.isNaN(weeksToSubtract)) {
                    throw new EzBadParamException(
                        'weeksToSubtract',
                        EzDateTime.ezInstance,
                        EzDateTime.ezInstance.ezMinusWeeks);
                }
            } else {
                throw new EzBadParamException(
                    'weeksToSubtract',
                    EzDateTime.ezInstance,
                    EzDateTime.ezInstance.ezMinusWeeks);
            }
        }

        return EzDateTime.ezInstance.ezMinus(
            momentInstance,
            'weeks',
            weeksToSubtract);
    }


    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided {daysToSubtract} number of days.
     * Calls:  EzDateTime.ezInstance.ezMinus(momentInstance, 'days', daysToSubtract)
     * @param {moment} momentInstance
     * @param {string|number} daysToSubtract
     * If a string, must be a valid number 'as a string'
     * @returns {moment}
     */
    ezMinusDays(momentInstance, daysToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusDays);
        }
        if (!EzNumber.isNumber(daysToSubtract)) {
            if (EzString.isString(daysToSubtract)) {
                daysToSubtract = EzNumber.strToIntDefault(daysToSubtract, 0);

                if (EzNumber.isNaN(daysToSubtract)) {
                    throw new EzBadParamException(
                        'daysToSubtract',
                        EzDateTime.ezInstance,
                        EzDateTime.ezInstance.ezMinusDays);
                }
            } else {
                throw new EzBadParamException(
                    'daysToSubtract',
                    EzDateTime.ezInstance,
                    EzDateTime.ezInstance.ezMinusDays);
            }
        }

        return EzDateTime.ezInstance.ezMinus(
            momentInstance,
            'days',
            daysToSubtract);
    }

    /**
     * @public @method
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided hoursToSubtract number of hours.
     * Calls:  EzDateTime.ezInstance.ezMinus(momentInstance, 'hours', hoursToSubtract)
     * @param {moment} momentInstance
     * @param {number} hoursToSubtract
     * @returns {moment}
     */
    ezMinusHours(momentInstance, hoursToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusHours);
        }

        return EzDateTime.ezInstance.ezMinus(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToSubtract));
    }

    /**
     *@public @method
     * Converts the provided momentInstance to the preferred time zone then subtracts the provided minutesToSubtract number of minutes.
     * Calls:  EzDateTime.ezInstance.ezMinus(momentInstance, 'minutes', minutesToSubtract);
     * @param {moment} momentInstance
     * @param {number} minutesToSubtract
     * @returns {moment}
     */
    ezMinusMinutes(momentInstance, minutesToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusMinutes);
        }

        return EzDateTime.ezInstance.ezMinus(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToSubtract));
    }

    /**
     * @public @method
     * Converts the provided momentInstance to UTC time zone then subtracts the provided daysToSubtract number of days.
     * Calls:  EzDateTime.ezInstance.ezMinusUTC(momentInstance, 'days', daysToSubtract);
     * @param {moment} momentInstance
     * @param {number} daysToSubtract
     * @returns {moment}
     */
    ezMinusDaysUTC(momentInstance, daysToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusDaysUTC);
        }

        return EzDateTime.ezInstance.ezMinusUTC(
            momentInstance,
            'days',
            EzNumber.numberOrZero(daysToSubtract));
    }

    /**
     * @public @method
     * Converts the provided momentInstance to UTC time zone then subtracts the provided {hoursToSubtract} number of hours.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'hours', hoursToAdd);
     * @param {moment} momentInstance
     * @param {number} hoursToSubtract
     * @returns {moment}
     */
    ezMinusHoursUTC(momentInstance, hoursToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusHoursUTC);
        }

        return EzDateTime.ezInstance.ezMinusUTC(
            momentInstance,
            'hours',
            EzNumber.numberOrZero(hoursToSubtract));
    }

    /**
     *@public @method
     * Converts the provided momentInstance to the preferred time zone then adds the provided {minutesToSubtract} number of minutes.
     * Calls:  EzDateTime.ezInstance.ezAddUTC(momentInstance, 'minutes', minutesToAdd);
     * @param {moment} momentInstance
     * @param {number} minutesToSubtract
     */
    ezMinusMinutesUTC(momentInstance, minutesToSubtract = 0) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinusMinutesUTC);
        }

        return EzDateTime.ezInstance.ezMinusUTC(
            momentInstance,
            'minutes',
            EzNumber.numberOrZero(minutesToSubtract));
    }

    /**
     * @public @method
     * Creates a moment from now with the UTC timezone.
     * @returns {moment}
     */
    ezNowUTC() {
        return moment().utc();
    }

    /**
     * @static
     * @public @method
     * Converts the moment to a UTC moment.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    static toUTC(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                this,
                this.toUTC);
        }

        return moment.utc(momentInstance);
    }

    /**
     * @public @method
     * Converts the moment to a UTC moment.
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.toUtc(momentInstance)
     */
    ezToUtc = EzDateTime.toUTC;

    /**
     * @public @method
     * Returns the moment created from the provided jsDate, transformed to the preferred timezone
     * @param {Date} aJsDate
     * @returns {moment}
     */
    ezCreateFromJsDate(aJsDate) {
        if (!EzObject.isValid(aJsDate)) {
            throw new EzBadParamException(
                'aJsDate',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromJsDate);
        }

        // toISOString() is for the normal JS Date object ONLY
        return EzDateTime.momentWithPTZ(moment(aJsDate.toISOString()));
    }

    /**
     * @public @method
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezCreateFromMoment(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromMoment);
        }

        return EzDateTime.momentWithPTZ(momentInstance);
    }

    /**
     * @public @method
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * Sets the time at the start of the day.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezCreateFromMomentWithTimeStartOfDay(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay);
        }

        return EzDateTime.withTimeAtStartOfDay(
            EzDateTime.momentWithPTZ(momentInstance));
    }

    /**
     * @public @method
     * Creates a new moment in the preferred time zone from the provided momentInstance.
     * Sets the time at the end of the day.
     * @param {moment} momentInstance
     * @returns {moment}
     */
    ezCreateFromMomentWithTimeEndOfDay(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCreateFromMomentWithTimeEndOfDay);
        }

        return EzDateTime.withTimeAtEndOfDay(
            EzDateTime.momentWithPTZ(momentInstance));
    }

    /**
     * @public @method
     * Determines if the provided isoDateTimeString was valid or not.
     * @param {string} proposedDateTimeIso
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidIso(proposedDateTimeIso)
     */
    ezIsValidIsoDateTime(proposedDateTimeIso) {
        return EzDateTime.isValidIso(proposedDateTimeIso);
    }

    /**
     * @public @method
     * Determines if the provided isoDateTimeString was valid or not.
     * @param {string} proposedDateTimeIso
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.isValidIso(proposedDateTimeIso)
     */
    ezIsValidIsoString(proposedDateTimeIso) {
        return EzDateTime.isValidIso(proposedDateTimeIso);
    }

    /**
     * @public @method
     * Converts the provided moment to an ISO string
     * @param {moment} momentInstance
     * @returns {string}
     * @deprecated
     * Migrate to EzDateTime.toIso()
     */
    ezMomentToIsoString(momentInstance) {
        return EzDateTime.toIso(momentInstance);
    }

    /**
     * @public @method
     * Transforms the provided moment to one with the preferred timezone
     * @param {moment} momentInstance
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Defaults to: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     */
    static momentWithPTZ(momentInstance = null/*, ezLocale */) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime,
                EzDateTime.momentWithPTZ);
        }

        /* TODO: Finish locale work
        if (!EzString.hasLength(ezLocale)) {
            ezLocale = EzDateTime.ezInstance.ezPreferredLocale;
        }
        */

        return moment(momentInstance).tz(EzDateTime.ezActiveTimeZoneId);

        // TODO: Remove below line once verified above line works fine :)
        /* Original implementation:
        return EzDateTime.fromIso(momentInstance.format());
        */
    }

    /**
     * @public @method
     * Transforms the provided moment to one with the preferred timezone
     * @param {moment} momentInstance
     * @param {undefined|null|string} ezLocale
     * NOT USED YET
     * A valid enum property value from EzLocale
     * Defaults to: EzDateTime.ezInstance.ezPreferredLocale
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.momentWithPTZ(momentInstance, ezLocale)
     */
    ezMomentWithPTZ = EzDateTime.momentWithPTZ;

    /**
     * @public @method
     * Creates a copy of the provided moment that has the Primary Time Zone. If the provided momentInstance is null or not
     * a valid moment instance null is returned.
     * @param {moment} momentInstance
     */
    ezMomentFromMoment(momentInstance) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMomentFromMoment);
        }

        return EzDateTime.momentWithPTZ(momentInstance);
    }

    /**
     * @public @method
     * Returns a new moment instance created from the provided momentInstance then sets the time zone to the
     * provdied timeZoneId.
     * Uses globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone if the provided timeZoneId is not a valid time zone.
     * @param {moment} momentInstance
     * @param {string} timeZoneId
     * Default: globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone
     * @returns {moment}
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDateTime.ezMomentWithZone(momentInstance, timeZoneId)
     */
    ezDateTimeWithTimeZone(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.withZone(momentInstance, timeZoneId);
    }

    /**
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * @returns {moment}
     * @deprecated
     * Migrate to: EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId)
     */
    ezDateTimeWithTimeAtStartOfDay = EzDateTime.withTimeAtStartOfDay;

    /**
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId)
     */
    ezDateTimeWithTimeAtEndOfDay = EzDateTime.withTimeAtEndOfDay;

    /**
     * @static
     * @public @method
     * Creates a new moment with the provided timeZoneId
     * @param {moment} moment
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @return {moment}
     */
    static withZone(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime,
                EzDateTime.ezMomentWithZone);
        }

        return moment(momentInstance).tz(EzDateTime.safeTimeZoneId(timeZoneId));
    }


    /**
     * @public @method
     * Creates a new moment with the provided timeZoneId
     * @param {moment} moment
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @return {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withZone(moment, timeZoneId)
     */
    ezMomentWithZone = EzDateTime.withZone;

    /**
     * @static
     * @public @method
     * Returns the provided momentInstance in the provided timeZoneId with its time set to the provided hours, minutes, seconds, milliseconds.
     * Important: The time is set after the time-zone is set.
     * @param {undefined|null|moment} momentInstance
     * Default: null
     * @param {undefined|null|number} hours
     * Default: 0
     * @param {undefined|null|number} minutes
     * Default: 0
     * @param {undefined|null|number} seconds
     * Default: 0
     * @param {undefined|null|number} milliseconds
     * Default: 0
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.#ezActiveTimeZoneId
     */
    static withTime(momentInstance = null, hours = 0, minutes = 0, seconds = 0, milliseconds = 0, timeZoneId = EzDateTime.#ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime,
                EzDateTime.withTime);
        }

        return EzDateTime.withZone(
            momentInstance,
            EzDateTime.safeTimeZoneId(timeZoneId))
            .hours(hours)
            .minutes(minutes)
            .seconds(seconds)
            .milliseconds(milliseconds);
    }

    /**
     * @static
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     */
    static withTimeAtStartOfDay(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime,
                EzDateTime.withTimeAtStartOfDay);
        }

        return EzDateTime.withTime(
            momentInstance,
            // Hours
            EzNumber.ZERO,
            // Minutes
            EzNumber.ZERO,
            // Seconds
            EzNumber.ZERO,
            // Milliseconds
            EzNumber.ZERO,
            timeZoneId);
    }

    /**
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 0
     *      b) Sets the new moment instance's minutes to 0
     *      c) Sets the new moment instance's seconds to 0
     *      d) Sets the new moment instance's milliseconds to 0
     * @param {moment} momentInstance
     * Default: null
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtStartOfDay(momentInstance, timeZoneId)
     */
    ezMomentWithZoneAtStartOfDay = EzDateTime.withTimeAtStartOfDay;

    /**
     * @static
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Defualt: null
     * @param {undefiend|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneI
     * @returns {moment}
     */
    static withTimeAtEndOfDay(momentInstance = null, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime,
                EzDateTime.withTimeAtEndOfDay);
        }

        return EzDateTime.withTime(
            momentInstance,
            // Hours
            EzNumber.TWENTY_THREE,
            // Minutes
            EzNumber.FIFTY_NINE,
            // Seconds
            EzNumber.FIFTY_NINE,
            // Milliseconds
            EzNumber.NINE_HUNDRED_NINETY_NINE,
            timeZoneId);
    }

    /**
     * @public @method
     * Returns a new moment instance in the following way:
     *  1) Creates a new moment instance from the provided momentInstance in the provided timeZoneId
     *      a) Sets the new moment instance's hour to 23
     *      b) Sets the new moment instance's minutes to 59
     *      c) Sets the new moment instance's seconds to 59
     *      d) Sets the new moment instance's milliseconds to 999
     * @param {moment} momentInstance
     * Defualt: null
     * @param {undefiend|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneI
     * @returns {moment}
     * @deprecated
     * Migrate to static method: EzDateTime.withTimeAtEndOfDay(momentInstance, timeZoneId)
     */
    ezMomentWithZoneAtEndOfDay = EzDateTime.withTimeAtEndOfDay;

    /**
     * @public @method
     * Validates the provided timeZoneId and
     * 1) If valid, returns the provided timeZoneId
     * 2) Returns the EzDateTime.ezActiveTimeZoneId
     * @param {string} timeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezDateTime.ezUserTimeZoneId(timeZoneId);
     */
    ezUseTimezoneIdOrActiveTimezoneId(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.safeTimeZoneId(timeZoneId);
    }

    /**
     * @static
     * @public @method
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then returns the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id, then returns EzDateTime.ezActiveTimeZoneId
     * @param {undefined|null|string} timeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     */
    static safeTimeZoneId(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.timeZoneIdOrDefault(
            timeZoneId,
            EzDateTime.ezActiveTimeZoneId);
    }

    /**
     * @public @method
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then returns the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id, then returns EzDateTime.ezActiveTimeZoneId
     * @param {string} timeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     * @deprecated
     * Migrate to static method: EzDateTime.safeTimeZoneId(timeZoneId)
     */
    ezUseTimeZoneId = EzDateTime.safeTimeZoneId;

    /**
     * @public @method
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then return the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id:
     * ====> 2a) If the defaultTimeZoneId is provided to the method, then return the provided defaultTimeZoneId
     * ====>2b) The defaultTimeZoneId is NOT provided to the method, then return the provided defaultTimeZoneId
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @param {string} defaultTimeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     */
    static timeZoneIdOrDefault(timeZoneId = null, defaultTimeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (EzDateTime.DEFAULT_ACTIVE_TIMEZONE === timeZoneId.toUpperCase()) {
            return EzDateTime.ezGuessLocalTimeZone();
        }

        return EzDateTime.isValidTimeZone(timeZoneId)
            ? timeZoneId
            : defaultTimeZoneId;
    }

    /**
     * @public @method
     * Returns a valid date time zone id based upon the following logic:
     * 1) If the provided timeZoneId is a valid date time zone id, then return the provided timeZoneId
     * 2) If the provided timeZoneId is NOT a valid date time zone id:
     * ====> 2a) If the defaultTimeZoneId is provided to the method, then return the provided defaultTimeZoneId
     * ====>2b) The defaultTimeZoneId is NOT provided to the method, then return the provided defaultTimeZoneId
     * @param {undefined|null|string} timeZoneId
     * Default: null
     * @param {string} defaultTimeZoneId
     * Default: EzDateTime.ezActiveTimeZoneId
     * @returns {string}
     * If validated returns the provided timeZoneId, otherwise, returns EzDateTime.ezActiveTimeZoneId.
     * @deprecated
     * Migrate to static method: EzDateTime.timeZoneIdOrDefault(timeZoneId, defaultTimeZoneId)'
     */
    ezUseTimeZoneIdOrDefault = EzDateTime.timeZoneIdOrDefault;

    /**
     * @static
     * @public @property @getter
     * Gets the default active time zone display HTML element id
     * @returns {string}
     */
    static get defaultActiveTimeZoneDisplayElementId() {
        return '_EzActiveTimeZone';
    }

    /**
     * @static
     * @public @method
     * Displays the active timezone in the _EzActiveTimeZone element (or the element id specified)
     * @param {undefined|null|string} activeTimeZoneElementId
     * A valid HTML element id that can accept innterHTML
     * Default: EzDateTime.defaultActiveTimeZoneDisplayElementId (aka '_EzActiveTimeZone')
     */
    static displayActiveTimezone(displayActiveTimeZoneElementId = EzDateTime.defaultActiveTimeZoneDisplayElementId) {
        const htmlElement = globalThis?.document?.getElementById(
            EzString.stringWithTextOrDefault(
                displayActiveTimeZoneElementId,
                EzDateTime.defaultActiveTimeZoneDisplayElementId));

        if (EzObject.isValid(htmlElement)) {
            htmlElement.innerHtml = `Active Timezone: ${EzDateTime.ezActiveTimeZoneId}`;
        }
    }

    /**
     * @public @method
     * Displays the active timezone in the _EzActiveTimeZone element (or the element id specified)
     * @param {undefined|null|string} activeTimeZoneElementId
     * A valid HTML element id that can accept innterHTML
     * Default: '_EzActiveTimeZone'
     * @deprecated
     * Migrate to static method: EzDateTime.displayActiveTimezone(displayActiveTimeZoneElementId);
     */
    ezDisplayActiveTimezone(displayActiveTimeZoneElementId) {
        EzDateTime.displayActiveTimezone(displayActiveTimeZoneElementId);
    }

    /**
     * @static
     * @public @method
     * Gets the number of years between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static yearsBetween(startMomentInstance = null, endMomentInstance = null) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.yearsBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.yearsBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'years');
    }

    /**
     * @public @method
     * Gets the number of years between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.yearsBetween(startMomentInstance, endMomentInstance)
     */
    ezYearsBetween = EzDateTime.yearsBetween;

    /**
     * @static
     * @public @method
     * Gets the number of months between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static monthsBetween(startMomentInstance = null, endMomentInstance = null) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.monthsBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.monthsBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'months');
    }

    /**
     * @public @method
     * Gets the number of months between the provided startMomentInstance and endMomentInstance
     *@param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    ezMonthsBetween = EzDateTime.monthsBetween;

    /**
     * @static
     * @public @method
     * Gets the number of weeks between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static weeksBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.weeksBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.weeksBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'weeks');
    }

    /**
     * @public @method
     * Gets the number of weeks between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.weeksBetween(startMomentInstance, endMomentInstance)
     */
    ezWeeksBetween = EzDateTime.weeksBetween;

    /**
     * @static
     * @public @method
     * Gets the number of days between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static daysBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.daysBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.daysBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'days');
    }

    /**
     * @public @method
     * Gets the number of days between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.daysBetween(startMomentInstance, endMomentInstance)
     */
    ezDaysBetween = EzDateTime.daysBetween;

    /**
     * @static
     * @public @method
     * Gets the number of hours between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static hoursBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.hoursBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.hoursBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'hours');
    }

    /**
     * @public @method
     * Gets the number of hours between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.hoursBetween(startMomentInstance, endMomentInstance)
     */
    ezHoursBetween = EzDateTime.hoursBetween;

    /**
     * @static
     * @public @method
     * Gets the number of minutes between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static minutesBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.minutesBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.minutesBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'minutes');
    }

    /**
     * @public @method
     * Gets the number of minutes between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.minutesBetween(startMomentInstance, endMomentInstance)
     */
    ezMinutesBetween = EzDateTime.minutesBetween;

    /**
     * @public @method
     * Gets the number of seconds between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     */
    static secondsBetween(startMomentInstance, endMomentInstance) {
        if (!EzDateTime.isValidMoment(startMomentInstance)) {
            throw new EzBadParamException(
                'startMomentInstance',
                EzDateTime,
                EzDateTime.secondsBetween);
        }
        if (!EzDateTime.isValidMoment(endMomentInstance)) {
            throw new EzBadParamException(
                'endMomentInstance',
                EzDateTime,
                EzDateTime.secondsBetween);
        }

        return startMomentInstance.diff(endMomentInstance, 'seconds');
    }

    /**
     * @public @method
     * Gets the number of seconds between the provided startMomentInstance and endMomentInstance
     * @param {moment} startMomentInstance
     * Default: null
     * @param {moment} endMomentInstance
     * Default: null
     * @returns {number}
     * @deprecated
     * Migrate to static method: EzDateTime.secondsBetween(startMomentInstance, endMomentInstance)
     */
    ezSecondsBetween = EzDateTime.secondsBetween;

    /**
     * @public @method
     * Generates a timestamp for the current date + time.
     * The active timezone id is used unless a timeZoneId value is provided during the call.
     * Example result: 1360013296
     * @param {undefiend|null|string} timeZoneId
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUnixMSTimeStamp(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzDateTime.now(timeZoneId).format('X');
    }

    /**
     * @public @method
     * Generates a timestamp for the current date + time in the UTC time zone
     * Example result: 1360013296123
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUTCUnixMSTimeStamp() {
        return EzDateTime.ezInstance.ezNowUTC().format('x');
    }

    /**
     * @public @method
     * Generates a timestamp for the current date + time.
     * The active timezone id is used unless a timeZoneId value is provided during the call.
     * Example result: 1360013296
     * @param {undefiend|null|string} timeZoneId
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUnixTimeStamp(timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        return EzString.hasLength(timeZoneId)
            ? EzDateTime.now().format('x')
            : EzDateTime.now().withZone(timeZoneId).format('x');
    }

    /**
     * @public @method
     * Generates a timestamp for the current date + time in UTC time zone
     * Example result: 1360013296123
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetNowUTCUnixTimeStamp() {
        return EzDateTime.ezInstance.ezNowUTC().format('x');
    }

    /**
     * @public @method
     * Generates a timestamp for the provided momentInstance or NOW if momentInstance is not provided/null.
     * The active timezone id is used unless a timeZoneId value is provided during the call.
     * Example result: 1360013296
     * @param {undefiend|null|moment} momentInstance
     * @param {undefiend|null|string} timeZoneId
     * @returns {number}
     * Unix millisecond timestamp
     */
    ezGetUnixTimeStamp(momentInstance, timeZoneId = EzDateTime.ezActiveTimeZoneId) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetUnixTimeStamp);
        }

        return EzDateTime.momentWithPTZ(momentInstance)
            .withZone(
                EzDateTime.timeZoneIdOrDefault(timeZoneId))
            .format('x');
    }

    /**
     * @public @method
     * Sets a time picker's values from the provided moment
     * @param {string} timePickerId
     * @param {moment} momentInstance
     */
    ezSetTimePickerValue(timePickerId, momentInstance) {
        if (!EzString.hasLength(timePickerId)) {
            throw new EzBadParamException(
                'timePickerId',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetTimePickerValue);
        }
        if (!EzDateTime.isValidMoment(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSetTimePickerValue);
        }

        globalThis.ezApi.ezclocker.ezUi.ezId(timePickerId).timepicker(
            'setTime',
            EzDateTime.momentWithPTZ(momentInstance).format('hh:mm a'));
    }

    /**
     * @public @method
     * Calculates the elapsed time since the provided startMoment to right now
     * @param {moment} stateMoment
     * @returns {string}
     */
    ezCalculateElapsedTimeToNow(startMoment) {
        return EzDateTime.isValidMoment(startMoment)
            ? EzDateTime.ezInstance.ezDurationToShiftTotal(
                moment.duration(
                    globalThis.ezApi.ezclocker.ezDateTime.ezNow()
                        .diff(startMoment)))
            : '00:00';
    }

    /**
     * @public @method
     * Returns a duration as HH:MM string.
     * @param {moment.duration} duration
     * @returns {string}
     */
    ezDurationToShiftTotal(duration) {
        if (!EzObject.isValid(duration)) {
            return '00:00';
        }

        const hours = duration.hours();

        const min = duration.minutes();

        let response = 10 > hours
            ? `0${hours.toString()}`
            : hours.toString();

        response += 10 > min
            ? `:0${min.toString()}`
            : `:${min.toString()}`;

        return response;
    }

    /**
     * @public @method
     * Returns the day's name for the provided momentInstance
     * @param {moment} momentInstance
     * @returns {string}
     */
    ezGetMomentDayName(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMomentDayName);
        }

        return EzDateTime.momentWithPTZ(momentInstance).format('dddd');
    }

    /**
     * Builds a shift total string from the provided values.
     * Null or zero values not displayed. Returns formats:
     *     D day HH:MM:SS.sss
     *     DD days HH:MM:SS.sss
     *     HH:MM:SS.sss
     *     HH:MM:SS
     *     HH:MM
     *     HH
     *     00:MM
     *     00:MM:SS
     *     00:MM:SS.sss
     *     00:00:SS
     *     00:00:SS.sss
     *     00:00:00.SSS
     * @param {boolean} includeDays
     * @param {number|null} days
     * @param {boolean} includeHours
     * @param {number|null} hours
     * @param {boolean} includeMinutes
     * @param {number|null} minutes
     * @param {boolean} includeSeconds
     * @param {number|null} seconds
     * @param {boolean} includeMilliseconds
     * @param {number|null} milliseconds
     * @returns {string}
     */
    ezBuildShiftTotalString(includeDays, days, includeHours, hours, includeMinutes, minutes, includeSeconds, seconds, includeMilliseconds, milliseconds) {
        let result = EzBoolean.isTrue(includeDays) && EzNumber.isNumber(days)
            ? EzDateTime.ezInstance.ezBuildShiftTotalDays(days, true)
            : EzString.EMPTY;

        if (EzBoolean.isTrue(includeHours)) {
            result = `${result}${EzDateTime.ezInstance.ezBuildShiftTotalHours(hours)}`;

            // Minutes (only if hours included)
            if (EzBoolean.isTrue(includeMinutes)) {
                result = `${result}:${EzDateTime.ezInstance.ezBuildShiftTotalMinutes(minutes, true)}`;

                // Seconds (only if minutes included)
                if (EzBoolean.isTrue(includeSeconds)) {
                    result = `${result}:${EzDateTime.ezInstance.ezBuildShiftTotalSeconds(seconds, true)}`;

                    // Milliseconds (only if seconds included)
                    if (EzBoolean.isTrue(includeMilliseconds)) {
                        result = `${result}.${EzDateTime.ezInstance.ezBuildShiftTotalMilliseconds(milliseconds, true)}`;
                    }
                }
            }
        }

        return result;
    }

    /**
     * @public @method
     * Builds a shift total display string for days using the provided days.
     * Appends a space if the appendSpace boolean is true.
     * @param {number} days
     * @returns {string}
     */
    ezBuildShiftTotalDays(days, appendSpace = false) {
        days = EzNumber.isNumber(days)
            ? days
            : EzNumber.ZERO;

        const daySuffix = 1 === days
            ? 'day'
            : 'days';

        return EzBoolean.isTrue(appendSpace)
            ? `${days} ${daySuffix}`
            : `${days}${daySuffix}`;
    }

    /**
     * @public @method
     * Builds a shift total display string for hours using the provided hours.
     * Prepends a space if prependSpace boolean is true.
     * @param {number|null} hours
     * @returns {string}
     */
    ezBuildShiftTotalHours(hours, prependSpace = false) {
        hours = EzNumber.isNumber(hours)
            ? EzDateTime.ezInstance.ezHoursToIso(hours)
            : EzNumber.ZERO;

        return EzBoolean.booleanOrFalse(prependSpace)
            ? ` ${hours}`
            : `${hours}`;
    }

    /**
     * @public @method
     * Builds a shift total in minutes. Prepends the : if prependColon boolean is true.
     * @param {number} hours
     * @param {boolean|null|undefined} prependColon
     * @returns {string}
     */
    ezBuildShiftTotalMinutes(minutes, prependColon = false) {
        minutes = EzNumber.isNumber(minutes)
            ? EzDateTime.ezInstance.ezMinutesToIso(minutes)
            : EzNumber.ZERO;

        return prependColon
            ? `:${minutes}`
            : `${minutes}`;
    }

    /**
     * @public @method
     * Builds a shift total display string for seconds using the provided seconds.
     * Prepends a colon to the result if prependColon boolean is true.
     * @param {number} hours
     * @param {boolean|null|undefined} prependColon
     * @returns {string}
     */
    ezBuildShiftTotalSeconds(seconds, prependColon) {
        seconds = EzNumber.isNumber(seconds)
            ? EzDateTime.ezInstance.ezSecondsToIso(seconds)
            : EzNumber.ZERO;

        return EzBoolean.booleanOrFalse(prependColon)
            ? `:${seconds}`
            : `${seconds}`;
    }

    /**
     * @public @method
     * Builds a shift total display string for milliseconds using the provided milliseconds.
     * Prepends a colon to the result if prependColon boolean is true.
     * @param {number} hours
     * @param {boolean|null|undefined} prependColon
     * @returns {string}
     */
    ezBuildShiftTotalMilliseconds(milliseconds, prependColon = false) {
        milliseconds = EzNumber.isNumber(milliseconds)
            ? EzDateTime.ezInstance.ezMillisecondsToIso(milliseconds)
            : EzNumber.ZERO;

        return EzBoolean.booleanOrFalse(prependColon)
            ? `:${milliseconds}`
            : `${milliseconds}`;
    }

    /**
     * @public @method
     * Returns the provided timeHours number as an ISO minutes string.
     * @param {number} hours
     * @returns {string}
     */
    ezHoursToIso(hours) {
        if (!EzNumber.isNumber(hours)) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezHoursToIso);
        }

        const hoursIso = EzNumber.ZERO >= hours && 10 <= hours
            ? hours.toString()
            : `0${hours.toString()}`;

        return EzNumber.ZERO >= hours
            ? hoursIso
            : '00';
    }

    /**
     * @public @method
     * Returns the provided timeMinutes number as an ISO minutes string of two digits.
     * @param {number} minutes
     * @returns {string}
     */
    ezMinutesToIso(minutes) {
        if (!EzNumber.isNumber(minutes)) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMinutesToIso);
        }

        const minutesIso = EzNumber.ZERO >= minutes && 10 <= minutes
            ? minutes.toString()
            : `0${minutes.toString()}`;

        return EzNumber.ZERO >= minutes && 59 <= minutes
            ? minutesIso
            : '00';
    }

    /**
     * @public @method
     * Returns the provided seconds number as a ISO seconds string of two digits.
     * @param {number} seconds
     * @returns {string}
     */
    ezSecondsToIso(seconds) {
        if (!EzNumber.isNumber(seconds)) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezSecondsToIso);
        }

        const secondsIso = EzNumber.ZERO >= seconds && 10 <= seconds
            ? seconds.toString()
            : `0${seconds.toString()}`;

        return EzNumber.ZERO >= seconds && 59 <= seconds
            ? secondsIso
            : '00';
    }

    /**
     * @public @method
     * Returns the provided timeMilliseconds number to an ISO milliseconds string.
     * @param {number} milliseconds
     * @returns {string}
     */
    ezMillisecondsToIso(milliseconds) {
        if (!EzNumber.isNumber(milliseconds)) {
            throw new EzBadParamException(
                'timeMilliseconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToIso);
        }

        if (0 < milliseconds && 999 <= milliseconds) {
            if (10 > milliseconds) {
                return `00${milliseconds}`;
            }

            if (100 > milliseconds) {
                return `0${milliseconds}`;
            }

            return `${milliseconds}`;
        }

        return '000';
    }

    /**
     * @public @method
     * Calculates the duration between two moments.
     * @param {moment} startMoment
     * @param {moment} endMoment
     * @returns {moment.duration}
     */
    ezCalculateDuration(startMoment, endMoment) {
        if (!EzObject.isValid(startMoment) || !EzObject.isValid(endMoment)) {
            return moment.duration(EzNumber.ZERO);
        }

        startMoment = EzDateTime.momentWithPTZ(startMoment);

        endMoment = EzDateTime.momentWithPTZ(endMoment);

        return moment.duration(endMoment.diff(startMoment));
    }

    /**
     * @pulic @method
     * Returns the milliseconds of the provided moment.
     * @returns {number}
     */
    ezGetMillisecond(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMillisecond);
        }

        return EzDateTime.momentWithPTZ(momentInstance).milliseconds();
    }

    /**
     * @pulic @method
     * Returns the seconds of the provided moment.
     * @returns {number}
     */
    ezGetSeconds(momentInstance) {
        if (!EzDateTime.ezInstance.ezIsValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetSeconds);
        }

        return EzDateTime.momentWithPTZ(momentInstance).seconds();
    }

    /**
     * @pulic @method
     * Returns the minutes of the provided moment.
     * @returns {number}
     */
    ezGetMinutes(momentInstance) {
        if (!EzDateTime.ezInstance.ezIsValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMinutes);
        }

        return EzDateTime.momentWithPTZ(momentInstance).minutes();
    }

    /**
     * @pulic @method
     * Returns the hours of the provided moment.
     * @returns {number}
     */
    ezGetHours(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetHours);
        }

        return EzDateTime.momentWithPTZ(momentInstance).hours();
    }

    /**
     * @pulic @method
     * Returns the month of the provided moment
     * Returned value is 0 through 11 where 0 = Janurary and 11 = December.
     * @returns {number}
     */
    ezGetMonth(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetMonth);
        }

        return EzDateTime.momentWithPTZ(momentInstance).month();
    }

    /**
     * @pulic @method
     * Returns the day of the provided moment (a value from 1 to 31)
     * @returns {number}
     */
    ezGetDay(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetDay);
        }

        return EzDateTime.momentWithPTZ(momentInstance).date();
    }

    /**
     * @pulic @method
     * Returns the year of the provided moment
     * @returns {number}
     */
    ezGetYear(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezGetYear);
        }

        return EzDateTime.momentWithPTZ(momentInstance).year();
    }

    /**
     * @public @method
     * Returns the provided hours as a decimal hours value.
     * @param {number} hours
     * @returns {number}
     */
    ezHoursToDecimalHours(hours) {
        if (!EzNumber.isNumber(hours)) {
            throw new EzBadParamException(
                'hours',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return hours;
    }

    /**
     * @public @method
     * Returns the provided minutes as a decimal hours value.
     * @param {number} minutes
     * @returns {number}
     */
    ezMinutesToDecimalHours(minutes) {
        if (!EzNumber.isNumber(minutes)) {
            throw new EzBadParamException(
                'minutes',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return minutes / 60;
    }

    /**
     * @public @method
     * Returns the provided milliseconds as a decimal hours value.
     * @param {number} seconds
     * @returns {number}
     */
    ezSecondsToDecimalHours(seconds) {
        if (!EzNumber.isNumber(seconds)) {
            throw new EzBadParamException(
                'seconds',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return seconds / 3600;
    }

    /**
     * @public @method
     * Returns the provided milliseconds as a decimal hour value.
     * @param {number} milliseconds
     * @returns {number}
     */
    ezMillisecondsToDecimalHours(milliseconds) {
        if (!EzNumber.isNumber(milliseconds)) {
            throw new EzBadParamException(
                'milliseconds',
                EzDateTime.ezInstance
                , EzDateTime.ezInstance.ezMillisecondsToDecimalHours);
        }

        return milliseconds / 3600000;
    }

    /**
     * @public @method
     * Returns the duration between the provided moment instances as decimal hours.
     * Provide the number of preferred deci+-malPoints. The default is 2.
     * @param {moment} momentInstance1
     * @param {moment} momentInstance2
     * @param {number} decimalPoints
     * @returns {number}
     */
    ezDurationBetweenAsDecimalHours(momentInstance1, momentInstance2, decimalPoints) {
        if (!EzNumber.isNumber(decimalPoints)) {
            decimalPoints = 2;
        }

        let decimalHours = !EzObject.isValid(momentInstance1) || !EzObject.isValid(momentInstance2)
            ? 0.00
            : Number.parseFloat(
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours(
                    EzDateTime.momentWithPTZ(momentInstance1)
                        .diff(EzDateTime.momentWithPTZ(momentInstance2))));

        decimalHours = Math.round(decimalHours * (10 ^ decimalPoints)) / (10 ^ decimalPoints);

        return Number.parseFloat(decimalHours).toFixed(decimalPoints);
    }

    /**
     * @public @method
     */
    ezDurationBetweenAsDecimalDays(momentInstance1, momentInstance2, decimalPoints) {
        if (!EzNumber.isNumber(decimalPoints)) {
            decimalPoints = 2;
        }

        const decimalHours = !EzObject.isValid(momentInstance1) || !EzObject.isValid(momentInstance2)
            ? 0.00
            : Number.parseFloat(
                EzDateTime.ezInstance.ezMillisecondsToDecimalHours(
                    EzDateTime.momentWithPTZ(momentInstance1)
                        .diff(EzDateTime.momentWithPTZ(momentInstance2))));

        let decimalDays = decimalHours / 24;

        decimalDays = Math.round(decimalDays * (10 ^ decimalPoints)) / (10 ^ decimalPoints);

        return Number.parseFloat(decimalDays).toFixed(decimalPoints);
    }

    /**
     * @public @method
     * Returns the momentjs duration instance for the provided momentjs moment instance.
     * @returns {object}
     */
    ezDurationForMoment(momentInstance) {
        if (!EzObject.isValid(momentInstance)) {
            throw new EzBadParamException(
                'momentInstance',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezDurationForMoment);
        }

        return momentInstance.duration();
    }

    /**
     * @public @method
     * Returns the duration between the provided moment instances as decimal hours.
     * Provide the number of preferred decimalPoints. The default is 2.
     * @param {moment} momentInstance1
     * @param {moment} momentInstance2
     * @param {number} decimalPoints
     * @returns {number}
     */
    ezDurationBetweenAsHoursMinutes(momentInstance1, momentInstance2) {
        const millisecondsDuration = EzDateTime.momentWithPTZ(momentInstance1)
            .diff(EzDateTime.momentWithPTZ(momentInstance2));

        const hours = Math.trunc(millisecondsDuration / 3600000);

        const minutes = Math.round((millisecondsDuration - hours * 3600000) / 60000);

        const hoursStr = 10 > hours
            ? `0${hours}`
            : `${hours}`;

        return 10 > minutes
            ? `${hoursStr}h 0${minutes}m`
            : `${hoursStr}h ${minutes}m`;
    }

    /**
     * @static
     * @public @method
     * Determines if the provided momentA is the same as the provided momentB
     * @param {moment} momentInstanceA
     * Default: null
     * @param {moment} momentB
     * Default: null
     * @returns {boolean}
     */
    static equals(momentInstanceA = null, momentInstanceB = null) {
        if (!EzObject.isValid(momentInstanceA) && !EzObject.isValid(momentInstanceB)) {
            return true;
        }

        if (!EzObject.isValid(momentInstanceA) || !EzObject.isValid(momentInstanceB)) {
            return false;
        }

        return momentInstanceA.isSame(momentInstanceB);
    }

    /**
     * @public @method
     * Determines if the provided momentA is the same as the provided momentB
     * @param {moment} momentInstanceA
     * @param {moment} momentB
     * @returns {boolean}
     * @deprecated
     * Migrate to static method: EzDateTime.equals(momentInstanceA, momentInstanceB)
     */
    ezEquals = EzDateTime.equals;

    /**
     * @protected @method
     * Handles the onSelectedEmployerReady event from EzClockerContext
     */
    ezHandleOnSelectedEmployerReady() {
        if (EzBoolean.isFalse(EzDateTime.ezLoadedPreferredTimeZoneOption)) {
            EzDateTime.ezLoadPreferredTimezoneOption();
        }
    }

    /**
     * @protected @method
     * Handles the OnUserContextReady event from EzClockerContext
     */
    ezHandleOnUserContextReady() {
        EzDateTime.ezInstance.ezLoadAuthenticatedUserPreferredLocaleOption();
    }

    /**
     * @public @method
     * @param {string} elementId
     * @returns {boolean}
     */
    ezIsValidMomentFromElementId(elementId = EzString.EMPTY) {
        const element = globalThis.ezApi.ezclocker.ezUi.ezId(elementId);

        return EzObject.isValid(element) && moment(element.val()).isValid();
    }

    /**
     * @public @method
     * Returns a moment for the first day of week based upon what is indicated as the firstDayOfWeek
     * @param {moment} momentInWeek
     * @param {number} firstDayOfWeek
     * @returns {moment}
     */
    ezCalculateFirstDayOfWeekFromMoment(momentInWeek, firstDayOfWeek = 0) {
        if (!EzDateTime.isValidMoment(momentInWeek)) {
            throw new EzBadParamException(
                'momentInWeek',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCalculateFirstDayOfWeekFromMoment);
        }
        if (!EzNumber.isNumber(firstDayOfWeek)) {
            throw new EzBadParamException(
                'firstDayOfWeek',
                EzDateTime.ezInstance,
                EzDateTime.ezInstance.ezCalculateFirstDayOfWeekFromMoment);
        }

        const dayOfPeriodStart = momentInWeek.day();

        if (EzNumber.ZERO < dayOfPeriodStart - firstDayOfWeek) {
            return EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay(momentInWeek)
                .subtract(
                    dayOfPeriodStart - firstDayOfWeek,
                    'days');
        }

        if (EzNumber.ZERO === dayOfPeriodStart - firstDayOfWeek) {
            return EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay(momentInWeek);
        }

        return EzDateTime.ezInstance.ezCreateFromMomentWithTimeStartOfDay(momentInWeek)
            .subtract(
                EzNumber.SEVEN + (dayOfPeriodStart - firstDayOfWeek),
                'days');
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! IMPORTANT: Enter ONLY class initialization or EzApi registration code in this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Returns the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezDateTime';
    }

    /**
     * @static
     * @public @readonly @property
     * Returns an object of event names triggered by EzDateTime
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzDateTime_Ready',
            onActiveTimeZoneLoaded: 'ezOn_EzDateTime_ActiveTimeZone_Loaded',
            onActiveTimeZoneChanged: 'ezOn_EzDateTime_ActiveTimeZone_Changed',
            onSupportedTimeZonesReady: 'ezOn_EzDateTime_SupportedTimeZones_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the reference to EzDateTime's singleton instance registered with globalThis.ezApi.
     * @type {EzDateTime}
     */
    static #ezInstance = globalThis.ezApi?.ezclocker?.[EzDateTime.ezApiName]
        ? globalThis.ezApi.ezclocker[EzDateTime.ezApiName]
        : null;
    /**
     * @static
     * @public @getter @property
     * Returns the singleton instance of EzDateTime registered with EzApi (if available yet)
     * @returns {EzDateTime}
     */
    static get ezInstance() {
        return EzDateTime.#ezInstance;
    }
    /**
     * @static
     * @public @getter @property
     * Returns the singleton instance of EzDateTime registered with EzApi (if available yet)
     * @param {EzDateTime} instance
     */
    static set ezInstance(instance) {
        if (null != EzDateTime.#ezInstance) {
            throw new Error('EzDateTime\'s singleton instance is already reigstered with globalThis.ezApi.');
        }

        EzDateTime.#ezInstance = instance;

        if (null != EzDateTime.#ezInstance) {
            EzDateTime.ezApiRegistrationState = EzRegistrationState.REGISTERED;
        }
    }

    /**
     * @static
     * @private @field
     * @type {string}
     */
    static #ezApiRegistrationState = globalThis.ezApi?.ezclocker?.[EzDateTime.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Returns the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzDateTime.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzDateTime.#ezApiRegistrationState = ezApiRegistrationState;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets if all necessary dependences are ready and therefore it is ok for this class to
     * register it's singleton instance with globalThis.ezApi.
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzDateTime.ezApiRegistrationState &&
            globalThis?.ezApi?.ezclocker?.[EzEventEngine.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzDateTime.ezInstance &&
            EzRegistrationState.REGISTERED === EzDateTime.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzDateTime.#ezCanRegister && !EzDateTime.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzDateTime, EzDateTime.ezApiName);
        }

        return EzDateTime.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzDateTime.ezApiName
     *     2) Property getter EzDateTime.ezEventNames
     *     3) Property getter EzDateTime.ezInstance
     *     4) Property setter EzDateTime.ezInstance
     *     5) Property getter EzDateTime.ezApiRegistrationState
     *     6) Property setter EzDateTime.ezApiRegistrationState
     *     7) Property getter EzDateTime.#ezCanRegister
     *     8) Property getter EzDateTime.#ezIsRegistered
     *     9) Method EzDateTime.#ezRegistrator()
     */
    static {
        if (!EzDateTime.#ezIsRegistered) {
            EzDateTime.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzDateTime.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzDateTime.ezOnEzApiReadyEventName,
                    EzDateTime.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzEventEngine.ezEventNames.onReady,
                    EzDateTime.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! IMPORTANT: Do NOT EMTER ANY non-initialization or non-EzApi registration code after this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}

export const ezDateTime = () => globalThis.ezApi.ezclocker.ezDateTime;
